/* TopNotchTCG - Sealed Vault motion engine.
   One rAF loop, one IntersectionObserver, compositor-only effects.
   Everything degrades to static + visible with no JS or reduced motion. */
(function () {
  'use strict';

  var doc = document;
  /* stamp: reveal-hiding CSS is gated on this class, so a failed script
     never leaves the page blank */
  doc.documentElement.classList.add('motion-ok');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;
  var wideScreen = window.matchMedia('(min-width: 861px)').matches;

  /* ---------- single rAF orchestrator ---------- */
  var ticks = [];
  var rafOn = false;
  function tick(now) {
    var snapshot = ticks.slice(); /* callbacks may remove themselves mid-loop */
    for (var i = 0; i < snapshot.length; i++) snapshot[i](now);
    if (ticks.length) { requestAnimationFrame(tick); } else { rafOn = false; }
  }
  function addTick(fn) {
    if (ticks.indexOf(fn) > -1) return;
    ticks.push(fn);
    if (!rafOn) { rafOn = true; requestAnimationFrame(tick); }
  }
  function removeTick(fn) {
    var i = ticks.indexOf(fn);
    if (i > -1) ticks.splice(i, 1);
  }

  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ---------- hero load choreography gate ---------- */
  var stamped = false;
  function stampLoaded() {
    if (stamped) return;
    stamped = true;
    doc.documentElement.classList.add('is-loaded');
  }
  if (doc.fonts && doc.fonts.ready) { doc.fonts.ready.then(stampLoaded); }
  setTimeout(stampLoaded, 500);

  /* ---------- reveal system v2 ----------
     A non-interacting renderer (crawler, Lighthouse, social preview, print)
     never scrolls, so IO alone would leave below-fold content at opacity 0
     forever. The fallback below reveals everything after a short delay, but is
     CANCELLED the moment a real visitor shows any sign of interacting - humans
     keep the scroll choreography, bots get the content. */
  var interacted = false;
  ['scroll', 'pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (evt) {
    window.addEventListener(evt, function () { interacted = true; }, { passive: true, once: true });
  });

  ready(function () {
    var els = doc.querySelectorAll('.reveal, .reveal-scale, .reveal-mask, .reveal-line');
    if (!els.length) return;
    function revealAll() {
      els.forEach(function (el) { el.classList.add('in-view'); });
    }
    /* no IO at all (very old browser): show everything immediately */
    if (!('IntersectionObserver' in window)) { revealAll(); return; }
    setTimeout(function () { if (!interacted) revealAll(); }, 2400);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('in-view');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -36px 0px' });
    els.forEach(function (el) {
      if (el.classList.contains('stagger')) {
        var kids = el.children;
        var step = wideScreen ? 70 : 60;
        var cap = wideScreen ? 560 : 210;
        for (var i = 0; i < kids.length; i++) {
          kids[i].style.setProperty('--d', Math.min(i * step, cap) + 'ms');
        }
      }
      io.observe(el);
    });
  });

  /* ---------- count-up numerals ---------- */
  ready(function () {
    var els = doc.querySelectorAll('[data-count]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    function run(el) {
      if (el.dataset.counted) return; /* IO + the no-scroll fallback may both fire */
      el.dataset.counted = '1';
      var target = parseInt(el.getAttribute('data-count'), 10) || 0;
      var suffix = el.getAttribute('data-suffix') || '';
      var final = el.getAttribute('data-final');
      /* thousands separators: fine to omit at 168, jarring at 10000 */
      var fmt = function (n) { return n.toLocaleString('en-US'); };
      if (reduceMotion) { el.textContent = final || (fmt(target) + suffix); return; }
      var t0 = null;
      var dur = 1100;
      function step(now) {
        if (t0 === null) t0 = now;
        var p = Math.min((now - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(Math.round(target * eased)) + suffix;
        if (p >= 1) { if (final) el.textContent = final; removeTick(step); }
      }
      addTick(step);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        run(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
    /* same interaction-gated fallback as the reveal system: forEach passes
       (element, index, array) and run() only reads the first argument */
    setTimeout(function () {
      if (!interacted) els.forEach(function (el) { run(el); });
    }, 2600);
  });

  /* ---------- header scroll state ---------- */
  ready(function () {
    var header = doc.querySelector('header');
    if (!header) return;
    var scrolled = false;
    function onScroll() {
      var want = window.scrollY > 40;
      if (want !== scrolled) {
        scrolled = want;
        header.classList.toggle('scrolled', want);
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  /* ---------- holo tilt (pointer-fine, wide, motion-ok) ---------- */
  ready(function () {
    if (reduceMotion || !finePointer || !wideScreen) return;
    function bindStages() {
    var stages = doc.querySelectorAll('.relic .ph.lit, .pdp-frame .card-frame');
    stages.forEach(function (stage) {
      var img = stage.querySelector('img');
      if (!img) return;
      if (stage.classList.contains('tilt')) return; /* idempotent: re-scan is safe */
      stage.classList.add('tilt');
      var glare = doc.createElement('span');
      glare.className = 'glare';
      glare.setAttribute('aria-hidden', 'true');
      stage.appendChild(glare);
      var isPdp = stage.classList.contains('card-frame');
      var maxDeg = isPdp ? 4 : 6;
      var cur = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
      var tgt = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
      var active = false;
      function loop() {
        var k = 0.14;
        var done = true;
        for (var p in cur) {
          cur[p] += (tgt[p] - cur[p]) * k;
          if (Math.abs(tgt[p] - cur[p]) > 0.002) done = false;
        }
        stage.style.setProperty('--rx', cur.rx.toFixed(2) + 'deg');
        stage.style.setProperty('--ry', cur.ry.toFixed(2) + 'deg');
        stage.style.setProperty('--gl', cur.gl.toFixed(3));
        stage.style.setProperty('--gx', cur.gx.toFixed(1) + '%');
        stage.style.setProperty('--gy', cur.gy.toFixed(1) + '%');
        stage.style.setProperty('--tz', cur.tz.toFixed(3));
        if (done && !active) { removeTick(loop); img.style.willChange = ''; }
      }
      stage.addEventListener('pointerenter', function () {
        active = true;
        img.style.willChange = 'transform'; /* promote only while interacting */
        tgt.gl = 0.55; tgt.tz = 1.03;
        addTick(loop);
      });
      stage.addEventListener('pointermove', function (e) {
        var r = stage.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        tgt.ry = (px - 0.5) * 2 * maxDeg;
        tgt.rx = (0.5 - py) * 2 * maxDeg;
        tgt.gx = px * 100;
        tgt.gy = py * 100;
      });
      stage.addEventListener('pointerleave', function () {
        active = false;
        tgt.rx = 0; tgt.ry = 0; tgt.gl = 0; tgt.gx = 50; tgt.gy = 42; tgt.tz = 1;
      });
    });
    }
    bindStages();
    /* the Vault Remembers rail (and any other block that builds cards) injects
       ~1550 lines below this one, during this same synchronous pass - without a
       re-scan those relics are the only plates on the page that never tilt */
    setTimeout(bindStages, 0);
  });

  /* (ember particle field removed 2026-07-16: permanent rAF loop for set dressing
     that fought the professional direction) */

  /* ---------- toast ---------- */
  var toastEl = null, toastTimer = null;
  function toast(msg, linkHref, linkText) {
    if (!toastEl) {
      toastEl = doc.createElement('div');
      toastEl.className = 'tntcg-toast';
      toastEl.setAttribute('role', 'status');
      doc.body.appendChild(toastEl);
    }
    /* un-inert BEFORE the content is written: a live region that is still
       inert while its text changes may not be announced on repeat toasts */
    toastEl.removeAttribute('inert');
    toastEl.innerHTML = '';
    var svg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z"/><path d="m9 12 2 2 4-4"/></svg>';
    var span = doc.createElement('span');
    span.textContent = msg;
    toastEl.insertAdjacentHTML('beforeend', svg);
    toastEl.appendChild(span);
    if (linkHref) {
      var a = doc.createElement('a');
      a.href = linkHref;
      a.textContent = linkText || 'View';
      toastEl.appendChild(a);
    }
    /* force reflow so repeat toasts re-animate */
    void toastEl.offsetWidth;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('show');
      toastEl.setAttribute('inert', '');
    }, 4200);
  }

  /* ---------- cart badge ---------- */
  function setCartCount(n) {
    var links = doc.querySelectorAll('.nav-ico a[href="/cart"]');
    links.forEach(function (link) {
      var b = link.querySelector('.cart-n');
      if (n > 0) {
        if (!b) {
          b = doc.createElement('span');
          b.className = 'cart-n';
          link.appendChild(b);
        }
        b.textContent = n > 99 ? '99+' : String(n);
        b.classList.remove('bump');
        void b.offsetWidth;
        b.classList.add('bump');
      } else if (b) { b.remove(); }
    });
  }

  /* ---------- quantity steppers (PDP + cart) ---------- */
  ready(function () {
    doc.addEventListener('click', function (e) {
      var btn = e.target.closest('.qty-stepper button[data-step]');
      if (!btn) return;
      e.preventDefault();
      var input = btn.parentElement.querySelector('input');
      if (!input) return;
      var v = parseInt(input.value, 10) || 1;
      var min = parseInt(input.min, 10);
      var max = parseInt(input.max, 10);
      v += parseInt(btn.getAttribute('data-step'), 10);
      if (!isNaN(min)) v = Math.max(min, v);
      if (!isNaN(max) && max > 0) v = Math.min(max, v);
      if (String(v) !== input.value) {
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  });

  /* ---------- product form: fetch add-to-cart with success choreography ---------- */
  ready(function () {
    var form = doc.querySelector('form[action*="/cart/add"]');
    if (!form || !window.fetch) return;
    var btn = form.querySelector('button[name="add"]');
    if (!btn) return; /* let native flow handle it */
    var original = btn.textContent;
    var proxy = doc.querySelector('.pdp-stickybar [data-proxy-add]');
    function setBusy(busy, label) {
      btn.disabled = busy;
      btn.textContent = label || original;
      if (proxy) { proxy.disabled = busy; proxy.textContent = label || 'Add to Cart'; }
    }
    /* success beat: specular sweep + drawn check, all compositor/paint-cheap;
       reduced-motion users get the same content with no animation (CSS-gated) */
    function showAdded() {
      btn.disabled = true;
      btn.innerHTML = '<span class="atc-ok"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true" focusable="false"><path d="m5 13 5 5L20 7"/></svg>Added to cart</span>';
      btn.classList.remove('sweep');
      void btn.offsetWidth; /* restart the sheen */
      btn.classList.add('sweep');
      if (proxy) { proxy.disabled = true; proxy.textContent = 'Added to cart'; }
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (form.dataset.busy) return; /* double-tap guard (proxy bypasses disabled btn) */
      form.dataset.busy = '1';
      setBusy(true, 'Adding...');
      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
        return res.json();
      }).then(function () {
        /* badge refresh is best-effort; its failure must not mask the success */
        fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { if (cart) setCartCount(cart.item_count); })
          .catch(function () {});
        showAdded();
        toast('Added to your cart.', '/cart', 'View cart');
        setTimeout(function () {
          delete form.dataset.busy;
          btn.classList.remove('sweep');
          setBusy(false);
        }, 1600);
      }).catch(function (err) {
        delete form.dataset.busy;
        setBusy(false);
        toast(err.message || 'Could not add to cart. Please try again.');
      });
    });
  });

  /* ---------- sticky mobile buy bar ---------- */
  ready(function () {
    var bar = doc.querySelector('.pdp-stickybar');
    var sentinel = doc.querySelector('[data-buy-sentinel]');
    if (!bar || !sentinel) return;
    /* scroll-position check, not IO: a zero-height sentinel can jump through
       the viewport in one frame on fast scrolls and IO never fires.
       Visual state and a11y state (inert/aria-hidden) are applied together so
       no code path (scroll, resize, init) can show a bar that is still inert. */
    var shown = null; /* null forces the first apply() to stamp a11y state */
    function apply(want) {
      if (want === shown) return;
      shown = want;
      bar.classList.toggle('show', want);
      bar.toggleAttribute('inert', !want);
      bar.setAttribute('aria-hidden', want ? 'false' : 'true');
    }
    function checkBar() {
      apply(sentinel.getBoundingClientRect().top < 0);
    }
    window.addEventListener('scroll', checkBar, { passive: true });
    window.addEventListener('resize', checkBar, { passive: true });
    checkBar();
    var proxy = bar.querySelector('[data-proxy-add]');
    if (proxy) {
      proxy.addEventListener('click', function (e) {
        e.preventDefault();
        var form = doc.querySelector('form[action*="/cart/add"]');
        if (form) {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
      });
    }
  });

  /* ---------- multi-variant PDP: server re-render on variant change ----------
     Full price/low-stock/qty-max/sticky-bar sync without duplicating Liquid
     pricing logic in JS. All products are single-variant today; this guard
     exists so the first 2-variant product isn't silently wrong. */
  ready(function () {
    var vsel = doc.querySelector('.pdp-variants select[name="id"]');
    if (!vsel) return;
    var initial = vsel.value;
    function go() {
      if (vsel.value === initial) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', vsel.value);
      window.location.replace(url.toString());
    }
    /* navigate on COMMIT, never on raw input: arrowing through a closed select
       fires change per keystroke, and navigating there is a WCAG 3.2.2
       change-of-context failure (the same trap the sort select had). Pointer
       selection is a commit; for keyboard, blur/Enter is the commit. */
    var viaPointer = false;
    vsel.addEventListener('pointerdown', function () { viaPointer = true; });
    vsel.addEventListener('change', function () {
      var commit = viaPointer;
      viaPointer = false;
      if (commit) go();
    });
    vsel.addEventListener('blur', function () { viaPointer = false; go(); });
    vsel.addEventListener('keydown', function (e) { if (e.key === 'Enter') go(); });
  });

  /* ---------- PDP gallery thumbs (crossfade, not a hard src pop) ---------- */
  ready(function () {
    var rail = doc.querySelector('.pdp-thumbs');
    if (!rail) return;
    var main = doc.querySelector('.pdp-frame .card-frame img');
    if (!main) return;
    rail.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-full]');
      if (!btn) return;
      e.preventDefault();
      main.classList.add('swapping');
      var swap = function () {
        main.src = btn.getAttribute('data-full');
        if (btn.getAttribute('data-srcset')) main.srcset = btn.getAttribute('data-srcset');
        else main.removeAttribute('srcset');
        var reveal = function () { main.classList.remove('swapping'); };
        if (main.decode) main.decode().then(reveal).catch(reveal);
        else if (main.complete) reveal();
        else main.addEventListener('load', reveal, { once: true });
      };
      setTimeout(swap, reduceMotion ? 0 : 160);
      rail.querySelectorAll('button').forEach(function (b) { b.classList.remove('on'); b.setAttribute('aria-pressed', 'false'); });
      btn.classList.add('on');
      btn.setAttribute('aria-pressed', 'true');
    });
  });

  /* ---------- quick-add: one-tap cart add on supply tiles ---------- */
  ready(function () {
    if (!window.fetch) return;
    doc.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-qadd]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: Number(btn.getAttribute('data-qadd')), quantity: 1 })
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
        return res.json();
      }).then(function () {
        btn.classList.add('added');
        /* on the cart page the line items are server-rendered - reload so the
           new row appears; everywhere else a toast + badge is enough */
        if (location.pathname === '/cart') { window.location.reload(); return; }
        fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { if (cart) setCartCount(cart.item_count); })
          .catch(function () {});
        toast('Added to your cart.', '/cart', 'View cart');
        setTimeout(function () { btn.disabled = false; btn.classList.remove('added'); }, 900);
      }).catch(function (err) {
        btn.disabled = false;
        toast(err.message || 'Could not add to cart. Please try again.');
      });
    });
  });

  /* ---------- collection sort: apply on change (the Apply button stays as the
       no-JS fallback) ---------- */
  ready(function () {
    var box = doc.querySelector('.sort-box');
    if (!box) return;
    var sel = box.querySelector('select');
    if (!sel) return;
    /* auto-submit only for pointer interactions: in Firefox, arrowing through a
       CLOSED select fires change per keystroke, so an unconditional submit
       navigates away before a keyboard user can reach a non-adjacent option
       (WCAG 3.2.2). Keyboard users get native Enter-to-submit + the Apply button. */
    var viaPointer = false;
    sel.addEventListener('pointerdown', function () { viaPointer = true; });
    sel.addEventListener('blur', function () { viaPointer = false; });
    /* one-shot: without consuming the flag, a single mouse open would arm
       auto-submit for every later keyboard change on the same focused select */
    sel.addEventListener('change', function () {
      var go = viaPointer;
      viaPointer = false;
      if (go) box.submit();
    });
  });

  /* ---------- cart page: in-place quantity updates ----------
     fetch /cart/change.js and patch the DOM - no full-page reload, no dark
     flash, no scroll reset. The form submit remains the no-JS fallback. */
  ready(function () {
    var cartForm = doc.querySelector('form[action="/cart"]');
    if (!cartForm) return;
    if (!window.fetch) return; /* native form flow still works */

    function money(cents) {
      var s = (cents / 100).toFixed(2);
      return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    function pulse(el, text) {
      if (!el) return;
      if (text !== undefined && el.textContent !== text) el.textContent = text;
      el.classList.remove('pulse-gold');
      void el.offsetWidth;
      el.classList.add('pulse-gold');
    }
    function patchSummary(cart) {
      var count = doc.querySelector('[data-cart-count]');
      var plural = doc.querySelector('[data-cart-plural]');
      if (count) pulse(count, String(cart.item_count));
      if (plural) plural.textContent = cart.item_count === 1 ? '' : 's';
      pulse(doc.querySelector('[data-cart-subtotal]'), money(cart.items_subtotal_price));
      pulse(doc.querySelector('[data-cart-total]'), money(cart.total_price));
      /* the discount row is always in the DOM (hidden at zero) so an automatic
         discount appearing or vanishing mid-session stays in sync */
      var discRow = doc.querySelector('[data-discount-row]');
      var disc = doc.querySelector('[data-cart-discount]');
      if (disc && typeof cart.total_discount === 'number') {
        disc.textContent = '-' + money(cart.total_discount);
        if (discRow) discRow.hidden = cart.total_discount <= 0;
      }
      setCartCount(cart.item_count);
    }
    function patchLines(cart) {
      doc.querySelectorAll('.cart-row[data-line-key]').forEach(function (row) {
        var key = row.getAttribute('data-line-key');
        var item = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) { item = cart.items[i]; break; }
        }
        if (!item) {
          /* if focus lives in the row being removed (Remove link, qty input),
             park it on the cart heading so keyboard users are not dropped
             at document top */
          if (row.contains(doc.activeElement)) {
            var h = doc.querySelector('main h1');
            /* preventScroll: keyboard users get focus placed and the heading
               announced without yanking a mouse user's viewport to the top */
            if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
          }
          row.classList.add('removing');
          setTimeout(function () { row.remove(); }, 260);
          return;
        }
        var input = row.querySelector('input[name="updates[]"]');
        if (input && String(item.quantity) !== input.value) input.value = item.quantity;
        pulse(row.querySelector('[data-line-price]'), money(item.final_line_price));
      });
    }
    var busy = false;
    var queued = {}; /* per-line: last change per key wins while a request is in flight */
    function drainQueue() {
      for (var k in queued) {
        var qty = queued[k];
        delete queued[k];
        change(k, qty);
        return true; /* one at a time; the completion handler drains the rest */
      }
      return false;
    }
    function change(key, qty) {
      if (busy) { queued[key] = qty; return; }
      busy = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'update failed'); });
        return r.json();
      }).then(function (cart) {
        if (cart.item_count === 0) { window.location.reload(); return; } /* server renders the designed empty state */
        patchLines(cart);
        patchSummary(cart);
      }).catch(function (err) {
        /* show the error, THEN re-sync with server truth - an instant reload
           would destroy the message before it can be read */
        toast(err.message || 'Could not update your cart. Please try again.');
        setTimeout(function () { window.location.reload(); }, 1800);
      }).then(function () {
        busy = false;
        drainQueue();
      });
    }
    /* per-line debounce timers: one shared timer would let an edit on line B
       cancel line A's pending change inside the debounce window */
    var timers = {};
    function clearAllTimers() {
      for (var k in timers) clearTimeout(timers[k]);
      timers = {};
    }
    cartForm.addEventListener('change', function (e) {
      if (!e.target.matches('input[name="updates[]"]')) return;
      var input = e.target;
      var key = input.getAttribute('data-line-key');
      if (!key) { cartForm.submit(); return; } /* markup without keys: legacy path */
      clearTimeout(timers[key]);
      timers[key] = setTimeout(function () {
        delete timers[key];
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) qty = 0;
        change(key, qty);
      }, 350);
    });
    /* Remove links: same fetch path with a row fade; native href stays as fallback */
    cartForm.addEventListener('click', function (e) {
      var rm = e.target.closest('a.remove[data-line-key]');
      if (rm) {
        e.preventDefault();
        clearTimeout(timers[rm.getAttribute('data-line-key')]);
        var row = rm.closest('.cart-row');
        if (row) row.classList.add('removing');
        change(rm.getAttribute('data-line-key'), 0);
        return;
      }
      /* pending updates must not race a checkout navigation */
      if (e.target.closest('button[name="checkout"]')) clearAllTimers();
    }, true);
  });

  /* ---------- predictive search overlay ---------- */
  ready(function () {
    var opener = doc.querySelector('[data-seek-open]');
    var overlay = doc.querySelector('.seek-overlay');
    if (!opener || !overlay) return;
    var input = overlay.querySelector('input[type="search"]');
    var results = overlay.querySelector('.seek-results');
    var hint = overlay.querySelector('.seek-hint');
    var lastFocus = null;
    function open(e) {
      if (e) e.preventDefault();
      lastFocus = doc.activeElement;
      overlay.classList.add('open');
      doc.body.style.overflow = 'hidden';
      setTimeout(function () { input.focus(); }, 40);
    }
    function close() {
      overlay.classList.remove('open');
      doc.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }
    opener.addEventListener('click', open);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    var closeBtn = overlay.querySelector('.seek-close');
    if (closeBtn) closeBtn.addEventListener('click', close);
    doc.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('open')) close();
    });
    /* keep Tab inside the dialog while it is open; hidden elements (the hint block
       is display:none'd once results render) must not anchor the trap or Tab
       escapes the aria-modal dialog */
    overlay.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.filter.call(
        overlay.querySelectorAll('input, button, a[href]'),
        function (el) { return el.getClientRects().length > 0; }
      );
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    var seq = 0, debounce = null;
    /* Every route out of this overlay must carry options[prefix]=last. The overlay's
       dropdown is fed by /search/suggest.json, which prefix-matches by default; the
       results page does NOT unless asked. The hidden input on the overlay form exists
       for exactly this, but the Enter handler below calls preventDefault() and the
       "All results" row is a hand-built href - so with JS on, the form (and its hidden
       input) is never used and the fix silently did nothing. One helper now, so the two
       JS routes cannot drift apart from the form again. */
    function searchUrl(q) {
      return '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
    }
    function render(products, q) {
      results.innerHTML = '';
      if (!products.length) {
        var d = doc.createElement('div');
        d.className = 'seek-empty';
        d.textContent = 'No matches for "' + q + '" - try another word.';
        results.appendChild(d);
        return;
      }
      products.forEach(function (p) {
        var a = doc.createElement('a');
        a.className = 'seek-item';
        a.href = p.url;
        var img = doc.createElement('img');
        img.alt = '';
        if (p.image) img.src = p.image;
        var t = doc.createElement('span');
        t.className = 't';
        var b = doc.createElement('b');
        b.textContent = p.title;
        var small = doc.createElement('small');
        small.textContent = p.available ? 'In stock - ships in 24h' : 'Sold out - notify me';
        t.appendChild(b); t.appendChild(small);
        var pr = doc.createElement('span');
        pr.className = 'pr';
        pr.textContent = p.price;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.appendChild(img); a.appendChild(t); a.appendChild(pr);
        results.appendChild(a);
      });
      var all = doc.createElement('a');
      all.className = 'seek-all';
      all.href = searchUrl(q);
      all.textContent = 'All results for "' + q + '"';
      results.appendChild(all);
    }
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounce);
      if (q.length < 2) {
        results.innerHTML = '';
        if (hint) hint.style.display = '';
        return;
      }
      debounce = setTimeout(function () {
        var mySeq = ++seq;
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last')
          .then(function (r) { if (!r.ok) throw new Error('suggest ' + r.status); return r.json(); })
          .then(function (data) {
            if (mySeq !== seq) return;
            if (hint) hint.style.display = 'none';
            var prods = (((data || {}).resources || {}).results || {}).products || [];
            render(prods.map(function (p) {
              var imgUrl = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
              if (imgUrl) imgUrl += (imgUrl.indexOf('?') > -1 ? '&' : '?') + 'width=120';
              return {
                url: p.url,
                title: p.title,
                image: imgUrl,
                price: p.price ? '$' + String(p.price).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '',
                available: p.available !== false
              };
            }), q);
          })
          .catch(function () { /* stay quiet; Enter still goes to /search */ });
      }, 220);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        var q = input.value.trim();
        if (q) window.location.href = searchUrl(q);
      }
    });
  });

  /* ---------- restock notify -> STOQ back-in-stock app ----------
     Primary path: keyless fetch to STOQ so EXISTING customers get captured
     too (Shopify's native customer form only applies tags to brand-new
     records and can silently no-op for repeat emails). The Liquid customer
     form stays wired underneath as the no-JS fallback. */
  ready(function () {
    if (!window.fetch) return; /* ancient browsers ride the native fallback */
    var forms = doc.querySelectorAll('form.notify-form');
    forms.forEach(function (f) {
      var cfgEl = f.querySelector('script[data-stoq-config]');
      if (!cfgEl) return; /* server-rendered success panel: nothing to wire */
      var cfg = null;
      try { cfg = JSON.parse(cfgEl.textContent); } catch (err) {}
      if (!cfg || !cfg.variantId) return; /* bad config: keep native fallback */
      var btn = f.querySelector('button[type="submit"]');
      function send() {
        if (f.dataset.busy) return;
        var input = f.querySelector('input[name="contact[email]"]');
        var email = input ? input.value.trim() : '';
        if (!input || !email || !input.checkValidity()) {
          if (input && input.reportValidity) input.reportValidity();
          else if (input) input.focus();
          return;
        }
        var original = btn ? btn.textContent : '';
        f.dataset.busy = '1';
        if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
        var oldErr = f.querySelector('.notify-err');
        if (oldErr) oldErr.remove();
        fetch('https://app.stoqapp.com/api/v1/intents.json', {
          method: 'POST',
          headers: {
            'X-Shopify-Shop-Domain': cfg.shop,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            intent: {
              shopify_variant_id: Number(cfg.variantId),
              shopify_product_id: Number(cfg.productId),
              shopify_market_id: Number(cfg.marketId) || 1,
              channel: 'email',
              quantity: 1,
              source: 'api'
            },
            customer: { email: email, accepts_marketing: false }, /* restock alert is transactional - the form promises "no spam" */
            product: {
              variant_count: Number(cfg.variantCount) || 1,
              title: cfg.productTitle || '',
              variant_title: cfg.variantTitle || ''
            }
          })
        }).then(function (res) {
          if (!res.ok) throw new Error('stoq ' + res.status);
          /* stay busy: fields are gone, a second submit is meaningless */
          var fields = f.querySelector('.notify-fields');
          var note = f.querySelector('.notify-note');
          var ok = f.querySelector('[data-notify-ok]');
          if (fields) fields.style.display = 'none';
          if (note) note.style.display = 'none';
          if (ok) ok.style.display = 'flex';
          /* announce into the always-rendered live region (a display-toggled
             panel that was hidden at load is not announced by screen readers) */
          var live = f.querySelector('[data-notify-live]');
          if (live) live.textContent = "You're on the list - we'll email you if it comes back in stock.";
        }).catch(function () {
          delete f.dataset.busy;
          if (btn) { btn.disabled = false; btn.textContent = original; }
          var oldP = f.querySelector('.notify-err');
          if (oldP) oldP.remove();
          var p = doc.createElement('p');
          p.className = 'notify-err';
          p.textContent = 'Could not save your alert - please try again in a moment.';
          var fields = f.querySelector('.notify-fields');
          if (fields) fields.insertAdjacentElement('afterend', p);
          else f.appendChild(p);
          var alertEl = f.querySelector('[data-notify-alert]');
          if (alertEl) alertEl.textContent = 'Could not save your alert - please try again in a moment.';
        });
      }
      /* Shopify's storefront-forms captcha intercepts TRUSTED clicks on
         customer-form submit buttons (preventDefault -> challenge -> native
         form.submit(), which skips submit listeners). A plain button opts
         out of that flow; no-JS keeps the submit button + native form. */
      if (btn) {
        btn.setAttribute('type', 'button');
        btn.addEventListener('click', send);
      }
      var emailField = f.querySelector('input[name="contact[email]"]');
      if (emailField) {
        emailField.addEventListener('keydown', function (e) {
          /* swallow implicit submission so Enter never reaches the captcha
             layer, which would race a native POST against our fetch */
          if (e.key === 'Enter') { e.preventDefault(); send(); }
        });
      }
      f.addEventListener('submit', function (e) {
        e.preventDefault(); /* any leftover submission vector */
        send();
      });
    });
  });

  /* ---------- customer-form success disambiguation ----------
     Shopify's posted_successfully? is shared across customer forms on a page;
     remember which form actually posted and hide the others' success panels. */
  ready(function () {
    var forms = doc.querySelectorAll('form.notify-form, form[id^="newsletter"], form[id^="notify"]');
    if (!forms.length) return;
    forms.forEach(function (f) {
      f.addEventListener('submit', function () {
        try { sessionStorage.setItem('tntcg-posted', f.id || ''); } catch (err) {}
      });
    });
    var posted = null;
    try { posted = sessionStorage.getItem('tntcg-posted'); } catch (err) {}
    var anySuccess = doc.querySelector('.notify-ok');
    if (!anySuccess) return;
    forms.forEach(function (f) {
      var ok = f.querySelector('.notify-ok');
      if (ok && posted && f.id && f.id !== posted) ok.style.display = 'none';
    });
    try { sessionStorage.removeItem('tntcg-posted'); } catch (err) {}
  });

  /* ---------- burger aria sync ----------
     aria-expanded goes on the checkbox itself (it carries aria-controls and is the
     focusable control; the burger label is aria-hidden). The old selector
     '.nav-burger[aria-controls]' matched nothing, so this sync never ran. */
  ready(function () {
    var toggle = doc.getElementById('navtoggle');
    if (!toggle) return;
    var sync = function () {
      toggle.setAttribute('aria-expanded', toggle.checked ? 'true' : 'false');
    };
    toggle.addEventListener('change', sync);
    sync();
  });

  /* ---------- Sealed box breakdown (/pages/card-pull-rates-set-info) ----------
     Restored + widened 2026-08-02. This widget lived in the tier3 script branch, which
     was deleted 2026-07-01 - the page shipped a permanent "Loading interactive
     calculator..." to real customers for a month. It was also Kayou-only while the
     catalogue had moved on to Pokemon, Lorcana and supplies.

     SOURCING, and why the wording is careful:
     - Kayou is the ONLY line whose publisher prints a pack production ratio on the box,
       so it is the only one that gets a rarity table.
     - The Pokemon Company does NOT publish pull-rate odds. We publish CONTENTS ONLY for
       Pokemon and never a per-box hit rate.
     - Pokemon contents were confirmed against the publisher's product listings and
       corroborated across major retailers. pokemon.com blocks automated fetching, so
       nothing is claimed as "rendered from pokemon.com" - the page says "publisher-stated,
       cross-checked against major retailers", which is exactly what was done.
     - Case sizes are usually only retailer/distributor listed; those are labelled in the
       UI rather than presented as manufacturer facts.
     - Anything that could not be verified is absent. No estimates. */
  ready(function () {
    var slot = doc.getElementById('tntcg-ev-calc');
    if (!slot || slot.dataset.tntcgInit) return;
    slot.dataset.tntcgInit = '1';

    var D = '◇'; /* the parallel-rarity diamond Kayou prints */
    var EN = ' — ';

    /* Per-box rarity averages: Kayou only. The common row is computed as
       (box total - everything else) so the column always reconciles to 96. */
    var KAYOU3 = [
      { tier: 'SR' + EN + 'Super Rare', per: 19 },
      { tier: 'SSR' + EN + 'Ninjutsu Showcase', per: 15.5 },
      { tier: 'HR' + EN + 'Holo Rare (Story Flashbacks)', per: 24 },
      { tier: 'UR' + EN + 'Ninja Profiles', per: 1 },
      { tier: D + 'UR' + EN + 'Parallel UR (Graffiti)', per: 0.4 },
      { tier: 'PU' + EN + 'Parallel Universe', per: 0.3 },
      { tier: 'MR' + EN + 'Miracle Rare', per: 0.1 },
      { tier: 'BP' + EN + 'Brush Rare (#/999)', per: 0.05 },
      { tier: 'AR' + EN + 'Amber / Ukiyo-e (#/777)', per: 0.04 },
      { tier: 'SE' + EN + 'Scenario-E (#/220)', per: 0.012 },
      { tier: 'ASP' + EN + 'Amber Special (#/99)', per: 0.012 },
      { tier: D + 'ASP' + EN + 'Parallel Amber Special (#/9)', per: 0.001 }
    ];
    var KAYOU2 = [
      { tier: 'SR' + EN + 'Super Rare', per: 19 },
      { tier: 'SSR' + EN + 'Ninjutsu Showcase', per: 14.5 },
      { tier: 'PTR' + EN + 'Character Posters', per: 24 },
      { tier: 'UR' + EN + 'Ninja Profiles', per: 2 },
      { tier: 'PU' + EN + 'Parallel Universe', per: 0.5 },
      { tier: 'MR' + EN + 'Constellations', per: 0.3 },
      { tier: 'BP' + EN + 'Ukiyo-e Battle', per: 0.1 },
      { tier: 'AR' + EN + 'Ideal Dreams', per: 0.05 },
      { tier: 'SP' + EN + 'Custom Stained Glass', per: 0.03 },
      { tier: 'SE' + EN + 'Interlude Theater', per: 0.01 }
    ];

    var PK_PACK = '10 + 1 Energy';
    var ETB_CONTENTS = '9 booster packs, 1 full-art foil promo card, 65 card sleeves, 45 Energy cards, ' +
      'a player\'s guide, 6 damage-counter dice, 1 competition-legal coin-flip die, 2 condition markers, ' +
      'and a collector\'s box with 4 dividers. Which Pokemon appears on the promo depends on the set.';

    var LINES = [
      { id: 'k3', group: 'Kayou Naruto', label: 'Series 3 booster box', packs: 12, perPack: '8', total: 96,
        promo: 1, perCase: '16 boxes', caseNote: 'publisher-stated',
        contents: '12 booster packs of 8 cards, plus 1 promo pack (Naruto PR-009 or Sasuke PR-010). The full set runs 185 cards across 13 rarities.',
        rarities: KAYOU3 },
      { id: 'k2', group: 'Kayou Naruto', label: 'Series 2 booster box', packs: 12, perPack: '8', total: 96,
        promo: 1, perCase: '16 boxes', caseNote: 'publisher-stated',
        contents: '12 booster packs of 8 cards, plus 1 promo pack. The full set runs 168 cards across 11 rarities.',
        rarities: KAYOU2 },
      { id: 'etb', group: 'Pokemon', label: 'Elite Trainer Box', packs: 9, perPack: PK_PACK,
        perCase: '10 boxes', caseNote: 'retailer-listed', contents: ETB_CONTENTS },
      { id: 'bb', group: 'Pokemon', label: 'Booster Box (36-pack display)', packs: 36, perPack: PK_PACK,
        perCase: '6 displays', caseNote: 'retailer-listed',
        contents: '36 booster packs and nothing else - this is the display carton shops open from.' },
      { id: 'bundle', group: 'Pokemon', label: 'Booster Bundle', packs: 6, perPack: PK_PACK,
        contents: '6 booster packs in a small display sleeve.' },
      { id: 'upc', group: 'Pokemon', label: 'Ultra-Premium Collection', packs: 18, perPack: '',
        perCase: '4 boxes', caseNote: 'retailer-listed',
        contents: '18 booster packs plus the oversized promo, metal coin and collector case. The publisher does not state which expansions the 18 packs are drawn from, so neither do we.' },
      { id: 'poster', group: 'Pokemon', label: 'Premium Poster Collection', packs: 10, perPack: PK_PACK,
        perCase: '6 boxes', caseNote: 'retailer-listed', contents: '10 booster packs, a promo card and the poster.' },
      { id: 'megaex', group: 'Pokemon', label: 'Mega ex Box', packs: 4, perPack: PK_PACK,
        perCase: '6 boxes', caseNote: 'retailer-listed', contents: '4 booster packs plus the foil promo card.' },
      { id: 'tin', group: 'Pokemon', label: 'Mega Moonlit Tin', packs: 4, perPack: '',
        perCase: '6 tins', caseNote: 'retailer-listed',
        contents: '4 booster packs plus the etched foil promo. The publisher does not name which expansions the packs come from, so the per-pack count is left unstated rather than guessed.' },
      { id: 'day', group: 'Pokemon', label: 'Pokemon Day 2026 Collection', packs: 3, perPack: PK_PACK,
        perCase: '6 boxes', caseNote: 'retailer-listed', contents: '3 booster packs plus the promo card.' },
      { id: 'sticker', group: 'Pokemon', label: 'Tech Sticker Collection', packs: 3, perPack: PK_PACK,
        perCase: '12 collections', caseNote: 'retailer-listed', contents: '3 booster packs, a promo card and the sticker sheet.' },
      { id: 'stellar', group: 'Pokemon', label: 'Stellar Miracle Deck Build Box (Japanese)', packs: 10, perPack: '5', total: 50,
        contents: '10 Japanese Stellar Miracle packs of 5 cards, plus the deck-build accessories. Japanese packs are smaller than English ones.' },
      { id: 'lorcana', group: 'Disney Lorcana', label: 'Azurite Sea Stitch Gift Set', packs: 4, perPack: '12', total: 48,
        contents: '4 Azurite Sea booster packs of 12 cards, plus the exclusive foil card, deck box and sleeves.' }
    ];

    function options() {
      var html = '', g = null;
      LINES.forEach(function (l) {
        if (l.group !== g) {
          if (g !== null) html += '</optgroup>';
          html += '<optgroup label="' + l.group + '">';
          g = l.group;
        }
        html += '<option value="' + l.id + '">' + l.label + '</option>';
      });
      return html + '</optgroup>';
    }

    slot.innerHTML =
      '<div class="evc">' +
        '<h3 class="evc-title">What a sealed box actually holds</h3>' +
        '<div class="evc-controls">' +
          '<div class="evc-field">' +
            '<label class="evc-label" for="evc-line">Product</label>' +
            '<div class="evc-select"><select id="evc-line">' + options() + '</select></div>' +
          '</div>' +
          '<div class="evc-field">' +
            '<label class="evc-label" for="evc-qty">How many</label>' +
            '<div class="evc-step">' +
              '<button type="button" data-d="-1" aria-label="One fewer">&minus;</button>' +
              '<input id="evc-qty" type="number" min="1" max="200" step="1" value="1" inputmode="numeric">' +
              '<button type="button" data-d="1" aria-label="One more">+</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="evc-stats" aria-live="polite" data-out="stats"></div>' +
        '<p class="evc-contents" data-out="contents"></p>' +
        '<div class="evc-scroll" data-out="tablewrap"></div>' +
        '<p class="evc-note" data-out="note"></p>' +
      '</div>';

    var sel = slot.querySelector('#evc-line');
    var qty = slot.querySelector('#evc-qty');

    function fmt(n) {
      if (n >= 10) return (Math.round(n * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });
      if (n >= 1) return n.toFixed(1);
      if (n >= 0.1) return n.toFixed(2);
      return n.toFixed(3);
    }
    function stat(num, cap) {
      return '<div class="evc-stat"><span class="evc-num">' + num + '</span><span class="evc-cap">' + cap + '</span></div>';
    }

    function update() {
      var n = parseInt(qty.value, 10);
      if (!(n >= 1)) n = 1;
      if (n > 200) n = 200;
      if (String(n) !== qty.value) qty.value = n;

      var line = LINES.filter(function (l) { return l.id === sel.value; })[0] || LINES[0];

      var s = stat((line.packs * n).toLocaleString(), 'Booster packs');
      if (line.perPack) s += stat(line.perPack, 'Cards per pack');
      if (line.total) s += stat((line.total * n).toLocaleString(), 'Cards');
      if (line.promo) s += stat((line.promo * n).toLocaleString(), line.promo * n === 1 ? 'Promo pack' : 'Promo packs');
      if (line.perCase) s += stat(line.perCase, 'Per case (' + line.caseNote + ')');
      slot.querySelector('[data-out=stats]').innerHTML = s;
      slot.querySelector('[data-out=contents]').textContent = line.contents;

      var wrap = slot.querySelector('[data-out=tablewrap]');
      if (line.rarities) {
        var listed = line.rarities.reduce(function (a, r) { return a + r.per; }, 0);
        var rows = [{ tier: 'R' + EN + 'Story Recap', per: Math.max(0, line.total - listed) }].concat(line.rarities);
        var html = '<table class="evc-table">' +
          '<caption class="visually-hidden">Average cards per rarity tier</caption>' +
          '<thead><tr><th scope="col">Rarity tier</th><th scope="col">Per box</th><th scope="col">Your total</th></tr></thead><tbody>';
        rows.forEach(function (r) {
          html += '<tr><th scope="row">' + r.tier + '</th><td>' + fmt(r.per) + '</td><td>' + fmt(r.per * n) + '</td></tr>';
        });
        wrap.innerHTML = html + '</tbody></table>';
      } else {
        wrap.innerHTML = '';
      }

      var note;
      if (line.rarities) {
        note = '<strong>Read this before you buy.</strong> The common tiers follow the pack production ratio ' +
          'printed on the box. The chase tiers are <em>community-tracked estimates</em>, not official odds, and ' +
          'they are wildly variable &mdash; some boxes hit, most do not. Figures are averages across many boxes ' +
          'and are rounded for readability, so the column reads a hair either side of the box total. They are ' +
          'never a promise about your box. We do not weigh, search, or pre-pull product.';
      } else {
        note = '<strong>Why there are no odds here.</strong> The Pok&eacute;mon Company does not publish pull ' +
          'rates, so nobody selling you a box can honestly quote them &mdash; and we will not invent them. What ' +
          'you see above is contents only: publisher-stated, cross-checked against major retailers. Anything ' +
          'marked "retailer-listed" comes from distributor listings rather than the publisher. We do not weigh, ' +
          'search, or pre-pull product.';
      }
      slot.querySelector('[data-out=note]').innerHTML = note;
    }

    sel.addEventListener('change', update);
    [].slice.call(slot.querySelectorAll('.evc-step button')).forEach(function (b) {
      b.addEventListener('click', function () {
        qty.value = (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.d, 10);
        update();
      });
    });
    qty.addEventListener('input', update);
    qty.addEventListener('change', update);
    update();
  });

  /* ---------- hero rotator ----------
     Crossfades the plinth through the in-stock catalogue and keeps the caption,
     link, price, stock line and Factory Sealed badge pointing at whatever is
     currently showing. Auto-advance requires: motion OK, tab visible, pointer
     and focus outside the hero, and no user pause; the dots and pause toggle
     stay operable in every one of those states. */
  ready(function () {
    var root = doc.querySelector('[data-hero-rotator]');
    if (!root) return;
    var slides = [].slice.call(root.querySelectorAll('.hero-slide'));
    if (slides.length < 2) return;

    var link = root.querySelector('[data-hero-link]');
    var cap = root.querySelector('[data-hero-cap]');
    var seal = root.querySelector('[data-hero-seal]');
    var status = root.querySelector('[data-hero-status]');
    var pauseBtn = root.querySelector('[data-hero-pause]');
    var dots = [].slice.call(root.querySelectorAll('.hero-dot'));
    var capName = cap && cap.querySelector('b');
    var capPrice = cap && cap.querySelector('.pr');
    var capStock = cap && cap.querySelector('.stock');
    var idx = 0;
    var timer = null;
    var HOLD = 4200;
    var hovering = false, focused = false, userPaused = false;

    /* rebuild the price lockup with DOM nodes rather than innerHTML - same shape
       as snippets/tntcg-fant-price.liquid: SR-only plain price + styled lockup */
    function priceLockup(money) {
      var parts = String(money).split('.');
      var wrap = doc.createElement('span');
      wrap.className = 'plk';
      var sr = doc.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = '$' + money;
      var vis = doc.createElement('span');
      vis.setAttribute('aria-hidden', 'true');
      var cur = doc.createElement('span');
      cur.className = 'cur';
      cur.textContent = '$';
      vis.appendChild(cur);
      vis.appendChild(doc.createTextNode(parts[0]));
      if (parts[1] && parts[1] !== '00') {
        var sup = doc.createElement('sup');
        sup.className = 'cents';
        sup.textContent = parts[1];
        vis.appendChild(sup);
      }
      wrap.appendChild(sr);
      wrap.appendChild(vis);
      return wrap;
    }

    function show(n, announce) {
      n = (n + slides.length) % slides.length;
      if (n === idx) return;
      slides[idx].classList.remove('is-on');
      slides[idx].setAttribute('aria-hidden', 'true');
      slides[n].classList.add('is-on');
      slides[n].removeAttribute('aria-hidden');
      if (dots[idx]) { dots[idx].classList.remove('is-on'); dots[idx].setAttribute('aria-pressed', 'false'); }
      if (dots[n]) { dots[n].classList.add('is-on'); dots[n].setAttribute('aria-pressed', 'true'); }
      idx = n;

      var d = slides[n].dataset;
      if (link && d.url) link.setAttribute('href', d.url);
      if (cap && d.url) cap.setAttribute('href', d.url);
      if (capName && d.title) capName.textContent = d.title;
      if (capPrice && d.money) {
        capPrice.textContent = '';
        capPrice.appendChild(priceLockup(d.money));
      }
      var live = d.avail === '1';
      if (capStock) {
        capStock.textContent = live ? 'In stock · ships in 24h' : 'Sold out · get a restock alert';
        capStock.classList.toggle('in', live);
      }
      /* the chip is an authenticity claim: sealed product only, never supplies */
      if (seal) seal.style.display = (live && d.sealed === '1') ? '' : 'none';
      /* announce manual picks only - a polite region firing every 4s is noise */
      if (announce && status && d.title) {
        status.textContent = d.title + ', $' + d.money + (live ? ', in stock' : ', sold out');
      }
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }
    function canRun() { return !reduceMotion && !doc.hidden && !hovering && !focused && !userPaused; }
    function start() {
      if (timer || !canRun()) return;
      timer = setInterval(function () { show(idx + 1, false); }, HOLD);
    }
    function syncPause() {
      if (!pauseBtn) return;
      pauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
      pauseBtn.setAttribute('aria-label', userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation');
      pauseBtn.classList.toggle('is-paused', userPaused);
    }

    dots.forEach(function (b, i) {
      b.addEventListener('click', function () {
        userPaused = true; /* a hand-picked slide stays picked */
        syncPause();
        stop();
        show(i, true);
      });
    });
    if (pauseBtn) {
      pauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        syncPause();
        if (userPaused) { stop(); } else { start(); }
      });
    }

    root.addEventListener('mouseenter', function () { hovering = true; stop(); });
    root.addEventListener('mouseleave', function () { hovering = false; start(); });
    root.addEventListener('focusin', function () { focused = true; stop(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { focused = false; start(); }
    });
    doc.addEventListener('visibilitychange', function () { if (doc.hidden) { stop(); } else { start(); } });

    start();
  });

  /* ==== week1:Homepage ==== */
  (function () {
/* ---------- wake the flagship: hero plinth joins the holo tilt rig ----------
     The rotator stacks absolutely-positioned .hero-slide imgs inside the frame,
     so the FRAME is the tilt target (CSS transforms .card-frame.tilt itself)
     and will-change is promoted on the stage, not the img. */
  ready(function () {
    if (reduceMotion || !finePointer || !wideScreen) return;
    var stage = doc.querySelector('.plinth .card-frame');
    if (!stage || !stage.querySelector('img')) return;
    stage.classList.add('tilt');
    var glare = doc.createElement('span');
    glare.className = 'glare';
    glare.setAttribute('aria-hidden', 'true');
    stage.appendChild(glare);
    var maxDeg = 3.5; /* the frame is large - keep the swing shallow */
    var cur = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
    var tgt = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
    var active = false;
    function loop() {
      var k = 0.14;
      var done = true;
      for (var p in cur) {
        cur[p] += (tgt[p] - cur[p]) * k;
        if (Math.abs(tgt[p] - cur[p]) > 0.002) done = false;
      }
      stage.style.setProperty('--rx', cur.rx.toFixed(2) + 'deg');
      stage.style.setProperty('--ry', cur.ry.toFixed(2) + 'deg');
      stage.style.setProperty('--gl', cur.gl.toFixed(3));
      stage.style.setProperty('--gx', cur.gx.toFixed(1) + '%');
      stage.style.setProperty('--gy', cur.gy.toFixed(1) + '%');
      stage.style.setProperty('--tz', cur.tz.toFixed(3));
      if (done && !active) { removeTick(loop); stage.style.willChange = ''; }
    }
    stage.addEventListener('pointerenter', function () {
      active = true;
      stage.style.willChange = 'transform'; /* promote only while interacting */
      tgt.gl = 0.5; tgt.tz = 1.015;
      addTick(loop);
    });
    stage.addEventListener('pointermove', function (e) {
      var r = stage.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width;
      var py = (e.clientY - r.top) / r.height;
      tgt.ry = (px - 0.5) * 2 * maxDeg;
      tgt.rx = (0.5 - py) * 2 * maxDeg;
      tgt.gx = px * 100;
      tgt.gy = py * 100;
    });
    stage.addEventListener('pointerleave', function () {
      active = false;
      tgt.rx = 0; tgt.ry = 0; tgt.gl = 0; tgt.gx = 50; tgt.gy = 42; tgt.tz = 1;
    });
  });

  /* ---------- presentation glint: sheen when the rotator advances ----------
     Decoupled from the rotator on purpose: a MutationObserver watches the
     .hero-slide class swaps the rotator already performs, so neither module
     needs to know the other exists. */
  ready(function () {
    if (reduceMotion || !('MutationObserver' in window)) return;
    var root = doc.querySelector('[data-hero-rotator]');
    if (!root) return;
    var frame = root.querySelector('.card-frame');
    var slides = root.querySelectorAll('.hero-slide');
    if (!frame || slides.length < 2) return;
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var was = (muts[i].oldValue || '').indexOf('is-on') > -1;
        if (!was && muts[i].target.classList.contains('is-on')) {
          frame.classList.remove('presenting');
          void frame.offsetWidth; /* restart the keyframe on rapid advances */
          frame.classList.add('presenting');
          break;
        }
      }
    });
    slides.forEach(function (s) {
      mo.observe(s, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    });
  });
  })();

  /* ==== week1:Search + 404 ==== */
  (function () {
/* ---------- seek overlay: keyboard (/ summons, arrows walk the dropdown) ---------- */
  ready(function () {
    var overlay = doc.querySelector('.seek-overlay');
    if (!overlay) return;
    var opener = doc.querySelector('[data-seek-open]');
    var input = overlay.querySelector('input[type="search"]');
    var results = overlay.querySelector('.seek-results');

    /* Press / anywhere: summon the overlay unless the user is already typing.
       Reuses the opener's click wiring so focus management stays in one place. */
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (!opener || overlay.classList.contains('open')) return;
      /* another modal surface already owns the top layer: opening behind it
         would put focus in an invisible (or inert) search field and leave two
         focus traps live at once */
      if (doc.querySelector('dialog[open]')) return;
      var haulOpen = doc.getElementById('tntcg-haul');
      if (haulOpen && haulOpen.classList.contains('open')) return;
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''))) return;
      e.preventDefault();
      opener.click();
    });

    /* Arrow keys walk the predictive dropdown. The .seek-item.sel wash already
       shipped in the CSS; this is the JS that finally applies it. Capture phase
       on the overlay so a selected Enter wins over the input's own Enter handler
       (which would otherwise navigate to the all-results page). */
    if (!input || !results) return;
    function selectedItem() { return results.querySelector('.seek-item.sel'); }
    overlay.addEventListener('keydown', function (e) {
      if (e.target !== input) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var items = results.querySelectorAll('.seek-item');
        if (!items.length) return;
        e.preventDefault();
        var cur = selectedItem();
        var idx = Array.prototype.indexOf.call(items, cur);
        idx = e.key === 'ArrowDown' ? (idx + 1) % items.length
                                    : (idx <= 0 ? items.length - 1 : idx - 1);
        if (cur) { cur.classList.remove('sel'); cur.setAttribute('aria-selected', 'false'); }
        var next = items[idx];
        next.classList.add('sel');
        next.setAttribute('aria-selected', 'true');
        if (!next.id) next.id = 'seek-opt-' + idx;
        input.setAttribute('aria-activedescendant', next.id);
        if (next.scrollIntoView) next.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        var sel = selectedItem();
        if (sel && sel.href) {
          e.preventDefault();
          e.stopPropagation(); /* the input's own Enter handler must not also navigate */
          window.location.href = sel.href;
        }
      } else {
        /* typing (or Esc) invalidates the highlight; a re-render replaces the
           nodes anyway, but Enter must never chase a stale selection */
        var stale = selectedItem();
        if (stale) stale.classList.remove('sel');
        input.removeAttribute('aria-activedescendant');
      }
    }, true);
  });
  })();

  /* ==== week1:Cart ==== */
  (function () {
/* ---------- cart page: manifest ceremony ----------
     patchSummary lives inside its own ready() closure, so this layer listens
     to the DOM instead: any text change on the running total means the haul
     changed. Recounts the haul subline + collector's counsel from the rows
     the updater already patched, then sweeps the sheen across Checkout. */
  ready(function () {
    var total = doc.querySelector('[data-cart-summary] [data-cart-total]');
    if (!total || !window.MutationObserver) return;
    var sumBtn = doc.querySelector('.sum-btn');
    var hCount = doc.querySelector('[data-haul-count]');
    var hPlural = doc.querySelector('[data-haul-plural]');
    var note = doc.querySelector('[data-bulk-note]');
    var callout = doc.querySelector('[data-bulk-callout]');
    var bCount = doc.querySelector('[data-bulk-count]');
    function recount() {
      var items = 0, sealed = 0;
      doc.querySelectorAll('.cart-row[data-line-key]:not(.removing)').forEach(function (row) {
        var input = row.querySelector('input[name="updates[]"]');
        var qty = input ? parseInt(input.value, 10) : 0;
        if (isNaN(qty) || qty < 0) qty = 0;
        items += qty;
        if (row.getAttribute('data-ptype') !== 'Card Supplies') sealed += qty;
      });
      if (hCount) hCount.textContent = String(items);
      if (hPlural) hPlural.textContent = items === 1 ? '' : 's';
      if (bCount) bCount.textContent = String(sealed);
      if (note) note.hidden = sealed >= 2;
      if (callout) callout.hidden = sealed < 2;
    }
    new MutationObserver(function () {
      recount();
      /* sheen restart idiom from showAdded(); also CSS-gated for reduced motion */
      if (sumBtn && !reduceMotion) {
        sumBtn.classList.remove('sweep');
        void sumBtn.offsetWidth;
        sumBtn.classList.add('sweep');
      }
    }).observe(total, { childList: true, characterData: true, subtree: true });
  });
  })();

  /* ==== week1:Collector tools page + nav ==== */
  (function () {
/* ---------- Odds flip + case arithmetic (/pages/card-pull-rates-set-info) ----------
     Decorates the sealed-box estimator's OUTPUT after each render; the widget
     above is untouched. Two additions, both derived from numbers the estimator
     has already rendered - no new data enters here:
     - a segmented control (the .evc-seg CSS shipped unused since 2026-08-02)
       flips the per-box column into collector-speak: a tier averaging under
       one per box reads "≈ 1 in N boxes", computed by inverting the rendered
       figure, so it inherits every rounding caveat the note under the table
       already states.
     - once the quantity clears the case count the widget already shows
       ("16 boxes", publisher-stated or retailer-listed), a stat tile does the
       case division: "1 case + 4".
     A MutationObserver re-applies both after every estimator re-render, so the
     enhancement survives product and quantity changes without touching
     update(). A contents-only line (Pokemon) has no table: the control hides
     rather than sit there as a switch that does nothing. */
  ready(function () {
    var slot = doc.getElementById('tntcg-ev-calc');
    if (!slot || slot.dataset.tntcgFlip) return;
    var evc = slot.querySelector('.evc');
    if (!evc) return; /* estimator did not build - nothing to decorate */
    slot.dataset.tntcgFlip = '1';

    var wrap = evc.querySelector('[data-out=tablewrap]');
    var stats = evc.querySelector('[data-out=stats]');
    var qtyEl = evc.querySelector('#evc-qty');
    if (!wrap || !stats) return;

    var mode = 'avg';

    var seg = doc.createElement('div');
    seg.className = 'evc-seg evc-flip';
    seg.setAttribute('role', 'group');
    seg.setAttribute('aria-label', 'Show rarity column as averages or odds');
    seg.innerHTML =
      '<button type="button" class="evc-seg-btn is-on" data-mode="avg" aria-pressed="true">Avg per box</button>' +
      '<button type="button" class="evc-seg-btn" data-mode="odds" aria-pressed="false">Odds</button>';
    evc.insertBefore(seg, wrap);

    function oddsText(per) {
      var n = 1 / per;
      var shown = n >= 10 ? Math.round(n).toLocaleString() : String(Math.round(n * 10) / 10);
      return '≈ 1 in ' + shown + ' boxes';
    }

    function applyOdds() {
      var table = wrap.querySelector('.evc-table');
      seg.hidden = !table;
      if (!table) return;
      var head = table.querySelectorAll('thead th')[1];
      if (head) {
        if (!head.hasAttribute('data-avg')) head.setAttribute('data-avg', head.textContent);
        head.textContent = mode === 'odds' ? 'Odds' : head.getAttribute('data-avg');
      }
      [].slice.call(table.querySelectorAll('tbody tr')).forEach(function (tr) {
        var cell = tr.querySelector('td');
        if (!cell) return;
        if (!cell.hasAttribute('data-avg')) cell.setAttribute('data-avg', cell.textContent);
        var per = parseFloat(cell.getAttribute('data-avg'));
        if (mode === 'odds' && per > 0 && per < 1) {
          cell.textContent = oddsText(per);
        } else if (mode === 'odds' && per >= 1) {
          cell.textContent = cell.getAttribute('data-avg') + ' per box';
        } else {
          cell.textContent = cell.getAttribute('data-avg');
        }
      });
    }

    function applyCase() {
      var old = stats.querySelector('[data-evc-case]');
      if (old) old.parentNode.removeChild(old);
      var perTile = null;
      [].slice.call(stats.querySelectorAll('.evc-stat')).forEach(function (t) {
        var cap = t.querySelector('.evc-cap');
        if (cap && cap.textContent.indexOf('Per case') === 0) perTile = t;
      });
      if (!perTile || !qtyEl) return;
      var c = parseInt(perTile.querySelector('.evc-num').textContent, 10);
      var n = parseInt(qtyEl.value, 10);
      if (!(c > 1) || !(n >= c)) return;
      var full = Math.floor(n / c);
      var rem = n % c;
      var m = perTile.querySelector('.evc-cap').textContent.match(/\(([^)]+)\)/);
      var tile = doc.createElement('div');
      tile.className = 'evc-stat';
      tile.setAttribute('data-evc-case', '1');
      tile.innerHTML = '<span class="evc-num"></span><span class="evc-cap"></span>';
      tile.querySelector('.evc-num').textContent =
        full + (full === 1 ? ' case' : ' cases') + (rem ? ' + ' + rem : '');
      tile.querySelector('.evc-cap').textContent =
        'Master-case equivalent' + (m ? ' (' + m[1] + ')' : '');
      stats.appendChild(tile);
    }

    var mo = null;
    function decorate() {
      if (mo) mo.disconnect(); /* our own writes must not re-trigger us */
      applyOdds();
      applyCase();
      if (mo) mo.observe(evc, { childList: true, subtree: true });
    }

    [].slice.call(seg.querySelectorAll('.evc-seg-btn')).forEach(function (b) {
      b.addEventListener('click', function () {
        if (mode === b.dataset.mode) return;
        mode = b.dataset.mode;
        [].slice.call(seg.querySelectorAll('.evc-seg-btn')).forEach(function (x) {
          x.classList.toggle('is-on', x === b);
          x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
        });
        decorate();
      });
    });

    decorate();
    mo = new MutationObserver(decorate);
    mo.observe(evc, { childList: true, subtree: true });
  });
  })();

  /* ==== week1:Mobile cross-cutting ==== */
  (function () {
/* ---------- swipe the plinth (mobile pass) ----------
     A horizontal thumb-flick on the hero rotator advances it by clicking the
     matching dot, so pause/announce/aria behaviour stays in one place - the
     dots' own handler. Touch-only, passive; vertical scrolling stays native
     (touch-action:pan-y on the rotator is in the CSS). Works under reduced
     motion by design: the crossfade is already transition:none there. */
  ready(function () {
    if (!window.matchMedia('(any-pointer: coarse)').matches) return;
    var root = doc.querySelector('[data-hero-rotator]');
    if (!root) return;
    var dots = [].slice.call(root.querySelectorAll('.hero-dot'));
    if (dots.length < 2) return;
    var SWIPE_MIN = 40; /* px of horizontal travel that reads as intent */
    var sx = 0, sy = 0, pid = null;
    root.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch') return;
      pid = e.pointerId; sx = e.clientX; sy = e.clientY;
    }, { passive: true });
    root.addEventListener('pointerup', function (e) {
      if (pid === null || e.pointerId !== pid) return;
      pid = null;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      /* horizontal-dominant only: a page scroll drifting sideways is not a swipe */
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
      var on = 0;
      for (var i = 0; i < dots.length; i++) {
        if (dots[i].classList.contains('is-on')) { on = i; break; }
      }
      dots[(on + (dx < 0 ? 1 : -1) + dots.length) % dots.length].click();
    }, { passive: true });
    root.addEventListener('pointercancel', function () { pid = null; }, { passive: true });
  });

  /* ---------- pressed glass: iOS :active enabler ----------
     iOS only applies :active to non-interactive elements (the PDP frame is a
     div) while a touch listener is alive; the reveal-system one is once:true,
     so keep a permanent no-op for the pressed-glass CSS. */
  if (window.matchMedia('(any-pointer: coarse)').matches) {
    doc.addEventListener('touchstart', function () {}, { passive: true });
  }
  })();

  /* ==== week1:Signature #1 "The Vault Remembers" ==== */
  (function () {
/* ---------- The Vault Remembers: private on-device browsing trail ----------
     One localStorage list (key tntcg.trail.v1) written on PDP view from the
     [data-trail-product] facts the page itself emits, read by four quiet
     surfaces: home "Your shelf", PDP "Back down the trail", collection
     "Where you left off", and the search page's "Your recent hunts" chips.
     Containers ship server-rendered, EMPTY and hidden - they unhide only when
     2+ remembered items exist, so first-time visitors see nothing at all.
     Honesty rules: no stock/availability chips (view-time data goes stale) and
     the price is labeled "listed at". Storage failures (private mode, quota)
     are swallowed: no trail, no error, page unchanged. */
  (function () {
    var KEY = 'tntcg.trail.v1';
    var CAP = 8;
    function loadTrail() {
      var arr;
      try { arr = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (err) { return []; }
      if (!arr || Object.prototype.toString.call(arr) !== '[object Array]') return [];
      /* keep only well-formed same-site rows: one corrupt entry must not kill a
         rail, and hrefs are pinned path-relative ('/x', never '//host') so
         tampered storage cannot plant a javascript: or cross-origin link */
      var out = [];
      for (var i = 0; i < arr.length && out.length < CAP; i++) {
        var e = arr[i];
        if (e && typeof e.handle === 'string' && e.handle &&
            typeof e.title === 'string' && e.title &&
            typeof e.url === 'string' && e.url.charAt(0) === '/' && e.url.charAt(1) !== '/' &&
            e.url.indexOf('\\') === -1) { /* browsers fold '\' to '/': '/\host' is cross-origin */
          out.push(e);
        }
      }
      return out;
    }
    function saveTrail(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP))); } catch (err) {}
    }

    /* record: the PDP emits its own already-stated facts on a hidden element */
    ready(function () {
      var el = doc.querySelector('[data-trail-product]');
      if (!el) return;
      var handle = el.getAttribute('data-handle') || '';
      var title = el.getAttribute('data-title') || '';
      var url = el.getAttribute('data-url') || '';
      if (!handle || !title || url.charAt(0) !== '/') return;
      var list = loadTrail();
      var next = [{
        handle: handle,
        title: title,
        url: url,
        img: el.getAttribute('data-img') || '',
        price: el.getAttribute('data-price') || '',
        ts: Date.now()
      }];
      for (var i = 0; i < list.length; i++) {
        if (list[i].handle !== handle) next.push(list[i]);
      }
      saveTrail(next);
    });

    /* same lockup shape as snippets/tntcg-fant-price.liquid: SR-only plain
       price (which carries the "listed at" qualifier), aria-hidden styled
       lockup - DOM nodes only, never innerHTML */
    function priceNode(money) {
      var parts = String(money).split('.');
      var wrap = doc.createElement('span');
      wrap.className = 'pr';
      var plk = doc.createElement('span');
      plk.className = 'plk';
      var sr = doc.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = 'listed at $' + money;
      var vis = doc.createElement('span');
      vis.setAttribute('aria-hidden', 'true');
      var cur = doc.createElement('span');
      cur.className = 'cur';
      cur.textContent = '$';
      vis.appendChild(cur);
      vis.appendChild(doc.createTextNode(parts[0]));
      if (parts[1] && parts[1] !== '00') {
        var sup = doc.createElement('sup');
        sup.className = 'cents';
        sup.textContent = parts[1];
        vis.appendChild(sup);
      }
      plk.appendChild(sr);
      plk.appendChild(vis);
      wrap.appendChild(plk);
      return wrap;
    }

    /* one remembered card: the .relic vitrine minus any stock chip - the
       honesty rule; availability at view-time may have gone stale */
    function buildCard(e) {
      var a = doc.createElement('a');
      a.className = 'relic';
      a.href = e.url;
      var ph = doc.createElement('div');
      ph.className = 'ph' + (e.img ? ' lit' : '');
      if (e.img) {
        var img = doc.createElement('img');
        img.src = e.img;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        /* nominal box only - the .ph aspect-ratio + object-fit own layout */
        img.width = 360;
        img.height = 360;
        ph.appendChild(img);
      }
      var h = doc.createElement('h3');
      h.className = 'relic-title';
      h.title = e.title;
      h.textContent = e.title;
      var row = doc.createElement('div');
      row.className = 'price-row';
      if (e.price) {
        var at = doc.createElement('span');
        at.className = 'trail-at';
        at.setAttribute('aria-hidden', 'true'); /* SR text already says "listed at" */
        at.textContent = 'Listed at';
        row.appendChild(at);
        row.appendChild(priceNode(e.price));
      }
      a.appendChild(ph);
      a.appendChild(h);
      a.appendChild(row);
      return a;
    }

    /* fill the surfaces; each unhides itself only past the 2-item bar.
       Runs after the record ready() above, so the PDP's own just-recorded
       entry is in the list and data-trail-exclude filters it back out. */
    ready(function () {
      var rails = doc.querySelectorAll('[data-trail-rail]');
      var chips = doc.querySelector('[data-trail-chips]');
      if (!rails.length && !chips) return;
      var list = loadTrail();
      if (list.length < 2) return; /* nothing remembered = nothing rendered */
      for (var i = 0; i < rails.length; i++) {
        var rail = rails[i];
        var slot = rail.querySelector('[data-trail-cards]');
        if (!slot || slot.firstChild) continue;
        var skip = rail.getAttribute('data-trail-exclude') || '';
        var n = 0;
        for (var j = 0; j < list.length; j++) {
          if (list[j].handle === skip) continue;
          slot.appendChild(buildCard(list[j]));
          n++;
        }
        /* a shelf holding one box reads as surveillance, not memory */
        if (n >= 2) { rail.hidden = false; }
        else { slot.textContent = ''; }
      }
      if (chips) {
        for (var k = 0; k < list.length; k++) {
          var c = doc.createElement('a');
          c.className = 'chip sm';
          c.href = list[k].url;
          c.textContent = list[k].title;
          chips.appendChild(c);
        }
        chips.hidden = false; /* list.length >= 2 guaranteed above */
      }
    });
  })();
  })();

  /* ==== week1:Signature #2: The Haul Drawer ==== */
  (function () {
/* ==== signature2:The Haul Drawer ==== */
(function () {
/* ---------- the haul drawer: add-to-cart's landing ceremony ----------
     Markup ships hidden+inert in snippets/tntcg-fant-haul.liquid (rendered from
     theme.liquid). This layer intercepts the two EXISTING add flows - the PDP
     product form and the quick-add tiles - at the fetch seam: any successful
     POST to /cart/add.js triggers a /cart.js read, the rows are built from that
     JSON, and the drawer opens. Neither success handler above is touched; their
     toast still fires and is retired the moment the drawer lands. If this
     markup is absent or the /cart.js read fails, the toast flow stands exactly
     as before (the reduced-chrome fallback). Never opens on /cart itself - that
     page is the drawer's full-size sibling. Reduced motion: the drawer appears
     with no slide and no settle (CSS-gated); it is a function, not decoration. */
  ready(function () {
    var root = doc.getElementById('tntcg-haul');
    if (!root || !window.fetch) return;
    if (location.pathname === '/cart') return;

    var panel = root.querySelector('.haul-panel');
    var latest = root.querySelector('[data-haul-latest]');
    var img = root.querySelector('[data-haul-img]');
    var nameEl = root.querySelector('[data-haul-name]');
    var metaEl = root.querySelector('[data-haul-meta]');
    var list = root.querySelector('[data-haul-list]');
    var subEl = root.querySelector('[data-haul-subtotal]');
    var countEl = root.querySelector('[data-haul-count]');
    var pluralEl = root.querySelector('[data-haul-plural]');
    if (!panel || !latest || !list || !subEl) return;

    var isOpen = false;
    var lastFocus = null;
    var lastTrigger = null;

    /* capture phase: the PDP flow disables the add button BEFORE its fetch
       fires (setBusy), which drops focus to <body> - so remember the control
       that started the ceremony while it is still live, and hand focus back
       to it on close */
    doc.addEventListener('click', function (e) {
      var b = e.target.closest('button[name="add"], [data-proxy-add], button[data-qadd]');
      if (b) lastTrigger = b;
    }, true);

    function money(cents) {
      var s = (cents / 100).toFixed(2);
      return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    /* same title trim the cart page performs server-side: split ' (' first */
    function trimTitle(t) { return String(t || '').split(' (')[0]; }
    function thumb(url, w) { return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }

    function render(cart, addedKey) {
      var items = cart.items || [];
      if (!items.length) return false;
      var star = null;
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === addedKey) { star = items[i]; break; }
      }
      if (!star) star = items[0]; /* /cart.js lists the newest line first */
      if (star.image) { img.src = thumb(star.image, 240); img.hidden = false; }
      else { img.removeAttribute('src'); img.hidden = true; }
      nameEl.textContent = trimTitle(star.product_title);
      metaEl.textContent = 'Qty ' + star.quantity + ' · ' + money(star.final_line_price);
      latest.hidden = false;
      list.innerHTML = '';
      var rest = 0;
      for (var j = 0; j < items.length; j++) {
        var it = items[j];
        if (it === star) continue; /* the arrival is on the plate, not listed twice */
        rest++;
        var li = doc.createElement('li');
        var im = doc.createElement('img');
        im.alt = '';
        im.width = 44;
        im.height = 44;
        if (it.image) im.src = thumb(it.image, 120);
        var t = doc.createElement('span');
        t.className = 't';
        var b = doc.createElement('b');
        b.textContent = trimTitle(it.product_title);
        var small = doc.createElement('small');
        small.textContent = 'Qty ' + it.quantity;
        t.appendChild(b);
        t.appendChild(small);
        var pr = doc.createElement('span');
        pr.className = 'pr';
        pr.textContent = money(it.final_line_price);
        li.appendChild(im);
        li.appendChild(t);
        li.appendChild(pr);
        list.appendChild(li);
      }
      list.hidden = rest === 0;
      subEl.textContent = money(cart.items_subtotal_price);
      if (countEl) countEl.textContent = String(cart.item_count);
      if (pluralEl) pluralEl.textContent = cart.item_count === 1 ? '' : 's';
      return true;
    }

    function open() {
      lastFocus = doc.activeElement;
      root.removeAttribute('hidden');
      root.removeAttribute('inert');
      void root.offsetWidth; /* restart the slide + settle on repeat opens */
      root.classList.add('open');
      isOpen = true;
      doc.body.style.overflow = 'hidden';
      /* the untouched success handler fired its toast before /cart.js came
         back; the drawer IS the confirmation now, so retire it early (same
         inert discipline the toast applies to itself on timeout) */
      var t = doc.querySelector('.tntcg-toast');
      if (t) { t.classList.remove('show'); t.setAttribute('inert', ''); }
      setTimeout(function () { panel.focus(); }, 40);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('open');
      root.setAttribute('hidden', '');
      root.setAttribute('inert', '');
      /* shared, unreference-counted lock: only release it if the seek overlay
         is not still holding the page open behind us */
      if (!doc.querySelector('.seek-overlay.open')) doc.body.style.overflow = '';
      if (lastTrigger && doc.contains(lastTrigger) && !lastTrigger.disabled) lastTrigger.focus();
      else if (lastFocus && doc.contains(lastFocus) && lastFocus !== doc.body && lastFocus.focus) lastFocus.focus();
      else {
        /* trigger still inside its own disabled window: park focus on the
           buy area rather than letting it fall to <body> */
        var host = (lastTrigger && lastTrigger.closest && lastTrigger.closest('form, .pdp-buy')) ||
                   doc.querySelector('main');
        if (host) { host.setAttribute('tabindex', '-1'); try { host.focus(); } catch (err) {} }
      }
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-haul-close]')) { e.preventDefault(); close(); }
    });
    doc.addEventListener('keydown', function (e) {
      /* a modal dialog owns the top layer and its own Escape; only take the
         key once nothing sits above the drawer */
      if (e.key === 'Escape' && isOpen && !doc.querySelector('dialog[open]')) close();
    });
    /* keep Tab inside the dialog while open - the seek overlay's visible-
       elements-only trap, plus the panel itself (tabindex=-1, holds initial
       focus) anchoring shift-Tab */
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen) return;
      var focusables = Array.prototype.filter.call(
        root.querySelectorAll('button, a[href]'),
        function (el) { return el.getClientRects().length > 0; }
      );
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && (doc.activeElement === first || doc.activeElement === panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* the fetch seam: observe, never alter. The original promise is returned
       untouched so the existing success/error choreography runs verbatim;
       the drawer's own /cart.js read goes through origFetch so it can never
       re-enter this wrapper. */
    var origFetch = window.fetch;
    var cartSeq = 0; /* out-of-order /cart.js reads must not repaint the drawer */
    window.fetch = function (input) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (err) {}
      var p = origFetch.apply(window, arguments);
      if (url.indexOf('/cart/add') > -1) {
        p.then(function (res) {
          if (!res || !res.ok) return; /* failed adds keep their error toast */
          /* clone: the original handler's own res.json() must stay readable */
          res.clone().json().then(function (added) {
            /* form posts return the bare line item; items-array posts wrap it */
            var key = added && added.key;
            if (!key && added && added.items && added.items[0]) key = added.items[0].key;
            var seq = ++cartSeq;
            origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (cart) {
                if (seq !== cartSeq) return; /* a later add already owns the drawer */
                if (!cart || !render(cart, key)) return;
                /* never open underneath an open viewer: it would be inert
                   behind the modal backdrop */
                var top = doc.querySelector('dialog[open]');
                if (top && top.close) { try { top.close(); } catch (err) {} }
                open();
              })
              .catch(function () {}); /* drawer is enhancement; the toast already confirmed */
          }).catch(function () {});
        }).catch(function () {});
      }
      return p;
    };
  });
})();
  })();

  /* ==== week1:"The Ledger" FAQ ==== */
  (function () {
/* ==== The Ledger: FAQ jump chips ==== */
  (function () {
    /* A closed <details> swallows an anchor jump (browsers only auto-reveal
       details ANCESTORS of a fragment target, not a details that is itself
       the target), so open the entry before the native scroll lands. Pure
       nicety: without JS the chips still scroll to the entry, which opens on
       tap - native disclosure stays the baseline. */
    ready(function () {
      if (!doc.querySelector('.faq-ledger')) return;
      function openEntry(id) {
        var t = id ? doc.getElementById(id) : null;
        if (t && t.tagName === 'DETAILS') t.open = true;
      }
      var chips = doc.querySelectorAll('.faq-chips a[href^="#faq-"]');
      for (var i = 0; i < chips.length; i++) {
        chips[i].addEventListener('click', function () {
          openEntry(this.getAttribute('href').slice(1));
        });
      }
      /* a shared or bookmarked #faq-N link lands open too */
      if (location.hash.indexOf('#faq-') === 0) openEntry(location.hash.slice(1));
    });
  })();
  })();

  /* ==== week1:The Misprint 404 ==== */
  (function () {
/* ---------- the misprint: 404 error card joins the holo tilt rig ----------
   The card is pure typography (no img inside), so the main engine's
   img requirement skips it - same stage-as-target binder the hero plinth
   uses (CSS transforms .mis-card.tilt itself, will-change on the stage). */
ready(function () {
  if (reduceMotion || !finePointer || !wideScreen) return;
  var stage = doc.querySelector('.mis-card');
  if (!stage) return;
  stage.classList.add('tilt');
  var glare = doc.createElement('span');
  glare.className = 'glare';
  glare.setAttribute('aria-hidden', 'true');
  stage.appendChild(glare);
  var maxDeg = 7; /* hand-size card - it can swing wider than a product plate */
  var cur = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
  var tgt = { rx: 0, ry: 0, gl: 0, gx: 50, gy: 42, tz: 1 };
  var active = false;
  function loop() {
    var k = 0.14;
    var done = true;
    for (var p in cur) {
      cur[p] += (tgt[p] - cur[p]) * k;
      if (Math.abs(tgt[p] - cur[p]) > 0.002) done = false;
    }
    stage.style.setProperty('--rx', cur.rx.toFixed(2) + 'deg');
    stage.style.setProperty('--ry', cur.ry.toFixed(2) + 'deg');
    stage.style.setProperty('--gl', cur.gl.toFixed(3));
    stage.style.setProperty('--gx', cur.gx.toFixed(1) + '%');
    stage.style.setProperty('--gy', cur.gy.toFixed(1) + '%');
    stage.style.setProperty('--tz', cur.tz.toFixed(3));
    if (done && !active) { removeTick(loop); stage.style.willChange = ''; }
  }
  stage.addEventListener('pointerenter', function () {
    active = true;
    stage.style.willChange = 'transform'; /* promote only while interacting */
    tgt.gl = 0.55; tgt.tz = 1.03;
    addTick(loop);
  });
  stage.addEventListener('pointermove', function (e) {
    var r = stage.getBoundingClientRect();
    var px = (e.clientX - r.left) / r.width;
    var py = (e.clientY - r.top) / r.height;
    tgt.ry = (px - 0.5) * 2 * maxDeg;
    tgt.rx = (0.5 - py) * 2 * maxDeg;
    tgt.gx = px * 100;
    tgt.gy = py * 100;
  });
  stage.addEventListener('pointerleave', function () {
    active = false;
    tgt.rx = 0; tgt.ry = 0; tgt.gl = 0; tgt.gx = 50; tgt.gy = 42; tgt.tz = 1;
  });
});
  })();

  /* ==== week1:Signature #3 mobile half — Foil Pass (scroll-driven foil sheen on touch devices) ==== */
  (function () {
/* ---------- Foil Pass: the mobile holo (Signature #3, mobile half) ----------
     Desktop keeps the pointer tilt; phones get one specular sweep across each
     lit plate as it crosses the viewport - the foil-in-hand glint without a
     deviceorientation permission dialog. IO stamps .foil-run and the CSS runs
     a single compositor-only keyframe (a transform on an appended strip, the
     same grammar as the .btn-quest sheen). Each plate sweeps once per pageview
     (unobserved the moment it fires); at most the TWO most recently entered
     plates run at once - older sweeps are cancelled by dropping the class,
     which is invisible because the strip rests off-plate at both ends.
     Skipped wherever the hover-tilt rig runs (pointer-fine + wide screen), so
     the two glare grammars never share a plate. Reduced motion: nothing. */
  ready(function () {
    if (reduceMotion) return;
    if (!window.matchMedia('(any-pointer: coarse)').matches) return;
    /* mirror of the tilt gate at the top of this file: a touch-laptop that
       qualifies for hover tilt keeps that grammar exclusively */
    if (window.matchMedia('(pointer: fine)').matches &&
        window.matchMedia('(min-width: 861px)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    var stages = doc.querySelectorAll('.relic .ph.lit, .pdp-frame .card-frame');
    if (!stages.length) return;
    var live = []; /* stages mid-sweep, oldest first */
    /* idempotent: safe if the cap and animationend both land on a stage */
    function settle(stage) {
      stage.classList.remove('foil-run');
      var strip = stage.querySelector('.foil');
      if (strip && strip.parentNode) strip.parentNode.removeChild(strip);
      var i = live.indexOf(stage);
      if (i > -1) live.splice(i, 1);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var stage = entry.target;
        io.unobserve(stage); /* once per element per pageview */
        var strip = doc.createElement('span');
        strip.className = 'foil';
        strip.setAttribute('aria-hidden', 'true');
        strip.addEventListener('animationend', function () { settle(stage); });
        stage.appendChild(strip);
        stage.classList.add('foil-run');
        live.push(stage);
        while (live.length > 2) settle(live[0]); /* newest two only */
      });
    }, { threshold: 0.4 });
    stages.forEach(function (stage) { io.observe(stage); });
  });
  })();

  /* ==== week1:The Vault Index ==== */
  (function () {
/* ==== week2:The Vault Index ==== */
  (function () {
  /* ---------- seek overlay: the Vault Index (instant, typo-tolerant) ----------
     With ~117 products the whole catalog fits in one small JSON payload, so the
     overlay stops asking the network per keystroke: on first open it fetches
     /products.json once (endpoint named by data-vault-index in chrome-top),
     caches it in sessionStorage for 30 minutes, and answers from memory -
     results from the FIRST character, 'kayu' finds Kayou, 'prismatc' finds
     Prismatic. Rows reuse the .seek-item contract, so the existing arrow-key /
     Enter walker and the .sel wash apply unchanged, and the focus trap picks
     the rows up as ordinary anchors.
     Coexistence is by exclusion, not by racing: a capture-phase input listener
     on the overlay stops propagation before the legacy 220ms suggest.json
     handler can schedule a fetch, so exactly one path ever renders. If the
     index fetch fails, the intercept stands down for good and replays the
     missed keystrokes into the legacy path - silent fallback, no seam. */
  ready(function () {
    if (!window.fetch) return; /* ancient browsers keep the legacy path + native form */
    var overlay = doc.querySelector('.seek-overlay');
    if (!overlay) return;
    var src = overlay.getAttribute('data-vault-index');
    if (!src) return; /* markup opted out: legacy overlay behavior only */
    var input = overlay.querySelector('input[type="search"]');
    var results = overlay.querySelector('.seek-results');
    var hint = overlay.querySelector('.seek-hint');
    var opener = doc.querySelector('[data-seek-open]');
    if (!input || !results) return;

    var KEY = 'tntcg-vindex';
    var TTL = 5 * 60 * 1000; /* rows assert stock as fact - keep the window tight */
    var revalidated = false;
    var idx = null;      /* built index records, or null until loaded */
    var loading = false;
    var failed = false;  /* once true, the intercept stands down permanently */
    var pending = false; /* the user typed while the fetch was in flight */

    /* Dropped-letter typos only ('kayu' -> 'kayou', 'prismatc' -> 'prismatic'),
       anchored on the first letter and gated to 3+ chars so short queries
       cannot subsequence-match half the vault. */
    function subseq(w, word) {
      if (w.length < 3 || w.charCodeAt(0) !== word.charCodeAt(0)) return false;
      var i = 0;
      for (var j = 0; j < word.length && i < w.length; j++) {
        if (w.charCodeAt(i) === word.charCodeAt(j)) i++;
      }
      return i === w.length;
    }

    function wordScore(w, r) {
      var k;
      if (r.tl.indexOf(w) === 0) return 60;                                 /* title starts with */
      for (k = 0; k < r.tw.length; k++) if (r.tw[k].indexOf(w) === 0) return 40; /* title word prefix */
      if (r.tl.indexOf(w) > -1) return 26;                                  /* title substring */
      for (k = 0; k < r.tw.length; k++) if (subseq(w, r.tw[k])) return 12;  /* title typo */
      for (k = 0; k < r.gw.length; k++) if (r.gw[k].indexOf(w) === 0) return 14; /* vendor/tag/type prefix */
      if (r.gl.indexOf(w) > -1) return 8;                                   /* vendor/tag/type substring */
      for (k = 0; k < r.gw.length; k++) if (subseq(w, r.gw[k])) return 5;   /* vendor/tag/type typo */
      return 0;
    }

    function query(q) {
      var words = q.toLowerCase().split(/\s+/).filter(Boolean);
      if (!words.length) return [];
      var scored = [];
      for (var i = 0; i < idx.length; i++) {
        var r = idx[i], total = 0, miss = false;
        for (var wi = 0; wi < words.length; wi++) {
          var s = wordScore(words[wi], r);
          if (!s) { miss = true; break; } /* every word must land somewhere */
          total += s;
        }
        if (miss) continue;
        if (r.a === true) total += 6; /* the case up front: in stock ranks first */
        scored.push({ r: r, s: total });
      }
      scored.sort(function (x, y) {
        if (y.s !== x.s) return y.s - x.s;
        return x.r.tl < y.r.tl ? -1 : x.r.tl > y.r.tl ? 1 : 0;
      });
      var out = [];
      for (var n = 0; n < scored.length && n < 8; n++) out.push(scored[n].r);
      return out;
    }

    /* same canonical route the legacy overlay uses: prefix=last keeps Enter
       from landing on "0 results" for a term the index just matched */
    function searchUrl(q) {
      return '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last';
    }

    function money(n) {
      return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /* /products.json -> compact cacheable items. Availability is tri-state:
       true / false only when the payload actually says so; if the field is
       absent we store null and the row claims nothing (no dot, no promise). */
    function condense(products) {
      var items = [];
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        if (!p || !p.handle || !p.title) continue;
        var vars = p.variants || [];
        var avail = null, min = null, spoke = false;
        for (var j = 0; j < vars.length; j++) {
          if (typeof vars[j].available === 'boolean') {
            spoke = true;
            if (vars[j].available) avail = true;
          }
          var pr = parseFloat(vars[j].price);
          if (!isNaN(pr) && (min === null || pr < min)) min = pr;
        }
        if (spoke && avail === null) avail = false; /* every variant said no */
        var ven = p.vendor || '';
        if (ven.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === 'topnotchtcg') ven = ''; /* the house name is chrome, not a brand row */
        var tags = p.tags || [];
        if (typeof tags === 'string') tags = tags.split(',');
        items.push({
          t: p.title,
          u: '/products/' + p.handle,
          v: ven,
          p: min === null ? '' : money(min.toFixed(2)),
          a: avail,
          i: (p.images && p.images[0] && p.images[0].src) ? p.images[0].src : '',
          g: ((p.product_type || '') + ' ' + ven + ' ' + tags.join(' ')).replace(/\s+/g, ' ')
        });
      }
      return items;
    }

    function buildRecords(items) {
      var recs = [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || !it.t || !it.u) continue;
        var tl = String(it.t).toLowerCase();
        var gl = String(it.g || '').toLowerCase();
        recs.push({
          t: it.t, u: it.u, v: it.v || '', p: it.p || '', i: it.i || '',
          a: (it.a === true || it.a === false) ? it.a : null,
          tl: tl, tw: tl.split(/[^a-z0-9]+/).filter(Boolean),
          gl: gl, gw: gl.split(/[^a-z0-9]+/).filter(Boolean)
        });
      }
      return recs;
    }

    function fromCache() {
      try {
        var raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        var box = JSON.parse(raw);
        if (!box || !box.ts || !box.items || !box.items.length) return null;
        if ((Date.now() - box.ts) > TTL) return null;
        return box.items;
      } catch (err) { return null; }
    }

    function toCache(items) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ ts: Date.now(), items: items }));
      } catch (err) { /* private mode / quota: the index still lives for this page */ }
    }

    function renderRows(rows, q) {
      results.innerHTML = '';
      if (hint) hint.style.display = 'none';
      if (!rows.length) {
        var d = doc.createElement('div');
        d.className = 'seek-empty';
        d.textContent = 'No matches for "' + q + '" - try another word.';
        results.appendChild(d);
      }
      rows.forEach(function (r) {
        var a = doc.createElement('a');
        a.className = 'seek-item';
        a.href = r.u;
        var img = doc.createElement('img');
        img.alt = '';
        img.width = 46; img.height = 46;
        if (r.i) img.src = r.i + (r.i.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        var t = doc.createElement('span');
        t.className = 't';
        var b = doc.createElement('b');
        b.textContent = r.t;
        var small = doc.createElement('small');
        if (r.a !== null) {
          var dot = doc.createElement('i');
          dot.className = 'vx-dot' + (r.a ? ' in' : '');
          small.appendChild(dot);
        }
        var meta = r.a === null ? '' : (r.a ? 'In stock - ships in 24h' : 'Sold out - notify me');
        var line = r.v ? (meta ? r.v + ' · ' + meta : r.v) : meta;
        small.appendChild(doc.createTextNode(line));
        t.appendChild(b); t.appendChild(small);
        var pr = doc.createElement('span');
        pr.className = 'pr';
        pr.textContent = r.p;
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.appendChild(img); a.appendChild(t); a.appendChild(pr);
        results.appendChild(a);
      });
      /* the server search reads body copy the index does not carry, so the
         all-results row stays even on an index miss */
      var all = doc.createElement('a');
      all.className = 'seek-all';
      all.href = searchUrl(q);
      all.textContent = 'All results for "' + q + '"';
      results.appendChild(all);
    }

    /* one short sentence in a polite region, instead of the entire result list
       being re-announced on every keystroke */
    var statusEl = doc.querySelector('[data-seek-status]');
    function announce(n, q) {
      input.setAttribute('aria-expanded', n > 0 ? 'true' : 'false');
      if (!statusEl) return;
      statusEl.textContent = !q ? '' :
        (n === 0 ? 'No matches for ' + q : n + (n === 1 ? ' result' : ' results') + ' for ' + q);
    }

    function repaint() {
      var q = input.value.trim();
      if (!q) {
        results.innerHTML = '';
        if (hint) hint.style.display = '';
        input.removeAttribute('aria-activedescendant');
        announce(0, '');
        return;
      }
      var rows = query(q);
      renderRows(rows, q);
      input.removeAttribute('aria-activedescendant');
      announce(rows.length, q);
    }

    /* stale-while-revalidate: the cache buys an instant first paint, but a
       stock dot or price that moved since must not keep being asserted, so
       every cache hit is checked against the live endpoint once per page and
       any open result list repaints with the truth. */
    function revalidate() {
      if (revalidated) return;
      revalidated = true;
      fetch(src)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          var items = condense(data.products || []);
          if (!items.length) return;
          toCache(items);
          idx = buildRecords(items);
          if (input.value.trim()) repaint();
        })
        .catch(function () {}); /* the cached index stays usable */
    }

    function load() {
      if (idx || loading || failed) return;
      var cached = fromCache();
      if (cached) {
        idx = buildRecords(cached);
        if (pending) { pending = false; repaint(); }
        revalidate();
        return;
      }
      loading = true;
      fetch(src)
        .then(function (r) { if (!r.ok) throw new Error('index ' + r.status); return r.json(); })
        .then(function (data) {
          loading = false;
          var items = condense((data || {}).products || []);
          if (!items.length) throw new Error('empty index');
          toCache(items);
          idx = buildRecords(items);
          if (pending) { pending = false; repaint(); }
        })
        .catch(function () {
          /* silent fallback: stand down for good and replay the keystrokes
             the legacy suggest.json handler never saw */
          loading = false;
          failed = true;
          if (pending) {
            pending = false;
            if (input.value.trim()) {
              try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch (err) {}
            }
          }
        });
    }

    /* Capture phase beats the legacy listener on the input element itself, so
       stopping propagation here keeps the 220ms suggest path from ever
       scheduling a fetch that could later overwrite index rows. While the
       index is still in flight we hold the keystroke (pending) instead of
       letting both paths run - exactly one renderer owns the pane at any time. */
    overlay.addEventListener('input', function (e) {
      if (e.target !== input) return;
      if (failed) return; /* legacy path owns the overlay again */
      e.stopPropagation();
      if (!idx) { pending = true; load(); return; }
      repaint();
    }, true);

    /* Warm the index before the first keystroke: hover intent on the header
       icon, the icon click itself (the / key routes through opener.click()),
       and input focus as the belt-and-suspenders catch-all. */
    if (opener) {
      opener.addEventListener('pointerenter', function () { load(); });
      opener.addEventListener('click', function () { load(); });
    }
    input.addEventListener('focus', function () { load(); });
  });
  })();
  })();

  /* ==== week1:Signature #5 "Lifted from the Case" + "Inspect the Seal" (PDP media pass) ==== */
  (function () {
/* ---------- Lifted from the Case (Signature #5): cross-page morph ----------
   Clicking a relic card stamps its box art with a view-transition-name derived
   from the product handle, one beat before navigation; the PDP plate img
   carries the same name statically (sections/tntcg-fant-product.liquid). The
   cross-document @view-transition rig in tntcg-fantasy.css then pairs the two
   and the art lifts out of the wall onto the plinth. Names are per-handle so
   a stale stamp from an aborted navigation can never pair with the wrong
   plate, and only the clicked card is ever named - one element per name per
   page (48 cards never all get names). Chromium-era progressive enhancement;
   every other browser keeps today's crossfade. Same fence as the rig:
   desktop + no-preference only (the 2026-07-10 burger-freeze trap lives
   below 861px and stays fenced off). */
ready(function () {
  if (reduceMotion || !wideScreen) return;
  var last = null; /* the previously stamped art, cleared on the next click */
  doc.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var card = e.target.closest ? e.target.closest('a.relic') : null;
    if (!card) return;
    var handle = card.getAttribute('data-handle') || '';
    if (!handle) { /* JS-built rail cards may lack the attribute: fall back to the href */
      var m = (card.getAttribute('href') || '').match(/\/products\/([\w-]+)/);
      if (m) handle = m[1];
    }
    if (!/^[A-Za-z0-9-]+$/.test(handle)) return; /* the name must stay a valid CSS ident */
    var art = card.querySelector('.ph img');
    if (!art) return;
    /* one element per name per page: a stamp from an aborted or new-tab
       navigation must never linger alongside a fresh one */
    if (last && last !== art) {
      last.style.removeProperty('view-transition-name');
      last.style.removeProperty('view-transition-class');
    }
    /* vault- prefix keeps digit-leading handles a valid custom-ident */
    art.style.setProperty('view-transition-name', 'vault-' + handle);
    art.style.setProperty('view-transition-class', 'vault-lift');
    last = art;
  });
});

/* ---------- Inspect the Seal: full-screen zoom on the 2000px rendition ----------
   Native <dialog>.showModal() supplies the focus trap and Escape for free
   (base.css and its dialog styling never load on fant templates). Pan and
   zoom are transform-only through the single-rAF ticker; reduced motion
   applies positions instantly instead of gliding. The trigger chip is
   motion-ok-gated CSS, so no-JS pages never show it. */
ready(function () {
  var dlg = doc.querySelector('.seal-scope');
  var frame = doc.querySelector('.pdp-frame .card-frame');
  var trigger = doc.querySelector('.pdp-inspect');
  if (!dlg || !frame) return;
  if (typeof dlg.showModal !== 'function') {
    /* pre-<dialog> engine: hide the affordance, keep the page as it was */
    if (trigger) trigger.hidden = true;
    return;
  }
  var stage = dlg.querySelector('.seal-scope-stage');
  var img = dlg.querySelector('.seal-scope-img');
  var closeBtn = dlg.querySelector('.seal-scope-close');
  if (!stage || !img || !closeBtn) return;

  var cur = { s: 1, x: 0, y: 0 };
  var tgt = { s: 1, x: 0, y: 0 };
  var maxZ = 4;
  var pts = []; /* live pointers: one = drag-pan, two = pinch */

  function paint() {
    img.style.transform = 'translate(-50%,-50%) translate(' + cur.x.toFixed(1) + 'px,' + cur.y.toFixed(1) + 'px) scale(' + cur.s.toFixed(4) + ')';
  }
  function loop() {
    var k = reduceMotion ? 1 : 0.22; /* reduced motion: land instantly, no glide */
    var done = true;
    var props = ['s', 'x', 'y'];
    for (var i = 0; i < props.length; i++) {
      var p = props[i];
      cur[p] += (tgt[p] - cur[p]) * k;
      if (Math.abs(tgt[p] - cur[p]) > (p === 's' ? 0.001 : 0.3)) done = false;
      else cur[p] = tgt[p];
    }
    paint();
    if (done) removeTick(loop);
  }
  function clamp() {
    if (tgt.s < 1) tgt.s = 1;
    if (tgt.s > maxZ) tgt.s = maxZ;
    var mx = Math.max(0, (img.offsetWidth * tgt.s - stage.clientWidth) / 2);
    var my = Math.max(0, (img.offsetHeight * tgt.s - stage.clientHeight) / 2);
    if (tgt.x > mx) tgt.x = mx; else if (tgt.x < -mx) tgt.x = -mx;
    if (tgt.y > my) tgt.y = my; else if (tgt.y < -my) tgt.y = -my;
  }
  function zoomAt(vx, vy, f) {
    var r = stage.getBoundingClientRect();
    var cx = vx - r.left - r.width / 2;
    var cy = vy - r.top - r.height / 2;
    var s0 = tgt.s;
    var s1 = Math.min(maxZ, Math.max(1, s0 * f));
    if (s1 === s0) return;
    /* keep the pixel under the cursor / pinch midpoint fixed while scaling */
    tgt.s = s1;
    tgt.x = cx - (cx - tgt.x) * (s1 / s0);
    tgt.y = cy - (cy - tgt.y) * (s1 / s0);
    clamp();
    addTick(loop);
  }
  function setMax() { /* zoom ceiling: native resolution, floor 2x, cap 6x */
    maxZ = Math.min(6, Math.max(2, (img.naturalWidth || 2000) / Math.max(1, img.offsetWidth)));
  }
  function openScope() {
    /* inspect whichever photo is on the plate right now (thumb rail keeps .on) */
    var on = doc.querySelector('.pdp-thumbs button.on');
    var zoomSrc = on && on.getAttribute('data-zoom');
    if (zoomSrc && img.getAttribute('src') !== zoomSrc) {
      img.src = zoomSrc;
      if (on.getAttribute('data-zw')) {
        img.setAttribute('width', on.getAttribute('data-zw'));
        img.setAttribute('height', on.getAttribute('data-zh'));
      }
    }
    cur = { s: 1, x: 0, y: 0 };
    tgt = { s: 1, x: 0, y: 0 };
    pts = [];
    img.style.transform = 'translate(-50%,-50%)';
    try { dlg.showModal(); } catch (err) { return; }
    doc.documentElement.classList.add('seal-lock');
    if (img.complete) setMax();
    else img.addEventListener('load', setMax, { once: true });
  }

  if (trigger) trigger.addEventListener('click', function () { openScope(); });
  frame.addEventListener('click', function (e) {
    /* the whole plate opens the viewer; the chip already wires itself */
    if (e.target.closest && e.target.closest('.pdp-inspect')) return;
    openScope();
  });
  frame.classList.add('scope-ok'); /* zoom-in cursor, JS-alive pages only */
  closeBtn.addEventListener('click', function () { dlg.close(); });
  dlg.addEventListener('close', function () { /* Escape lands here too (cancel -> close) */
    pts = [];
    removeTick(loop);
    doc.documentElement.classList.remove('seal-lock');
    if (trigger && trigger.focus) trigger.focus();
  });

  /* keyboard equivalents for every pointer gesture: the feature exists FOR
     magnification, so it cannot be pointer-only. +/- zoom about the centre,
     arrows pan once there is something to pan, 0 returns to fit. */
  dlg.addEventListener('keydown', function (e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    var r = stage.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var step = Math.max(40, Math.min(r.width, r.height) * 0.15);
    var k = e.key;
    if (k === '+' || k === '=') { e.preventDefault(); zoomAt(cx, cy, 1.3); }
    else if (k === '-' || k === '_') { e.preventDefault(); zoomAt(cx, cy, 1 / 1.3); }
    else if (k === '0') { e.preventDefault(); tgt.s = 1; tgt.x = 0; tgt.y = 0; addTick(loop); }
    else if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
      if (tgt.s <= 1.01) return; /* nothing to pan at fit-to-frame: let the key through */
      e.preventDefault();
      if (k === 'ArrowLeft') tgt.x += step;
      else if (k === 'ArrowRight') tgt.x -= step;
      else if (k === 'ArrowUp') tgt.y += step;
      else tgt.y -= step;
      clamp();
      addTick(loop);
    }
  });

  stage.addEventListener('wheel', function (e) {
    e.preventDefault();
    var f = e.deltaMode === 1 ? Math.pow(1.06, -e.deltaY) : Math.pow(1.0018, -e.deltaY);
    zoomAt(e.clientX, e.clientY, f);
  }, { passive: false });

  stage.addEventListener('dblclick', function (e) {
    e.preventDefault();
    if (tgt.s > 1.01) { tgt.s = 1; tgt.x = 0; tgt.y = 0; addTick(loop); }
    else zoomAt(e.clientX, e.clientY, 2.6);
  });

  function findPt(id) {
    for (var i = 0; i < pts.length; i++) if (pts[i].id === id) return pts[i];
    return null;
  }
  function dropPt(id) {
    for (var i = 0; i < pts.length; i++) if (pts[i].id === id) { pts.splice(i, 1); return; }
  }
  stage.addEventListener('pointerdown', function (e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (err) {} }
    if (!findPt(e.pointerId)) pts.push({ id: e.pointerId, x: e.clientX, y: e.clientY });
    e.preventDefault();
  });
  stage.addEventListener('pointermove', function (e) {
    var p = findPt(e.pointerId);
    if (!p) return;
    if (pts.length === 1) {
      tgt.x += e.clientX - p.x;
      tgt.y += e.clientY - p.y;
      clamp();
      addTick(loop);
    } else if (pts.length === 2) {
      var q = pts[0].id === p.id ? pts[1] : pts[0];
      var dx0 = p.x - q.x, dy0 = p.y - q.y;
      var dx1 = e.clientX - q.x, dy1 = e.clientY - q.y;
      var d0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
      var d1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      /* ignore degenerate pinches: two pointers nearly on top of each other */
      if (d0 > 8) zoomAt((e.clientX + q.x) / 2, (e.clientY + q.y) / 2, d1 / d0);
    }
    p.x = e.clientX; p.y = e.clientY;
  });
  var lift = function (e) { dropPt(e.pointerId); };
  stage.addEventListener('pointerup', lift);
  stage.addEventListener('pointercancel', lift);
});
  })();

  /* ==== prismatic:home-hero ==== */
  (function () {
/* ---------- prismatic hero halo sync ----------
     The blurred glow twins live OUTSIDE the card frame (inside it they would
     wash the ivory plate), so the rotator's .hero-slide class flips can't
     reach them through CSS sibling selectors. Mirror the active index over
     with a MutationObserver - same pattern as the presentation-glint module -
     so the rotator's data-* contract, pause and announce logic stay untouched.
     Runs under reduced motion too: manual dot picks must still relight the
     hero (the CSS transition is already none there, so the swap is instant). */
  ready(function () {
    if (!('MutationObserver' in window)) return;
    var root = doc.querySelector('[data-hero-rotator]');
    if (!root) return;
    var slides = [].slice.call(root.querySelectorAll('.hero-slide'));
    var glows = [].slice.call(root.querySelectorAll('.pn-hero-halo .art-glow'));
    if (slides.length < 2 || glows.length !== slides.length) return;
    function sync() {
      for (var i = 0; i < slides.length; i++) {
        glows[i].classList.toggle('is-on', slides[i].classList.contains('is-on'));
      }
    }
    var mo = new MutationObserver(sync);
    slides.forEach(function (s) {
      mo.observe(s, { attributes: true, attributeFilter: ['class'] });
    });
    sync();
  });
  })();

  /* ==== prismatic:collection-search ==== */
  (function () {
/* Collection drawer tabs: stamp .is-stuck while the sticky toolbar is pinned
   under the 70px header so CSS can light its accent underline (the
   header.scrolled idiom). Observes the bar itself at threshold 1 with the
   header's offset carved out of the root - intersectionRatio dips below 1
   exactly while pinned; the boundingClientRect.top guard rejects the other
   way a bar loses ratio (clipped at the viewport's bottom edge on load).
   No scroll handler, no rAF; desktop-only to match the sticky rule. */
if (wideScreen && 'IntersectionObserver' in window) {
  ready(function () {
    var bar = doc.querySelector('.coll-room .coll-toolbar');
    if (!bar) return;
    var io = new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      bar.classList.toggle('is-stuck', e.intersectionRatio < 1 && e.boundingClientRect.top <= 71);
    }, { threshold: [1], rootMargin: '-71px 0px 0px 0px' });
    io.observe(bar);
  });
}
  })();

  /* ==== prismatic:pdp ==== */
  (function () {
/* ---- PDP vitrine light: the art-glow twin follows whichever photo the
   visitor picks from the thumb rail. The 140px thumb rendition is already
   warm in cache and 48px of blur erases the resolution difference. Purely
   decorative color (static unless motion-ok), so no reduceMotion gate. ---- */
ready(function () {
  var twin = doc.querySelector('.pdp-frame > .art-glow');
  var rail = doc.querySelector('.pdp-thumbs');
  if (!twin || !rail) return;
  rail.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[data-full]');
    if (!btn) return;
    var th = btn.querySelector('img');
    if (th && th.src) twin.src = th.src;
  });
});
  })();

  /* ==== prismatic:motion ==== */
  (function () {
/* ==== prismatic:motion choreography ==== */

  /* ---------- stagger promotion ----------
     The reveal IO already observes every .reveal and assigns --d only to
     .stagger groups; IO callbacks cannot fire until after DOMContentLoaded
     handlers finish, so promoting the class here (registered after the
     reveal module) still lands before any .in-view does. Curated on
     purpose: blanket auto-staggering would double-hide children that carry
     their own reveal classes. */
  ready(function () {
    var groups = doc.querySelectorAll('.tales-stats.reveal:not(.stagger)');
    if (!groups.length) return;
    var step = wideScreen ? 70 : 60; /* mirror the reveal module's timing */
    var cap = wideScreen ? 560 : 210;
    groups.forEach(function (el) {
      el.classList.add('stagger');
      for (var i = 0; i < el.children.length; i++) {
        el.children[i].style.setProperty('--d', Math.min(i * step, cap) + 'ms');
      }
    });
  });

  })();

  /* ==== everything:motion-sky ==== */
  (function () {
/* ==== everything:motion-sky ==== */
  (function () {
/* ---------- hero parallax depths: three layers under one pointer ----------
     The tilt rig owns .card-frame's TRANSFORM (rotateX/rotateY/scale custom
     props) and the seal's hover scale lives on `transform` too, so this
     module speaks through the separate individual `translate` property -
     the engine composes the two and neither module can overwrite the other.
     Depths: the glow halo drifts most, the card frame slides mid, the
     caption chip stays shallow; the seal rides inside the frame, so it gets
     a small COUNTER translate that nets its movement down to the shallow
     layer. Engines without individual transform properties keep the flat
     hero - the bail below is the whole fallback. */
  ready(function () {
    if (reduceMotion || !finePointer || !wideScreen) return;
    if (!(window.CSS && CSS.supports && CSS.supports('translate', '1px 1px'))) return;
    var plinth = doc.querySelector('.hero .plinth');
    if (!plinth) return;
    var frame = plinth.querySelector('.card-frame');
    if (!frame || !frame.querySelector('img')) return; /* noimg hero casts no depth */
    var hero = plinth.closest('.hero') || plinth;
    var halo = plinth.querySelector('.pn-hero-halo');
    var cap = plinth.querySelector('.plinth-cap');
    var seal = frame.querySelector('.seal');
    /* px of travel at full deflection; the seal's negatives subtract from
       the frame's mid travel it inherits as a child */
    var layers = [];
    if (halo) layers.push({ el: halo, fx: 26, fy: 16 });
    layers.push({ el: frame, fx: 10, fy: 6 });
    if (cap) layers.push({ el: cap, fx: 5, fy: 3 });
    if (seal) layers.push({ el: seal, fx: -5, fy: -3 });
    var cur = { x: 0, y: 0 };
    var tgt = { x: 0, y: 0 };
    var active = false;
    function loop() {
      cur.x += (tgt.x - cur.x) * 0.1;
      cur.y += (tgt.y - cur.y) * 0.1;
      for (var i = 0; i < layers.length; i++) {
        var L = layers[i];
        L.el.style.translate = (cur.x * L.fx).toFixed(2) + 'px ' + (cur.y * L.fy).toFixed(2) + 'px';
      }
      if (!active && Math.abs(tgt.x - cur.x) < 0.004 && Math.abs(tgt.y - cur.y) < 0.004) {
        removeTick(loop);
        if (halo) halo.style.willChange = '';
      }
    }
    hero.addEventListener('pointerenter', function () {
      active = true;
      /* promote only the big blurred layer, and only while interacting */
      if (halo) halo.style.willChange = 'transform';
      addTick(loop);
    });
    hero.addEventListener('pointermove', function (e) {
      var r = hero.getBoundingClientRect();
      var nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      var ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
      tgt.x = Math.max(-1, Math.min(1, nx));
      tgt.y = Math.max(-1, Math.min(1, ny));
    });
    hero.addEventListener('pointerleave', function () {
      active = false;
      tgt.x = 0; tgt.y = 0;
      addTick(loop); /* glide home even if the loop already settled */
    });
  });

/* ---------- add-to-cart foil spark: shards over the claim header ----------
     Observational hook only: the haul drawer's open() stamps .open on
     #tntcg-haul after every successful /cart/add.js, so one MutationObserver
     on that class is the whole seam - the drawer module never learns this
     exists and its fetch wrapper is untouched. Shards are throwaway spans on
     the holo gradient, animated by CSS (motion-ok + no-preference gated
     there too) and removed on animationend, with a timed sweep in case the
     animation never fires - spans must never pile up in the header. */
  ready(function () {
    if (reduceMotion || !('MutationObserver' in window)) return;
    var root = doc.getElementById('tntcg-haul');
    if (!root) return;
    var head = root.querySelector('.haul-head');
    if (!head) return;
    var lastBurst = 0;
    function burst() {
      var now = Date.now();
      if (now - lastBurst < 1000) return; /* rapid re-adds share one celebration */
      lastBurst = now;
      var n = 7;
      for (var i = 0; i < n; i++) {
        var s = doc.createElement('span');
        s.className = 'pn-spark';
        s.setAttribute('aria-hidden', 'true');
        /* fan the shards upward-and-outward from the title, lightly jittered */
        var ang = (-90 + (i / (n - 1) - 0.5) * 220) * Math.PI / 180 + (Math.random() - 0.5) * 0.3;
        var dist = 36 + Math.random() * 26;
        s.style.setProperty('--kx', (Math.cos(ang) * dist).toFixed(1) + 'px');
        s.style.setProperty('--ky', (Math.sin(ang) * dist).toFixed(1) + 'px');
        s.style.setProperty('--kr', Math.round(120 + Math.random() * 200) + 'deg');
        /* each shard samples a different stripe of the holo gradient */
        s.style.backgroundPosition = ((i * 53) % 100) + '% 50%';
        s.addEventListener('animationend', function () {
          if (this.parentNode) this.parentNode.removeChild(this);
        });
        head.appendChild(s);
      }
      setTimeout(function () {
        var stale = head.querySelectorAll('.pn-spark');
        for (var j = 0; j < stale.length; j++) {
          if (stale[j].parentNode) stale[j].parentNode.removeChild(stale[j]);
        }
      }, 900); /* under the 1s burst cap, so it can never eat a newer burst */
    }
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        /* token test, not substring: 'haul-drawer' must never false-match */
        var was = /(^|\s)open(\s|$)/.test(muts[i].oldValue || '');
        if (!was && root.classList.contains('open')) { burst(); break; }
      }
    }).observe(root, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
  });
  })();
  })();

  /* ==== everything:motion-doors ==== */
  (function () {
/* ==== everything:motion-doors (vault-door morph + seal loupe) ==== */
  (function () {
/* ---------- Vault-door open: home door -> collection placard morph ----------
   Mirror of the "Lifted from the Case" module above: clicking a home door
   tile (.cat-tile.door, sections/tntcg-fant-home.liquid) stamps
   view-transition-name:door-<handle> one beat before navigation; the
   collection page's placard block (.ch-main in
   sections/tntcg-fant-collection.liquid) carries the same name statically,
   and the cross-document @view-transition rig pairs the two so the door
   grows into the room's placard. The handle comes from the href - door
   tiles carry no data-handle - and is regex-fenced so the stamped name is
   always a valid custom-ident (the door- prefix keeps digit-leading
   handles legal). One element per name per page: the previous stamp is
   cleared so an aborted or new-tab navigation can never leave two tiles
   named. Same fence as the rig: desktop + no-preference only (the
   2026-07-10 burger-freeze trap lives below 861px and stays fenced off). */
  ready(function () {
    if (reduceMotion || !wideScreen) return;
    var last = null; /* the previously stamped tile, cleared on the next click */
    doc.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var tile = e.target.closest ? e.target.closest('a.cat-tile.door') : null;
      if (!tile) return;
      var m = (tile.getAttribute('href') || '').match(/\/collections\/([A-Za-z0-9-]+)(?:[\/?#]|$)/);
      if (!m) return; /* no clean handle = no stamp; the plain crossfade still runs */
      if (last && last !== tile) {
        last.style.removeProperty('view-transition-name');
        last.style.removeProperty('view-transition-class');
      }
      tile.style.setProperty('view-transition-name', 'door-' + m[1]);
      tile.style.setProperty('view-transition-class', 'door-swing');
      last = tile;
    });
  });

/* ---------- Seal loupe: Shift-held magnifier in the seal viewer ----------
   The dialog markup is product.liquid's; the lens is built HERE and appended
   to the stage on the dialog's first open, so no-JS pages never carry it.
   Hold Shift = a 180px holo ring follows the cursor showing 2.5x of the
   full-res rendition. The glass img lays out at a fixed 1000px and is
   mapped purely via transform (origin 0 0: point p lands at T + p*k), the
   ring rides translate3d - zero layout writes while tracking. A tick on the
   shared ticker runs ONLY while Shift is held so the sample stays honest
   through the pan/zoom glide, and is removed on release/close. Deliberately
   NOT gated on reduceMotion - this is a pointer tool, not an animation -
   and it follows the cursor directly with no glide easing. Fine-pointer
   only: Shift-hold is a keyboard+cursor gesture, and gating here also keeps
   the toast off touch devices where it would be a lie. */
  ready(function () {
    if (!finePointer || !('MutationObserver' in window)) return;
    var dlg = doc.querySelector('.seal-scope');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    var stage = dlg.querySelector('.seal-scope-stage');
    var img = dlg.querySelector('.seal-scope-img');
    if (!stage || !img) return;

    var RING = 180, MAG = 2.5, GLASS_W = 1000; /* GLASS_W mirrors the CSS layout width */
    var lens = null, glass = null;
    var lensOn = false;
    var cx = -1, cy = -1; /* last pointer position (client coords); -1 = never seen */

    function paintLens() {
      if (!lens || cx < 0) return; /* Shift before any pointer move: stay parked offscreen */
      var r = img.getBoundingClientRect(); /* already includes the engine's pan/zoom transform */
      if (!r.width || !r.height) return;
      var sr = stage.getBoundingClientRect();
      var u = (cx - r.left) / r.width; /* cursor's landing point on the art, 0..1 */
      var v = (cy - r.top) / r.height;
      if (u < 0 || u > 1 || v < 0 || v > 1) { lens.style.opacity = '0'; return; }
      lens.style.opacity = '1';
      /* follow thumb-rail swaps: openScope may retarget the stage img */
      if (img.currentSrc && glass.src !== img.currentSrc) glass.src = img.currentSrc;
      /* scale the glass so its on-screen width is MAG x the art's CURRENT
         on-screen width, then translate so the sampled point sits at the
         ring's centre. Same src = same aspect, so height derives exactly. */
      var k = (r.width * MAG) / GLASS_W;
      var gh = GLASS_W * (r.height / r.width);
      glass.style.transform = 'translate(' + (RING / 2 - u * GLASS_W * k).toFixed(1) + 'px,' +
        (RING / 2 - v * gh * k).toFixed(1) + 'px) scale(' + k.toFixed(4) + ')';
      lens.style.transform = 'translate3d(' + (cx - sr.left - RING / 2).toFixed(1) + 'px,' +
        (cy - sr.top - RING / 2).toFixed(1) + 'px,0)';
    }
    function tickPaint() { paintLens(); }

    function showLens() {
      if (lensOn || !lens) return;
      lensOn = true;
      lens.classList.add('on');
      stage.classList.add('loupe-on');
      paintLens();
      addTick(tickPaint); /* keeps the sample synced through the pan/zoom glide; not permanent */
    }
    function hideLens() {
      if (!lensOn) return;
      lensOn = false;
      removeTick(tickPaint);
      lens.classList.remove('on');
      stage.classList.remove('loupe-on');
    }

    function buildLens() {
      lens = doc.createElement('div');
      lens.className = 'seal-loupe';
      lens.setAttribute('aria-hidden', 'true');
      var clip = doc.createElement('div');
      clip.className = 'seal-loupe-clip';
      glass = doc.createElement('img');
      glass.className = 'seal-loupe-glass';
      glass.alt = '';
      clip.appendChild(glass);
      lens.appendChild(clip);
      stage.appendChild(lens); /* pointer-events:none, so drag/pinch pass straight through */
    }

    function maybeToast() {
      /* self-evidence for a hidden gesture: one line, once per session (once
         per page load if storage is walled off - the built flag already
         limits this call to the first open). */
      var KEY = 'tntcg-loupe-hint';
      var seenIt = false;
      try { seenIt = !!window.sessionStorage.getItem(KEY); } catch (err) {}
      if (seenIt) return;
      var hint = doc.createElement('p');
      hint.className = 'seal-loupe-hint';
      hint.setAttribute('role', 'status');
      hint.setAttribute('aria-live', 'polite');
      dlg.appendChild(hint); /* empty first: the live region must exist before its text */
      setTimeout(function () {
        hint.appendChild(doc.createTextNode('Hold '));
        var kbd = doc.createElement('kbd');
        kbd.textContent = 'Shift';
        hint.appendChild(kbd);
        hint.appendChild(doc.createTextNode(' for the loupe'));
        try { window.sessionStorage.setItem(KEY, '1'); } catch (err) {}
        setTimeout(function () {
          hint.classList.add('bye');
          setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 700);
        }, 4200);
      }, 400); /* let the dialog's own .3s entrance land first */
    }

    /* first open builds the lens + maybe the toast; every close kills a held
       loupe. The open attribute is the one signal all open/close paths share
       (trigger chip, plate click, Escape), so a single observer covers them
       without touching the dialog module's own wiring. */
    var built = false;
    new MutationObserver(function () {
      if (!dlg.open) { hideLens(); return; }
      if (built) return;
      built = true;
      buildLens();
      maybeToast();
    }).observe(dlg, { attributes: true, attributeFilter: ['open'] });

    dlg.addEventListener('keydown', function (e) { if (e.key === 'Shift') showLens(); });
    dlg.addEventListener('keyup', function (e) { if (e.key === 'Shift') hideLens(); });
    window.addEventListener('blur', hideLens); /* missed keyup: alt-tab away with Shift down */
    stage.addEventListener('pointermove', function (e) {
      cx = e.clientX; cy = e.clientY;
      if (lensOn && !e.shiftKey) hideLens(); /* missed-keyup self-heal */
    });
  });
  })();
  })();

  /* ==== everything:pdp-commerce ==== */
  (function () {
/* ==== everything:pdp-commerce ==== */

  /* ---------- release countdown: live days-hrs-min above the buy box ----------
     Markup is DATA-GATED in Liquid (release:YYYY-MM-DD tag); the server ships
     the date line and a [hidden] count row. This layer only fills and unhides
     the count, targeting midnight LOCAL time - the moment release day begins -
     and hides it again the second release day arrives. One setTimeout wake per
     minute, aligned to the boundary: not a rAF loop, it self-terminates at
     release, and background tabs get browser timer throttling for free. */
  ready(function () {
    var tile = doc.querySelector('[data-release]');
    if (!tile) return;
    var count = tile.querySelector('[data-release-count]');
    if (!count) return;
    var parts = (tile.getAttribute('data-release') || '').split('-');
    if (parts.length !== 3) return;
    /* new Date(y, m, d) = local midnight; the Liquid comment owns the honesty
       contract (date-only, "until release day", no fabricated hour) */
    var target = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (isNaN(target.getTime())) return;
    var dEl = count.querySelector('[data-rc="d"]');
    var hEl = count.querySelector('[data-rc="h"]');
    var mEl = count.querySelector('[data-rc="m"]');
    if (!dEl || !hEl || !mEl) return;
    function setCell(el, val) {
      var s = String(val);
      if (el.textContent === s) return;
      el.textContent = s;
      /* minute roll (CSS-gated on reduced motion): retrigger the keyframe */
      el.classList.remove('tick');
      void el.offsetWidth;
      el.classList.add('tick');
    }
    var timer = null;
    function paint() {
      var ms = target.getTime() - Date.now();
      if (ms <= 0) { count.hidden = true; return; } /* release day: the date line stands alone */
      var mins = Math.floor(ms / 60000);
      setCell(dEl, Math.floor(mins / 1440));
      setCell(hEl, Math.floor((mins % 1440) / 60));
      setCell(mEl, mins % 60);
      count.hidden = false;
      timer = setTimeout(paint, (ms % 60000) + 60);
    }
    paint();
    /* a tab restored hours later would show its parked count until the next
       minute tick - repaint immediately when the page comes back */
    doc.addEventListener('visibilitychange', function () {
      if (doc.hidden) return;
      if (timer) clearTimeout(timer);
      paint();
    });
  });

  /* ---------- bundle quick-add: box + sleeves + loaders in ONE call ----------
     The button ships only when Liquid verified every part (buyable box,
     single-variant in-stock picks). One POST with items[] so the haul drawer's
     fetch seam sees a single /cart/add and announces once (it reads
     added.items[0] - the box - as the landing item). The box variant id is
     read from the live form at click time so a variant switch is honored.
     toast/setCartCount come from the enclosing engine closure, same as the
     shipped quick-add flow. */
  ready(function () {
    var btn = doc.querySelector('button[data-bundle]');
    if (!btn || !window.fetch) return;
    var original = btn.textContent;
    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      var form = doc.querySelector('form[action*="/cart/add"]');
      var idEl = form && form.querySelector('[name="id"]');
      var boxId = idEl ? parseInt(idEl.value, 10) : 0;
      var items = [];
      if (boxId) items.push({ id: boxId, quantity: 1 });
      (btn.getAttribute('data-bundle-ids') || '').split(',').forEach(function (s) {
        var n = parseInt(s, 10);
        if (n) items.push({ id: n, quantity: 1 });
      });
      if (items.length < 3) return; /* a missing part = no half-bundle surprises */
      btn.disabled = true;
      btn.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add the bundle'); });
        return res.json();
      }).then(function () {
        /* badge refresh is best-effort, same as the PDP form flow */
        fetch('/cart.js').then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) { if (cart) setCartCount(cart.item_count); })
          .catch(function () {});
        toast('Bundle added to your cart.', '/cart', 'View cart');
        btn.textContent = 'Added to cart';
        setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1600);
      }).catch(function (err) {
        btn.disabled = false;
        btn.textContent = original;
        toast((err && err.message) || 'Could not add the bundle. Please try again.');
      });
    });
  });
  })();

  /* ==== everything:stamp-hygiene ==== */
  (function () {
    /* The door and relic morph modules each clear only their OWN last stamp,
       so an aborted navigation (or a bfcache restore) can leave a stale
       view-transition-name that pairs with the next page's static twin and
       morphs from an element the user never clicked. Grid-side stamps live
       only on relic imgs and door tiles - the PDP plate and collection head
       carry STATIC names that must never be touched. */
    function wipe() {
      var stamped = doc.querySelectorAll('.relic img[style*="view-transition-name"], .cat-tile[style*="view-transition-name"]');
      for (var i = 0; i < stamped.length; i++) {
        stamped[i].style.removeProperty('view-transition-name');
        stamped[i].style.removeProperty('view-transition-class');
      }
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) wipe(); });
    /* capture phase runs before both modules' own click handlers, so every
       fresh click starts from zero stamps regardless of which kind is stale */
    doc.addEventListener('click', function () { wipe(); }, true);
  })();

  /* ==== guide:finder ==== */
  (function () {
/* ==== guide:finder ==== */
  (function () {
  /* ---------- The Vault Guide stepper (/pages/finder) ----------
     sections/tntcg-fant-finder.liquid server-renders every step as plain
     labelled anchor grids; this module upgrades the page into a stepper.
     It stamps .vg-live (the only class the hiding CSS keys on), parks both
     step-two panels behind [hidden], and lets a branch world tile choose
     which panel opens. Direct worlds (Kayou / Lorcana / FF) carry no
     branch attribute and keep their normal navigation - the door morph
     module still owns those clicks unmodified. Modifier clicks and
     non-primary buttons on branch tiles fall through to the anchor, so
     open-in-new-tab lands on the world's real collection. All text is
     written via textContent; every href set here was rendered by this
     theme into a data attribute. Announcements ride a visually-hidden
     role=status line that exists from first paint. */
  ready(function () {
    var root = doc.querySelector('[data-vg]');
    if (!root) return;
    var hunt = root.querySelector('[data-vg-panel="hunt"]');
    var gear = root.querySelector('[data-vg-panel="gear"]');
    if (!hunt || !gear) return;
    var crumb = root.querySelector('[data-vg-crumb]');
    var status = root.querySelector('[data-vg-status]');
    var room = root.querySelector('[data-vg-room]');
    var roomLabel = room ? room.querySelector('[data-vg-room-label]') : null;
    var worldTiles = Array.prototype.slice.call(root.querySelectorAll('[data-vg-world]'));
    if (!worldTiles.length) return;

    root.classList.add('vg-live');
    hunt.hidden = true;
    gear.hidden = true;
    /* branch tiles choose a step now instead of navigating, so say so */
    worldTiles.forEach(function (t) {
      var go = t.querySelector('.go');
      if (go) go.textContent = 'Choose →';
    });

    var worldLabel = '';

    function say(text) {
      if (status) status.textContent = text;
    }
    function setCrumb(text) {
      if (!crumb) return;
      crumb.hidden = false;
      crumb.textContent = text;
    }

    function select(tile) {
      var branch = tile.getAttribute('data-vg-branch') === 'gear' ? 'gear' : 'hunt';
      worldLabel = tile.getAttribute('data-vg-label') || '';
      worldTiles.forEach(function (t) {
        var on = t === tile;
        t.classList.toggle('is-sel', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      var open = branch === 'gear' ? gear : hunt;
      var shut = branch === 'gear' ? hunt : gear;
      shut.hidden = true;
      open.hidden = false;
      if (branch === 'hunt') {
        /* hunt accents + the whole-room band follow the chosen world */
        var game = tile.getAttribute('data-vg-game') || '';
        if (game) hunt.setAttribute('data-game', game);
        else hunt.removeAttribute('data-game');
        if (room) {
          var href = tile.getAttribute('data-vg-room-href') || room.getAttribute('data-vg-room-default-href');
          var lab = tile.getAttribute('data-vg-room-label') || room.getAttribute('data-vg-room-default-label');
          if (href) room.setAttribute('href', href);
          if (roomLabel && lab) roomLabel.textContent = lab;
        }
      }
      var ask = branch === 'gear' ? 'what needs protecting?' : 'what kind of hunt?';
      setCrumb(worldLabel + ' → ' + ask);
      say(worldLabel + ' selected. Step two: ' + ask);
      var h = open.querySelector('h2');
      if (h) {
        h.setAttribute('tabindex', '-1');
        try { h.focus({ preventScroll: true }); } catch (err) { h.focus(); }
      }
      try { open.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }); }
      catch (err) { open.scrollIntoView(); }
    }

    root.addEventListener('click', function (e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!a || !root.contains(a)) return;
      var jump = a.getAttribute('data-vg-jump');
      if (jump) {
        /* the "jump to the armor" crosslink selects the matching world tile;
           with no such tile it stays a real /collections/supplies anchor */
        for (var i = 0; i < worldTiles.length; i++) {
          if (worldTiles[i].getAttribute('data-vg-world') === jump) {
            e.preventDefault();
            select(worldTiles[i]);
            return;
          }
        }
        return;
      }
      if (a.hasAttribute('data-vg-branch')) {
        e.preventDefault();
        select(a);
        return;
      }
      /* a step-two pick: stamp the trail on the way out, navigation proceeds */
      var pick = a.getAttribute('data-vg-pick');
      if (pick && worldLabel) setCrumb(worldLabel + ' → ' + pick);
    });
  });
  })();
  })();

  /* ==== guide:home-guide ==== */
  (function () {
/* ==== guide:home-guide ==== */
(function () {
  /* "I know what I'm hunting" wayfinding tile -> the predictive search
     overlay. The overlay module wires doc.querySelector('[data-seek-open]')
     - singular - and the header icon owns that query, so the tile carries
     its own [data-gw-seek] hook and proxies a click to the wired opener:
     open/close, focus restore, Esc and the focus trap all stay in the one
     shipped module. If the opener or overlay are missing (markup changed,
     module stood down), the tile stays a plain working link to /search. */
  ready(function () {
    var tile = doc.querySelector('[data-gw-seek]');
    if (!tile) return;
    var opener = doc.querySelector('[data-seek-open]');
    var overlay = doc.querySelector('.seek-overlay');
    if (!opener || !overlay) return;
    tile.setAttribute('aria-haspopup', 'dialog');
    tile.addEventListener('click', function (e) {
      e.preventDefault();
      opener.click();
    });
  });
})();
  })();

  /* ==== events:events-motion ==== */
  (function () {
/* ==== events:events-motion ==== */
/* The events page's moving parts: live countdowns, type filtering with a
   FLIP transition, .ics download, timeline reveal and share. Every block
   bails silently the moment its hook is missing, so this module is inert on
   every other template - and the events page stays complete, readable and
   operable if the whole thing never runs. */

var evSlice = function (list) { return Array.prototype.slice.call(list || []); };
/* motion-ok is stamped by the engine before this runs; reduceMotion is the
   engine's own media-query read. Both must be true before anything moves. */
var evMotion = !reduceMotion && doc.documentElement.classList.contains('motion-ok');

/* ---------- one polite live region for the whole page ---------- */
var evStatus = null;
var evStatusResolved = false;
var evSayToken = 0;
var evSayTimer = null;
var evResting = null;
/* transient=true for a one-off confirmation ("link copied"). Where the page
   renders the region on screen, its standing sentence is put back afterwards
   instead of being permanently replaced by a receipt. */
function evSay(msg, transient) {
  if (!evStatusResolved) {
    evStatusResolved = true;
    evStatus = doc.querySelector('[data-ev-status]');
    if (!evStatus) {
      evStatus = doc.createElement('p');
      evStatus.className = 'visually-hidden';
      evStatus.setAttribute('data-ev-status', '');
      evStatus.setAttribute('role', 'status');
      evStatus.setAttribute('aria-live', 'polite');
      var host = doc.querySelector('[data-ev-root]') || doc.querySelector('[data-ev-grid]') || doc.body;
      if (host) host.appendChild(evStatus);
      else evStatus = null;
    }
  }
  if (!evStatus || !msg) return;
  /* the standing sentence: seeded from whatever the page shipped, replaced by
     every persistent message, and restored after a transient one. Held apart
     from the live text so a run of receipts cannot erase it. */
  if (evResting === null) evResting = evStatus.textContent || '';
  var token = ++evSayToken;
  if (evSayTimer) { clearTimeout(evSayTimer); evSayTimer = null; }
  if (!transient) {
    evResting = msg;
  } else if (!evStatus.classList.contains('visually-hidden') && evResting && evResting !== msg) {
    evSayTimer = setTimeout(function () {
      if (token !== evSayToken) return;
      evStatus.textContent = evResting;
    }, 5000);
  }
  if (evStatus.textContent === msg) {
    /* identical text is not a change, so AT would stay silent. Blanking and
       restating fixes that, but only where the region is visually hidden -
       doing it to a region the page renders on screen would flash the copy. */
    if (!evStatus.classList.contains('visually-hidden')) return;
    var region = evStatus;
    region.textContent = '';
    setTimeout(function () { region.textContent = msg; }, 60);
    return;
  }
  evStatus.textContent = msg;
}

/* ---------- shared button feedback: swap an optional label, mark the state,
     and put both back after a beat ---------- */
function evFlash(btn, text) {
  var lab = btn.querySelector('[data-ev-btn-label]');
  btn.classList.add('is-done');
  if (lab) {
    if (btn.evLabelWas === undefined || btn.evLabelWas === null) btn.evLabelWas = lab.textContent;
    lab.textContent = text;
  }
  if (btn.evFlashTimer) clearTimeout(btn.evFlashTimer);
  btn.evFlashTimer = setTimeout(function () {
    btn.classList.remove('is-done');
    if (lab && btn.evLabelWas !== undefined && btn.evLabelWas !== null) {
      lab.textContent = btn.evLabelWas;
      btn.evLabelWas = null;
    }
  }, 2400);
}

/* a type value is compared handleized, so "Draft Night" from the metaobject
   and "draft-night" from a handleize filter are the same thing */
function evNorm(v) {
  return String(v === null || v === undefined ? '' : v)
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '').replace(/-+$/, '');
}

function evSlug(v) {
  var s = evNorm(v);
  return s || 'event';
}

/* a <button> with no type inside a Shopify form is a submit button */
function evFixButton(el) {
  if (el.tagName === 'BUTTON' && !el.getAttribute('type')) el.setAttribute('type', 'button');
}

/* ---------- 1. live countdowns ----------
   [data-ev-countdown="<start epoch seconds>"], with an optional end epoch on
   either data-ev-end or data-ev-ends, and an optional [data-ev-count-live]
   slot for the live line. One shared rAF tick drives every countdown on the
   page and only touches the DOM when the displayed second actually changes.
   Nothing ever renders negative time, and the surrounding copy - above all
   the human date line - is never overwritten. */
ready(function () {
  var hosts = evSlice(doc.querySelectorAll('[data-ev-countdown]'));
  if (!hosts.length) return;

  var UNITS = ['days', 'hours', 'mins', 'secs'];
  var LABELS = { days: ['day', 'days'], hours: ['hr', 'hrs'], mins: ['min', 'mins'], secs: ['sec', 'secs'] };

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  function makeCell(unit) {
    var cell = doc.createElement('span');
    cell.className = 'ev-cd-cell';
    cell.setAttribute('data-ev-cd-unit', unit);
    var num = doc.createElement('b');
    num.className = 'ev-cd-num';
    num.setAttribute('data-ev-cd-val', '');
    num.textContent = '0';
    var lab = doc.createElement('i');
    lab.className = 'ev-cd-lab';
    lab.setAttribute('data-ev-cd-lab', '');
    lab.textContent = LABELS[unit][1];
    cell.appendChild(num);
    cell.appendChild(lab);
    return cell;
  }

  /* restart the settle animation without forcing a synchronous layout:
     animationend clears the flag, with a timer as the safety net */
  function flip(num) {
    if (!evMotion || num.evFlipping) return;
    num.evFlipping = true;
    num.classList.add('is-flip');
    var clear = function () {
      num.classList.remove('is-flip');
      num.evFlipping = false;
      num.removeEventListener('animationend', clear);
      if (num.evFlipTimer) { clearTimeout(num.evFlipTimer); num.evFlipTimer = null; }
    };
    num.addEventListener('animationend', clear);
    num.evFlipTimer = setTimeout(clear, 700);
  }

  function setup(host) {
    var start = parseInt(host.getAttribute('data-ev-countdown'), 10);
    if (isNaN(start) || start <= 0) return null;
    /* the page may name the end either way round */
    var ends = parseInt(host.getAttribute('data-ev-ends'), 10);
    if (isNaN(ends)) ends = parseInt(host.getAttribute('data-ev-end'), 10);
    if (isNaN(ends) || ends <= start) ends = 0;
    /* an optional slot the page reserves for the live/wrapped line */
    var liveSlot = host.querySelector('[data-ev-count-live]');

    var cells = {};
    var found = evSlice(host.querySelectorAll('[data-ev-cd-unit]'));
    for (var i = 0; i < found.length; i++) {
      var u = found[i].getAttribute('data-ev-cd-unit');
      if (UNITS.indexOf(u) > -1) cells[u] = found[i];
    }
    var mount = host;
    var owned = false;
    if (!cells.days || !cells.hours || !cells.mins || !cells.secs) {
      owned = true;
      cells = {};
      /* A host with element children is carrying real content - the human
         date line, a reserved live slot - and must never be wiped: that copy
         is the authoritative time. Only a bare text host (a plain "starts
         soon" no-JS fallback) is replaced outright. */
      if (host.firstElementChild) {
        mount = doc.createElement('p');
        mount.className = 'ev-cd';
        if (liveSlot) host.insertBefore(mount, liveSlot);
        else host.appendChild(mount);
      } else {
        host.textContent = '';
        host.classList.add('ev-cd');
      }
      for (var j = 0; j < UNITS.length; j++) {
        var cell = makeCell(UNITS[j]);
        mount.appendChild(cell);
        cells[UNITS[j]] = cell;
      }
    }
    /* a frozen seconds cell would be a lie under reduced motion, where we
       render once and never tick - so drop it and keep minute precision */
    if (reduceMotion && cells.secs) cells.secs.hidden = true;

    var state = { mode: '', prev: {} };

    function put(unit, value, text, hide) {
      var cell = cells[unit];
      if (!cell) return;
      if (hide !== undefined && cell.hidden !== hide) {
        cell.hidden = hide;
        /* coming back into view must repaint even if the value is unchanged */
        if (!hide) state.prev[unit] = undefined;
      }
      if (cell.hidden) return;
      if (state.prev[unit] === value) return;
      state.prev[unit] = value;
      var num = cell.querySelector('[data-ev-cd-val]') || cell.querySelector('.ev-cd-num');
      if (num) { num.textContent = text; flip(num); }
      var lab = cell.querySelector('[data-ev-cd-lab]') || cell.querySelector('.ev-cd-lab');
      if (lab) lab.textContent = LABELS[unit][value === 1 ? 0 : 1];
    }

    function hideLockup() {
      if (mount !== host) { mount.hidden = true; return; }
      for (var u = 0; u < UNITS.length; u++) {
        if (cells[UNITS[u]]) cells[UNITS[u]].hidden = true;
      }
    }

    function banner(mode) {
      var wrap = doc.createElement('span');
      wrap.className = mode === 'live' ? 'ev-cd-live' : 'ev-cd-live ev-cd-live--done';
      if (mode === 'live') {
        var dot = doc.createElement('i');
        dot.className = 'ev-now-dot';
        dot.setAttribute('aria-hidden', 'true');
        wrap.appendChild(dot);
        wrap.appendChild(doc.createTextNode(ends ? 'Happening now' : 'Starting now'));
      } else {
        wrap.appendChild(doc.createTextNode('Just wrapped'));
      }
      /* the badges were rendered server-side from the state at request time;
         once this module moves the state on, they must move with it or the
         card contradicts its own countdown */
      var card = host.closest ? host.closest('.ev-feat') : null;
      var badge = card ? card.querySelector('.ev-feat-badge') : null;
      if (badge) badge.textContent = mode === 'live' ? 'Happening now' : 'Just wrapped';
      var row = doc.querySelector('.ev-row .ev-card-next');
      if (row) row.textContent = mode === 'live' ? 'Live now' : 'Just wrapped';

      /* fill the page's reserved slot when there is one; otherwise replace
         only what this module built, never the surrounding copy */
      if (liveSlot) {
        liveSlot.textContent = '';
        liveSlot.appendChild(wrap);
        liveSlot.hidden = false;
        hideLockup();
        return;
      }
      if (owned && mount !== host) { mount.textContent = ''; mount.appendChild(wrap); return; }
      if (owned) { host.textContent = ''; host.classList.add('ev-cd'); host.appendChild(wrap); return; }
      hideLockup();
      host.appendChild(wrap);
    }

    /* returns true once this countdown has nothing left to say */
    function render(nowSec) {
      var mode = 'count';
      if (ends && nowSec >= ends) mode = 'done';
      else if (nowSec >= start) mode = 'live';
      if (mode !== state.mode) {
        state.mode = mode;
        host.setAttribute('data-ev-cd-state', mode);
        if (mode !== 'count') banner(mode);
      }
      if (mode !== 'count') return mode === 'done' || !ends;
      var rem = start - nowSec;
      if (rem < 0) rem = 0;
      var d = Math.floor(rem / 86400);
      var h = Math.floor((rem % 86400) / 3600);
      var m = Math.floor((rem % 3600) / 60);
      var s = rem % 60;
      /* the lockup tightens as the event nears: a leading "0 DAYS" is noise,
         and dropping it makes the final hour read as the event it is */
      put('days', d, String(d), d === 0);
      put('hours', h, pad2(h), d === 0 && h === 0);
      put('mins', m, pad2(m));
      put('secs', s, pad2(s));
      return false;
    }

    return { render: render };
  }

  var items = [];
  for (var k = 0; k < hosts.length; k++) {
    var item = setup(hosts[k]);
    if (item) items.push(item);
  }
  if (!items.length) return;

  var nowSec = Math.floor(Date.now() / 1000);
  for (var n = items.length - 1; n >= 0; n--) {
    if (items[n].render(nowSec)) items.splice(n, 1);
  }
  /* reduced motion: the remaining time is painted once above, and that is
     the whole feature - no ticker is ever registered */
  if (reduceMotion || !items.length) return;

  var lastSec = nowSec;
  function loop() {
    var sec = Math.floor(Date.now() / 1000);
    if (sec === lastSec) return;
    lastSec = sec;
    for (var i = items.length - 1; i >= 0; i--) {
      if (items[i].render(sec)) items.splice(i, 1);
    }
    if (!items.length) removeTick(loop);
  }
  addTick(loop);
});

/* ---------- 2. type filtering, FLIP transition ----------
   [data-ev-filter="<type or all>"] chips over [data-ev-type] rows inside
   [data-ev-list] (or [data-ev-grid]), with [data-ev-month] groups folding
   away once empty. Hidden rows get the hidden attribute (already
   display:none !important site-wide), so they leave the layout, the a11y
   tree and the tab order together. */
ready(function () {
  var chips = evSlice(doc.querySelectorAll('[data-ev-filter]'));
  if (!chips.length) return;

  /* the stream wrapper, however the page names it. Scoping to it matters:
     the chips themselves carry data-ev-type for their accent, and the
     already-happened list carries it too - neither belongs in the filter. */
  var grid = doc.querySelector('[data-ev-list]') || doc.querySelector('[data-ev-grid]');
  var tiles = evSlice((grid || doc).querySelectorAll('[data-ev-item],[data-ev-type]')).filter(function (t) {
    return !t.hasAttribute('data-ev-filter');
  });
  if (!tiles.length) return;
  if (!grid) grid = tiles[0].parentNode;
  if (!grid) return;

  /* month headings must not survive their own rows */
  var groups = evSlice(grid.querySelectorAll('[data-ev-month]')).filter(function (g) {
    return g.querySelector('[data-ev-item]') !== null;
  });

  var emptyOwned = false;
  var empty = grid.querySelector('[data-ev-empty]') || doc.querySelector('[data-ev-empty]');
  if (!empty) {
    empty = doc.createElement('p');
    empty.className = 'ev-empty-filter';
    empty.setAttribute('data-ev-empty', '');
    grid.appendChild(empty);
    emptyOwned = true;
  }
  empty.hidden = true;

  var canAnimate = evMotion && typeof Element !== 'undefined' &&
    Element.prototype && typeof Element.prototype.animate === 'function' &&
    tiles.length <= 48;
  var EASE = 'cubic-bezier(.16,1,.3,1)';
  var DUR = 320;

  /* a chip may carry a count badge as a child element ("Draft Night" + "3");
     only the chip's own text nodes are its name */
  function labelOf(chip) {
    var explicit = chip.getAttribute('data-ev-label');
    if (explicit) return explicit.replace(/\s+/g, ' ').trim();
    var out = '';
    for (var n = chip.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3) out += n.nodeValue;
    }
    out = out.replace(/\s+/g, ' ').trim();
    return out || (chip.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /* the page's own opening sentence, restored verbatim whenever the filter
     is cleared - so its wording (and its timezone note) is never lost */
  var baseStatus = '';

  function apply(value, label, animate) {
    var i, t;
    var play = animate && canAnimate;
    var first = null;

    if (play) {
      /* settle any FLIP still in flight before measuring, so "first" is a
         real resting position and not a mid-animation frame */
      for (i = 0; i < tiles.length; i++) {
        if (tiles[i].evAnim) { try { tiles[i].evAnim.cancel(); } catch (e) {} tiles[i].evAnim = null; }
      }
      first = [];
      for (i = 0; i < tiles.length; i++) {
        first.push(tiles[i].hidden ? null : tiles[i].getBoundingClientRect());
      }
    }

    var shown = 0;
    for (i = 0; i < tiles.length; i++) {
      t = tiles[i];
      var match = value === 'all' || evNorm(t.getAttribute('data-ev-type')) === value;
      t.hidden = !match;
      if ('inert' in t) t.inert = !match;
      if (match) shown++;
    }

    /* a month heading with no rows left under it is a lie about the month */
    for (i = 0; i < groups.length; i++) {
      groups[i].hidden = groups[i].querySelector('[data-ev-item]:not([hidden])') === null;
    }

    empty.hidden = shown > 0;
    if (!shown && emptyOwned) {
      empty.textContent = label
        ? 'Nothing on the calendar for ' + label + ' right now. Try another type.'
        : 'Nothing on the calendar for that type right now.';
    }
    /* announce only a change the visitor asked for: the opening render must
       not overwrite the sentence the page shipped */
    if (animate) {
      var word = shown === 1 ? 'event' : 'events';
      if (shown === 0) {
        evSay(label ? 'No ' + label + ' events on the calendar right now.' : 'No events match that filter.');
      } else if (value === 'all') {
        evSay(baseStatus || ('Showing all ' + shown + ' upcoming ' + word + '.'));
      } else {
        evSay('Showing ' + shown + ' ' + label + ' ' + word + '.');
      }
    }

    if (!play) return;
    for (i = 0; i < tiles.length; i++) {
      t = tiles[i];
      if (t.hidden) continue;
      var last = t.getBoundingClientRect();
      var was = first[i];
      if (was) {
        var dx = was.left - last.left;
        var dy = was.top - last.top;
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
        t.evAnim = t.animate(
          [{ transform: 'translate3d(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px,0)' },
           { transform: 'translate3d(0,0,0)' }],
          { duration: DUR, easing: EASE }
        );
      } else {
        t.evAnim = t.animate(
          [{ opacity: 0, transform: 'translate3d(0,12px,0)' },
           { opacity: 1, transform: 'translate3d(0,0,0)' }],
          { duration: DUR, easing: EASE }
        );
      }
    }
  }

  /* The page may mark the active chip with a class of its own (.on, .is-active,
     whatever it chose) as well as aria-pressed. Rather than guess the name,
     read it off the markup: any class the pre-pressed chip has that no
     unpressed chip has IS the marker, and travels with the selection. Without
     this the page's own highlight would stay stuck on the first chip. */
  var markers = [];
  (function () {
    var on = null, off = [];
    for (var i = 0; i < chips.length; i++) {
      if (chips[i].getAttribute('aria-pressed') === 'true' && !on) on = chips[i];
      else off.push(chips[i]);
    }
    if (!on || !off.length) return;
    for (var c = 0; c < on.classList.length; c++) {
      var name = on.classList[c];
      var shared = false;
      for (var o = 0; o < off.length; o++) {
        if (off[o].classList.contains(name)) { shared = true; break; }
      }
      if (!shared) markers.push(name);
    }
  }());

  function select(chip, animate) {
    for (var i = 0; i < chips.length; i++) {
      chips[i].setAttribute('aria-pressed', chips[i] === chip ? 'true' : 'false');
      for (var m = 0; m < markers.length; m++) {
        chips[i].classList.toggle(markers[m], chips[i] === chip);
      }
    }
    var raw = chip.getAttribute('data-ev-filter') || '';
    var value = evNorm(raw);
    if (!value || value === 'all') value = 'all';
    apply(value, value === 'all' ? '' : labelOf(chip), animate);
  }

  var pageStatus = doc.querySelector('[data-ev-status]');
  if (pageStatus) baseStatus = (pageStatus.textContent || '').replace(/\s+/g, ' ').trim();

  var initial = null;
  for (var c = 0; c < chips.length; c++) {
    evFixButton(chips[c]);
    if (!chips[c].hasAttribute('aria-pressed')) chips[c].setAttribute('aria-pressed', 'false');
    if (chips[c].getAttribute('aria-pressed') === 'true' && !initial) initial = chips[c];
    (function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        select(chip, true);
      });
    }(chips[c]));
  }
  if (!initial) {
    for (var a = 0; a < chips.length; a++) {
      if (evNorm(chips[a].getAttribute('data-ev-filter')) === 'all') { initial = chips[a]; break; }
    }
  }
  if (!initial) initial = chips[0];
  select(initial, false);
});

/* ---------- 3. add to calendar (.ics) ----------
   [data-ev-ics] carrying data-ics-title / -start / -end / -desc / -loc /
   -url / -uid / -rrule / -file. -start and -end accept either the UTC
   ISO8601 string the metaobject stores or plain epoch seconds. Only fields
   the button actually carries are written: no end time is invented, and no
   recurrence rule is added unless the page passes one. */
ready(function () {
  /* an explicit hook, or simply any control carrying both of the fields an
     event needs - the page does not have to remember a second attribute */
  var btns = evSlice(doc.querySelectorAll('[data-ev-ics],[data-ics-title][data-ics-start]'));
  if (!btns.length) return;

  function icsEscape(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n|\r|\n/g, '\\n');
  }

  /* UTF-8 octet count without TextEncoder, because RFC 5545 folds on octets */
  function byteLen(s) {
    var n = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 0x80) n += 1;
      else if (c < 0x800) n += 2;
      else if (c >= 0xD800 && c <= 0xDBFF) { n += 4; i++; }
      else n += 3;
    }
    return n;
  }

  /* fold to 75 octets; continuation lines carry a leading space, which is
     itself one of the 75 */
  function fold(line) {
    if (byteLen(line) <= 75) return line;
    var parts = [], cur = '', n = 0, limit = 75;
    for (var i = 0; i < line.length; i++) {
      var ch = line.charAt(i);
      var code = line.charCodeAt(i);
      if (code >= 0xD800 && code <= 0xDBFF && i + 1 < line.length) { ch = line.substr(i, 2); i++; }
      var b = byteLen(ch);
      if (n + b > limit) { parts.push(cur); cur = ''; n = 0; limit = 74; }
      cur += ch;
      n += b;
    }
    parts.push(cur);
    return parts.join('\r\n ');
  }

  function stamp(d) {
    function p(n) { return n < 10 ? '0' + n : String(n); }
    return d.getUTCFullYear() + p(d.getUTCMonth() + 1) + p(d.getUTCDate()) + 'T' +
      p(d.getUTCHours()) + p(d.getUTCMinutes()) + p(d.getUTCSeconds()) + 'Z';
  }

  function toDate(v) {
    if (!v) return null;
    var s = String(v).trim();
    if (!s) return null;
    if (/^\d{9,}$/.test(s)) {
      var epoch = parseInt(s, 10);
      if (isNaN(epoch)) return null;
      return new Date(epoch * 1000);
    }
    /* RFC 5545 basic form - what an .ics file itself carries, and what the
       events section emits. Parsed explicitly because engines disagree on it. */
    var basic = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (basic) {
      return new Date(Date.UTC(parseInt(basic[1], 10), parseInt(basic[2], 10) - 1,
        parseInt(basic[3], 10), parseInt(basic[4], 10), parseInt(basic[5], 10),
        parseInt(basic[6], 10)));
    }
    var d = new Date(s);
    if (isNaN(d.getTime())) {
      /* older Safari rejects some ISO shapes; rebuild an explicit UTC one.
         Only ...Z strings take this path, so nothing is re-zoned by guess. */
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?Z$/);
      if (m) {
        d = new Date(Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10),
          parseInt(m[4], 10), parseInt(m[5], 10), m[6] ? parseInt(m[6], 10) : 0));
      }
    }
    return isNaN(d.getTime()) ? null : d;
  }

  var ICS_TZ = 'America/New_York';
  var ICS_VTIMEZONE = [
    'BEGIN:VTIMEZONE', 'TZID:' + ICS_TZ,
    'BEGIN:DAYLIGHT', 'TZOFFSETFROM:-0500', 'TZOFFSETTO:-0400', 'TZNAME:EDT',
    'DTSTART:19700308T020000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU', 'END:DAYLIGHT',
    'BEGIN:STANDARD', 'TZOFFSETFROM:-0400', 'TZOFFSETTO:-0500', 'TZNAME:EST',
    'DTSTART:19701101T020000', 'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU', 'END:STANDARD',
    'END:VTIMEZONE'];
  /* Eastern wall-clock stamp for a recurring event. A UTC DTSTART with an
     RRULE replays the same UTC instant every week, so the customer's copy
     would slide an hour at each DST change; a TZID-anchored local time does
     not. Falls back to null (and therefore to UTC) where Intl cannot resolve
     the zone, so the file is never malformed. */
  function stampLocalET(d) {
    try {
      var f = new Intl.DateTimeFormat('en-US', { timeZone: ICS_TZ, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      var o = {};
      f.formatToParts(d).forEach(function (p) { o[p.type] = p.value; });
      if (!o.year || !o.month || !o.day || !o.hour) return null;
      return o.year + o.month + o.day + 'T' + (o.hour === '24' ? '00' : o.hour) + o.minute + o.second;
    } catch (e) { return null; }
  }

  function build(f) {
    var L = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//TopNotchTCG//Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH'
    ];
    var localStart = f.rrule ? stampLocalET(f.start) : null;
    if (localStart) L = L.concat(ICS_VTIMEZONE);
    L.push('BEGIN:VEVENT', 'UID:' + f.uid, 'DTSTAMP:' + stamp(new Date()));
    if (localStart) L.push('DTSTART;TZID=' + ICS_TZ + ':' + localStart);
    else L.push('DTSTART:' + stamp(f.start));
    /* no DTEND and no DURATION when the metaobject has no end time: an
       invented length would be a claim the shop never made */
    if (f.end) {
      var localEnd = localStart ? stampLocalET(f.end) : null;
      L.push(localEnd ? ('DTEND;TZID=' + ICS_TZ + ':' + localEnd) : ('DTEND:' + stamp(f.end)));
    }
    if (f.rrule) L.push('RRULE:' + f.rrule);
    L.push('SUMMARY:' + icsEscape(f.title));
    if (f.desc) L.push('DESCRIPTION:' + icsEscape(f.desc));
    if (f.loc) L.push('LOCATION:' + icsEscape(f.loc));
    if (f.url) L.push('URL:' + icsEscape(f.url));
    L.push('END:VEVENT');
    L.push('END:VCALENDAR');
    var out = [];
    for (var i = 0; i < L.length; i++) out.push(fold(L[i]));
    return out.join('\r\n') + '\r\n';
  }

  function download(name, text) {
    var blob = null;
    try { blob = new Blob([text], { type: 'text/calendar;charset=utf-8' }); } catch (e) { blob = null; }
    if (blob && window.URL && window.URL.createObjectURL) {
      var url = window.URL.createObjectURL(blob);
      var a = doc.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      a.style.position = 'fixed';
      a.style.left = '-9999px';
      a.style.opacity = '0';
      doc.body.appendChild(a);
      a.click();
      /* revoking in the same turn cancels the download in some engines */
      setTimeout(function () {
        if (a.parentNode) a.parentNode.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 1500);
      return true;
    }
    try {
      window.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(text);
      return true;
    } catch (e2) { return false; }
  }

  function fieldsFor(btn) {
    var start = toDate(btn.getAttribute('data-ics-start'));
    if (!start) return null;
    var end = toDate(btn.getAttribute('data-ics-end'));
    if (end && end.getTime() <= start.getTime()) end = null;
    var title = (btn.getAttribute('data-ics-title') || '').trim();
    if (!title) return null;
    var rrule = (btn.getAttribute('data-ics-rrule') || '').trim().toUpperCase();
    /* only a page-supplied, well-formed rule ever reaches the file */
    if (rrule && !/^FREQ=[A-Z0-9;=,+\-:]*$/.test(rrule)) rrule = '';
    var host = (window.location && window.location.hostname) || 'topnotchtcg';
    var uid = (btn.getAttribute('data-ics-uid') || '').trim() ||
      (evSlug(title) + '-' + Math.floor(start.getTime() / 1000));
    if (uid.indexOf('@') === -1) uid = uid + '@' + host;
    return {
      title: title,
      start: start,
      end: end,
      rrule: rrule,
      desc: (btn.getAttribute('data-ics-desc') || '').trim(),
      loc: (btn.getAttribute('data-ics-loc') || '').trim(),
      url: (btn.getAttribute('data-ics-url') || '').trim(),
      uid: icsEscape(uid),
      file: (btn.getAttribute('data-ics-file') || '').trim() || (evSlug(title) + '.ics')
    };
  }

  btns.forEach(function (btn) {
    var f = fieldsFor(btn);
    if (!f) {
      /* no usable start or title: leave the control exactly as the page
         rendered it rather than wiring a button that would lie */
      return;
    }
    evFixButton(btn);
    /* stamp the canonical hook so the stylesheet has one selector to target */
    if (!btn.hasAttribute('data-ev-ics')) btn.setAttribute('data-ev-ics', '');
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var ok = download(f.file, build(f));
      if (ok) {
        evFlash(btn, 'Saved');
        evSay('Calendar file for ' + f.title + ' downloaded.', true);
      } else {
        evSay('This browser could not save the calendar file.', true);
      }
    });
  });
});

/* ---------- 4. timeline reveal ----------
   One observer for every [data-ev-reveal], unobserved the moment it lands.
   A non-interacting renderer (crawler, Lighthouse, social preview) never
   scrolls, so everything reveals after a beat unless a real visitor has
   already shown a sign of life - same contract as the engine's own reveal. */
ready(function () {
  var els = evSlice(doc.querySelectorAll('[data-ev-reveal]'));
  if (!els.length) return;

  function showAll() {
    for (var i = 0; i < els.length; i++) els[i].classList.add('ev-in');
  }
  if (!evMotion || !('IntersectionObserver' in window)) { showAll(); return; }

  var touched = false;
  ['scroll', 'pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'].forEach(function (evt) {
    window.addEventListener(evt, function () { touched = true; }, { passive: true, once: true });
  });
  setTimeout(function () { if (!touched) showAll(); }, 2400);

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('ev-in');
      io.unobserve(entry.target);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  var step = wideScreen ? 68 : 52;
  var cap = wideScreen ? 420 : 180;
  els.forEach(function (el) {
    var idx = 0;
    var sib = el.previousElementSibling;
    while (sib) {
      if (sib.hasAttribute('data-ev-reveal')) idx++;
      sib = sib.previousElementSibling;
    }
    el.style.setProperty('--evd', Math.min(idx * step, cap) + 'ms');
    io.observe(el);
  });
});

/* ---------- 5. share this event ----------
   [data-ev-share] optionally carrying the anchor (a full URL, a #hash, or a
   bare element id) plus data-share-title / data-share-text. Native share
   sheet where the browser has one, clipboard otherwise, and a plainly
   reported failure if neither is available. */
ready(function () {
  var btns = evSlice(doc.querySelectorAll('[data-ev-share]'));
  if (!btns.length) return;

  var origin = (window.location && window.location.origin) ||
    (window.location.protocol + '//' + window.location.host);

  function anchorUrl(btn) {
    var explicit = (btn.getAttribute('data-share-url') || '').trim();
    if (explicit) return explicit;
    var raw = (btn.getAttribute('data-ev-share') || '').trim();
    var base = origin + window.location.pathname;
    if (!raw || raw === 'true') return base;
    if (/^https?:/i.test(raw)) return raw;
    if (raw.charAt(0) === '#') return base + raw;
    return base + '#' + raw;
  }

  function legacyCopy(text) {
    try {
      var ta = doc.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      doc.body.appendChild(ta);
      ta.select();
      if (ta.setSelectionRange) ta.setSelectionRange(0, ta.value.length);
      var done = doc.execCommand ? doc.execCommand('copy') : false;
      doc.body.removeChild(ta);
      return !!done;
    } catch (e) { return false; }
  }

  function copy(url, btn, title) {
    function ok() {
      evFlash(btn, 'Link copied');
      evSay('Link to ' + title + ' copied to your clipboard.', true);
    }
    function fail() {
      evSay('This browser would not copy the link. The address is ' + url);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(ok, function () {
        if (legacyCopy(url)) ok(); else fail();
      });
      return;
    }
    if (legacyCopy(url)) ok(); else fail();
  }

  btns.forEach(function (btn) {
    evFixButton(btn);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var title = (btn.getAttribute('data-share-title') || btn.getAttribute('data-ics-title') || doc.title || '').trim();
      var url = anchorUrl(btn);
      var payload = { title: title, url: url };
      var text = (btn.getAttribute('data-share-text') || '').trim();
      if (text) payload.text = text;
      var canNative = !!navigator.share &&
        (typeof navigator.canShare !== 'function' || navigator.canShare(payload));
      if (canNative) {
        try {
          navigator.share(payload).then(function () {
            evSay('Shared ' + title + '.', true);
          }, function (err) {
            /* the visitor closing the share sheet is not a failure */
            if (err && err.name === 'AbortError') return;
            copy(url, btn, title);
          });
          return;
        } catch (shareErr) { /* fall through to the clipboard */ }
      }
      copy(url, btn, title);
    });
  });
});
  })();

  /* ==== events:events-chrome ==== */
  (function () {
/* ==== events:events-chrome ==== */
(function () {
  /* Live "in N days" chip on the homepage next-event band. The instant comes
     from the metaobject's OWN starts value, rendered server-side into
     data-evh-at as epoch seconds - nothing here invents a time, it only
     restates that same instant relative to the visitor's clock. The chip
     ships empty and hidden: no JS, a missing/unparseable stamp, or a start
     that is already behind us, and it simply never appears. The band's
     weekday + date + time line is the complete statement without it. */
  ready(function () {
    var chip = doc.querySelector('[data-evh-in]');
    if (!chip) return;
    var at = parseInt(chip.getAttribute('data-evh-at'), 10);
    if (!at || at < 0) return;

    function label(sec) {
      var d = Math.floor(sec / 86400);
      if (d >= 1) return 'In ' + d + (d === 1 ? ' day' : ' days');
      var h = Math.floor(sec / 3600);
      if (h >= 1) return 'In ' + h + (h === 1 ? ' hour' : ' hours');
      var m = Math.floor(sec / 60);
      if (m >= 1) return 'In ' + m + ' min';
      return '';
    }
    /* textContent, never innerHTML - and the empty string path clears the
       chip as well as hiding it, so :empty holds even if [hidden] is lost */
    function paint() {
      var txt = label(at - Math.floor(Date.now() / 1000));
      chip.textContent = txt;
      chip.hidden = !txt;
    }
    paint();
    /* a minute-scale readout needs no rAF: the shared ticker is for motion */
    setInterval(paint, 30000);
  });
})();
  })();

  /* ==== everypage:calendar ==== */
  (function () {
/* ==== events:calendar ==== */
/* The month grid on /pages/events (pass:calendar). Reads the data island the
   section emits ([data-evcal-data]: effective start/end from the SAME epochs the
   board rendered, so the two can never disagree), expands weekly entries in
   America/New_York through Intl (DST-correct by construction), and draws a
   Sun-Sat month grid plus a day panel into [data-evcal-cal]. The List / Calendar
   toggle ([data-evcal-view]) swaps the grid with the board's stream; list is the
   default and the no-JS state. Everything is built with DOM methods; nothing
   here invents a time, a price or a link - every value is from the island. The
   module bails, hiding its mount and toggle, whenever the browser cannot do
   timezone-aware Intl formatting: the list is complete without it. */

var evcMotion = !reduceMotion && doc.documentElement.classList.contains('motion-ok');
var EVC_TZ = 'America/New_York';
var EVC_STORE = 'tntcg:evview';
var EVC_WEEK_MS = 604800000;
var EVC_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
var EVC_DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
var EVC_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
/* how far the grid may page ahead: weekly entries would otherwise imply a
   schedule set in stone forever */
var EVC_MONTHS_AHEAD = 18;
/* America/New_York as the .ics needs it for a TZID-anchored weekly series */
var EVC_VTIMEZONE = [
  'BEGIN:VTIMEZONE', 'TZID:America/New_York',
  'BEGIN:DAYLIGHT', 'TZOFFSETFROM:-0500', 'TZOFFSETTO:-0400', 'TZNAME:EDT',
  'DTSTART:19700308T020000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU', 'END:DAYLIGHT',
  'BEGIN:STANDARD', 'TZOFFSETFROM:-0400', 'TZOFFSETTO:-0500', 'TZNAME:EST',
  'DTSTART:19701101T020000', 'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU', 'END:STANDARD',
  'END:VTIMEZONE'];

function evcNorm(v) {
  return String(v === null || v === undefined ? '' : v).trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
}
function evcPad(n) { return n < 10 ? '0' + n : String(n); }
function evcEl(tag, cls, text) {
  var el = doc.createElement(tag);
  if (cls) el.className = cls;
  if (text !== undefined && text !== null) el.textContent = text;
  return el;
}
function evcIcon(paths) {
  var svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  var p = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
  p.setAttribute('d', paths);
  svg.appendChild(p);
  return svg;
}

ready(function () {
  var mount = doc.querySelector('[data-evcal-cal]');
  if (!mount) return;
  var island = doc.querySelector('[data-evcal-data]');
  var toggles = Array.prototype.slice.call(doc.querySelectorAll('[data-evcal-view]'));
  var toggleGroup = toggles.length ? toggles[0].parentNode : null;
  var stream = doc.querySelector('[data-ev-list]');
  var solo = mount.hasAttribute('data-evcal-solo');
  var shopLoc = (mount.getAttribute('data-evcal-loc') || '').trim();

  /* no grid is better than a wrong one: without the data or the formatter
     the mount and its toggle leave, and the list stands as the whole page */
  function retire() {
    mount.hidden = true;
    if (toggleGroup) toggleGroup.hidden = true;
    if (stream) stream.hidden = false;
  }
  if (!island) { retire(); return; }

  var fmtParts, fmtTime, fmtShort;
  try {
    fmtParts = new Intl.DateTimeFormat('en-US', { timeZone: EVC_TZ, hour12: false, year: 'numeric',
      month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short' });
    fmtTime = new Intl.DateTimeFormat('en-US', { timeZone: EVC_TZ, hour: 'numeric', minute: '2-digit' });
    fmtShort = new Intl.DateTimeFormat('en-US', { timeZone: EVC_TZ, month: 'short', day: 'numeric' });
    if (typeof fmtParts.formatToParts !== 'function') throw new Error('no formatToParts');
    if (fmtParts.resolvedOptions().timeZone !== EVC_TZ) throw new Error('tz unsupported');
  } catch (e) { retire(); return; }

  function partsET(date) {
    var out = {};
    var ps = fmtParts.formatToParts(date);
    for (var i = 0; i < ps.length; i++) out[ps[i].type] = ps[i].value;
    var hh = parseInt(out.hour, 10);
    if (hh === 24) hh = 0; /* some engines print midnight as 24 under hour12:false */
    return { y: parseInt(out.year, 10), m: parseInt(out.month, 10), d: parseInt(out.day, 10),
      hh: hh, mm: parseInt(out.minute, 10), wd: EVC_DAYS_SHORT.indexOf(out.weekday) };
  }
  /* day keys are plain integers, YYYYMMDD, so they sort and compare directly */
  function keyOf(p) { return p.y * 10000 + p.m * 100 + p.d; }
  function keyParts(k) { return { y: Math.floor(k / 10000), m: Math.floor(k / 100) % 100, d: k % 100 }; }
  function keyAdd(k, n) {
    var p = keyParts(k);
    var d = new Date(Date.UTC(p.y, p.m - 1, p.d + n));
    return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
  }
  function weekdayOf(k) { var p = keyParts(k); return new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay(); }
  function daysIn(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

  function parseIso(s) {
    if (!s || typeof s !== 'string') return null;
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/);
    if (!m) return null;
    var d = new Date(Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10),
      parseInt(m[4], 10), parseInt(m[5], 10), parseInt(m[6], 10)));
    return isNaN(d.getTime()) ? null : d;
  }

  var raw;
  try { raw = JSON.parse(island.textContent || '[]'); } catch (e) { retire(); return; }
  if (!Array.isArray(raw)) { retire(); return; }
  var events = [];
  for (var r = 0; r < raw.length; r++) {
    var it = raw[r];
    if (!it || typeof it !== 'object') continue;
    var start = parseIso(it.start);
    var name = String(it.name || '').replace(/\s+/g, ' ').trim();
    if (!start || !name) continue;
    var end = parseIso(it.end);
    if (end && end.getTime() <= start.getTime()) end = null;
    var signup = String(it.signup || '').trim();
    if (!/^https?:\/\//i.test(signup)) signup = '';
    events.push({
      name: name, start: start, end: end,
      type: String(it.type || '').trim(),
      slug: evcNorm(it.slug || it.type),
      game: String(it.game || '').trim(),
      anchor: String(it.anchor || '').trim(),
      weekly: it.weekly === true,
      entry: String(it.entry || '').trim(),
      spots: it.spots === null || it.spots === undefined ? '' : String(it.spots).trim(),
      spotsrc: String(it.spotsrc || '').trim(),
      signup: signup,
      summary: String(it.summary || '').trim()
    });
  }
  if (!events.length) { retire(); return; }

  /* ---------- the live region: the page's own, transient writes only ---------- */
  var region = null;
  var sayTimer = null;
  var resting = null;
  function findRegion() {
    if (region) return region;
    region = doc.querySelector('[data-ev-status]');
    if (!region) {
      region = evcEl('p', 'visually-hidden');
      region.setAttribute('data-ev-status', '');
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      mount.parentNode.insertBefore(region, mount);
    }
    /* the sentence the page shipped is the one every transient returns to */
    resting = region.textContent;
    return region;
  }
  function restore() {
    if (region && resting !== null && !region.classList.contains('visually-hidden')) region.textContent = resting;
  }
  function say(msg) {
    findRegion();
    if (sayTimer) { clearTimeout(sayTimer); sayTimer = null; }
    /* the filter chips write a NEW standing sentence into the same region;
       adopt it so a later transient does not roll the count back */
    var cur = region.textContent;
    if (cur !== resting && /^Showing /.test(cur)) resting = cur;
    if (cur === msg) {
      /* identical text is not a change to AT: blank it, restate it a beat later -
         and still hand the standing sentence back afterwards */
      region.textContent = '';
      setTimeout(function () { region.textContent = msg; }, 60);
    } else {
      region.textContent = msg;
    }
    sayTimer = setTimeout(function () { if (region.textContent === msg) restore(); }, 5000);
  }

  /* ---------- occurrences: weekly entries expanded in Eastern time ----------
     The island's start for a weekly entry is already its current or next
     occurrence, so expansion runs forward from there. Each step is a whole
     week, then the Eastern minutes-of-day is put back to the original - the
     same correction the section applies server-side. */
  function occurrences(e, k1, k2) {
    var out = [];
    var dur = e.end ? e.end.getTime() - e.start.getTime() : 0;
    var k, ke;
    if (!e.weekly) {
      k = keyOf(partsET(e.start));
      ke = e.end ? keyOf(partsET(e.end)) : k;
      if (ke >= k1 && k <= k2) out.push({ start: e.start, end: e.end, k: k, ke: ke });
      return out;
    }
    var base = e.start.getTime();
    var op = partsET(e.start);
    var oMin = op.hh * 60 + op.mm;
    for (var n = 0; n < 320; n++) {
      var t = base + n * EVC_WEEK_MS;
      var cand = new Date(t);
      var p = partsET(cand);
      var dmin = oMin - (p.hh * 60 + p.mm);
      if (dmin > 720) dmin -= 1440; else if (dmin < -720) dmin += 1440;
      if (dmin) { cand = new Date(t + dmin * 60000); p = partsET(cand); }
      k = keyOf(p);
      if (k > k2) break;
      var endD = e.end ? new Date(cand.getTime() + dur) : null;
      ke = endD ? keyOf(partsET(endD)) : k;
      if (ke >= k1) out.push({ start: cand, end: endD, k: k, ke: ke });
    }
    return out;
  }

  /* the board's type filter is the calendar's too: read the pressed chip */
  function activeFilter() {
    var on = doc.querySelector('[data-ev-filter][aria-pressed="true"]');
    if (!on) return { value: '', label: '' };
    var v = evcNorm(on.getAttribute('data-ev-filter'));
    if (!v || v === 'all') return { value: '', label: '' };
    var label = '';
    for (var n = on.firstChild; n; n = n.nextSibling) if (n.nodeType === 3) label += n.nodeValue;
    return { value: v, label: label.replace(/\s+/g, ' ').trim() };
  }

  function monthData(y, m) {
    var first = y * 10000 + m * 100 + 1;
    var gridStart = keyAdd(first, -weekdayOf(first));
    var gridEnd = keyAdd(gridStart, 41);
    var filter = activeFilter();
    var days = {};
    var count = 0;
    for (var i = 0; i < events.length; i++) {
      var e = events[i];
      if (filter.value && e.slug !== filter.value) continue;
      var occs = occurrences(e, gridStart, gridEnd);
      for (var j = 0; j < occs.length; j++) {
        var o = occs[j];
        var k = o.k < gridStart ? gridStart : o.k;
        var last = o.ke > gridEnd ? gridEnd : o.ke;
        var steps = 0;
        while (k <= last && steps < 14) {
          (days[k] = days[k] || []).push({ e: e, o: o, cont: k !== o.k });
          k = keyAdd(k, 1);
          steps++;
        }
        var sp = keyParts(o.k);
        if (sp.y === y && sp.m === m) count++;
      }
    }
    for (var dk in days) {
      if (days.hasOwnProperty(dk)) {
        days[dk].sort(function (a, b) {
          var d = a.o.start.getTime() - b.o.start.getTime();
          return d || (a.e.name < b.e.name ? -1 : a.e.name > b.e.name ? 1 : 0);
        });
      }
    }
    return { y: y, m: m, gridStart: gridStart, gridEnd: gridEnd, days: days, count: count, filter: filter };
  }

  /* ---------- .ics: same escaping and folding rules as the board's button ---------- */
  function icsEscape(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r\n|\r|\n/g, '\\n');
  }
  function byteLen(s) {
    var n = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 0x80) n += 1;
      else if (c < 0x800) n += 2;
      else if (c >= 0xD800 && c <= 0xDBFF) { n += 4; i++; }
      else n += 3;
    }
    return n;
  }
  function fold(line) {
    if (byteLen(line) <= 75) return line;
    var parts = [], cur = '', n = 0, limit = 75;
    for (var i = 0; i < line.length; i++) {
      var ch = line.charAt(i);
      var code = line.charCodeAt(i);
      if (code >= 0xD800 && code <= 0xDBFF && i + 1 < line.length) { ch = line.substr(i, 2); i++; }
      var b = byteLen(ch);
      if (n + b > limit) { parts.push(cur); cur = ''; n = 0; limit = 74; }
      cur += ch;
      n += b;
    }
    parts.push(cur);
    return parts.join('\r\n ');
  }
  function stampUTC(d) {
    return d.getUTCFullYear() + evcPad(d.getUTCMonth() + 1) + evcPad(d.getUTCDate()) + 'T' +
      evcPad(d.getUTCHours()) + evcPad(d.getUTCMinutes()) + evcPad(d.getUTCSeconds()) + 'Z';
  }
  /* a weekly series is anchored to Eastern wall-clock time: a UTC DTSTART
     with RRULE would slide an hour at every DST change */
  function stampLocal(d) {
    var p = partsET(d);
    return p.y + evcPad(p.m) + evcPad(p.d) + 'T' + evcPad(p.hh) + evcPad(p.mm) + evcPad(d.getUTCSeconds());
  }
  var host = (window.location && window.location.hostname) || 'topnotchtcg';
  var pageUrl = (window.location && (window.location.origin || (window.location.protocol + '//' + window.location.host)) + window.location.pathname) || '';
  function vevent(e, o) {
    /* a weekly SERIES keeps one UID for life (the entry's anchor), so re-saving
       it from any week updates the series instead of stacking a duplicate;
       a one-off event is genuinely a different event on a different date */
    var uid = e.weekly
      ? icsEscape((e.anchor || evcNorm(e.name) || 'event') + '@' + host)
      : icsEscape((evcNorm(e.name) || 'event') + '-' + Math.floor(o.start.getTime() / 1000) + '@' + host);
    var L = ['BEGIN:VEVENT', 'UID:' + uid, 'DTSTAMP:' + stampUTC(new Date())];
    if (e.weekly) {
      L.push('DTSTART;TZID=' + EVC_TZ + ':' + stampLocal(o.start));
      if (o.end) L.push('DTEND;TZID=' + EVC_TZ + ':' + stampLocal(o.end));
      L.push('RRULE:FREQ=WEEKLY');
    } else {
      L.push('DTSTART:' + stampUTC(o.start));
      if (o.end) L.push('DTEND:' + stampUTC(o.end));
    }
    L.push('SUMMARY:' + icsEscape(e.name));
    if (e.summary) L.push('DESCRIPTION:' + icsEscape(e.summary));
    var loc = e.slug === 'tiktok-live' ? e.signup : shopLoc;
    if (loc) L.push('LOCATION:' + icsEscape(loc));
    if (e.anchor && pageUrl) L.push('URL:' + icsEscape(pageUrl + '#' + e.anchor));
    L.push('END:VEVENT');
    return L;
  }
  function buildIcs(items) {
    var L = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TopNotchTCG//Events//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH'];
    var weekly = false;
    for (var i = 0; i < items.length; i++) if (items[i].e.weekly) weekly = true;
    if (weekly) L = L.concat(EVC_VTIMEZONE);
    for (var j = 0; j < items.length; j++) L = L.concat(vevent(items[j].e, items[j].o));
    L.push('END:VCALENDAR');
    var out = [];
    for (var k = 0; k < L.length; k++) out.push(fold(L[k]));
    return out.join('\r\n') + '\r\n';
  }
  function download(name, text) {
    var blob = null;
    try { blob = new Blob([text], { type: 'text/calendar;charset=utf-8' }); } catch (e) { blob = null; }
    if (blob && window.URL && window.URL.createObjectURL) {
      var url = window.URL.createObjectURL(blob);
      var a = doc.createElement('a');
      a.href = url;
      a.download = name;
      a.rel = 'noopener';
      a.style.position = 'fixed';
      a.style.left = '-9999px';
      a.style.opacity = '0';
      doc.body.appendChild(a);
      a.click();
      setTimeout(function () {
        if (a.parentNode) a.parentNode.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 1500);
      return true;
    }
    try { window.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(text); return true; }
    catch (e2) { return false; }
  }
  function flash(btn, text) {
    var lab = btn.querySelector('[data-evcal-btn-label]');
    btn.classList.add('is-done');
    if (lab) {
      if (btn.evcLabelWas === undefined || btn.evcLabelWas === null) btn.evcLabelWas = lab.textContent;
      lab.textContent = text;
    }
    if (btn.evcFlashTimer) clearTimeout(btn.evcFlashTimer);
    btn.evcFlashTimer = setTimeout(function () {
      btn.classList.remove('is-done');
      if (lab && btn.evcLabelWas !== undefined && btn.evcLabelWas !== null) { lab.textContent = btn.evcLabelWas; btn.evcLabelWas = null; }
    }, 2400);
  }

  /* ---------- state + structure ---------- */
  var todayP = partsET(new Date());
  var todayKey = keyOf(todayP);
  var minIdx = todayP.y * 12 + (todayP.m - 1);
  var maxIdx = minIdx + EVC_MONTHS_AHEAD;
  var state = { y: todayP.y, m: todayP.m, sel: null, data: null, built: false, dirty: false, view: 'list' };
  var ui = {};
  var cells = [];

  function fade(el) {
    if (!evcMotion || !el || typeof el.animate !== 'function') return;
    try {
      el.animate([{ opacity: 0, transform: 'translate3d(0,6px,0)' }, { opacity: 1, transform: 'none' }],
        { duration: 240, easing: 'cubic-bezier(.16,1,.3,1)' });
    } catch (e) { /* decorative */ }
  }
  function monthName(y, m) { return EVC_MONTHS[m - 1] + ' ' + y; }
  function longDay(k) {
    var p = keyParts(k);
    var s = EVC_DAYS[weekdayOf(k)] + ', ' + EVC_MONTHS[p.m - 1] + ' ' + p.d;
    if (p.y !== todayP.y) s += ', ' + p.y;
    return s;
  }
  function countWord(n, label) {
    return n + ' ' + (label ? label + ' ' : '') + (n === 1 ? 'event' : 'events');
  }

  function build() {
    var head = evcEl('div', 'evcal-head');
    var nav = evcEl('div', 'evcal-nav');
    ui.prev = evcEl('button', 'evcal-nbtn');
    ui.prev.type = 'button';
    ui.prev.appendChild(evcIcon('M14.5 5.5 8 12l6.5 6.5'));
    ui.next = evcEl('button', 'evcal-nbtn');
    ui.next.type = 'button';
    ui.next.appendChild(evcIcon('M9.5 5.5 16 12l-6.5 6.5'));
    var tw = evcEl('div', 'evcal-title-wrap');
    ui.title = evcEl('h3', 'evcal-title');
    ui.title.id = 'evcal-title';
    ui.count = evcEl('p', 'evcal-count');
    tw.appendChild(ui.title);
    tw.appendChild(ui.count);
    nav.appendChild(ui.prev);
    nav.appendChild(tw);
    nav.appendChild(ui.next);
    var tools = evcEl('div', 'evcal-tools');
    ui.today = evcEl('button', 'evcal-tbtn', 'Today');
    ui.today.type = 'button';
    ui.today.setAttribute('aria-label', 'Go to today, ' + longDay(todayKey));
    ui.all = evcEl('button', 'evcal-tbtn evcal-all');
    ui.all.type = 'button';
    ui.all.appendChild(evcIcon('M3.5 5h17v15.5h-17zM3.5 9.5h17M8 3v4M16 3v4'));
    var allLab = evcEl('span', '', 'Add all to calendar');
    allLab.setAttribute('data-evcal-btn-label', '');
    ui.all.appendChild(allLab);
    ui.all.setAttribute('aria-label', 'Add all ' + countWord(events.length) + ' to your calendar');
    tools.appendChild(ui.today);
    tools.appendChild(ui.all);
    head.appendChild(nav);
    head.appendChild(tools);

    ui.body = evcEl('div', 'evcal-body');
    ui.grid = evcEl('div', 'evcal-grid');
    ui.grid.setAttribute('role', 'grid');
    ui.grid.setAttribute('aria-labelledby', 'evcal-title');
    var hrow = evcEl('div', 'evcal-row evcal-row--head');
    hrow.setAttribute('role', 'row');
    for (var h = 0; h < 7; h++) {
      var wd = evcEl('div', 'evcal-wd', EVC_DAYS_SHORT[h]);
      wd.setAttribute('role', 'columnheader');
      wd.setAttribute('aria-label', EVC_DAYS[h]);
      hrow.appendChild(wd);
    }
    ui.grid.appendChild(hrow);
    for (var rI = 0; rI < 6; rI++) {
      var row = evcEl('div', 'evcal-row');
      row.setAttribute('role', 'row');
      for (var c = 0; c < 7; c++) {
        var cell = evcEl('div', 'evcal-day');
        cell.setAttribute('role', 'gridcell');
        cell.setAttribute('tabindex', '-1');
        cell.setAttribute('aria-selected', 'false');
        row.appendChild(cell);
        cells.push(cell);
      }
      ui.grid.appendChild(row);
    }
    ui.panel = evcEl('section', 'evcal-panel');
    ui.panel.setAttribute('aria-labelledby', 'evcal-panel-h');
    ui.panelH = evcEl('h4', 'evcal-panel-h');
    ui.panelH.id = 'evcal-panel-h';
    ui.panelBody = evcEl('div', 'evcal-panel-body');
    ui.panel.appendChild(ui.panelH);
    ui.panel.appendChild(ui.panelBody);
    ui.body.appendChild(ui.grid);
    ui.body.appendChild(ui.panel);
    mount.appendChild(head);
    mount.appendChild(ui.body);

    ui.prev.addEventListener('click', function () { step(-1, true); });
    ui.next.addEventListener('click', function () { step(1, true); });
    ui.today.addEventListener('click', function () {
      go(todayP.y, todayP.m, true);
      select(todayKey, { focus: true, announce: true });
    });
    ui.all.addEventListener('click', function () {
      var items = [];
      for (var i = 0; i < events.length; i++) items.push({ e: events[i], o: { start: events[i].start, end: events[i].end } });
      if (download('topnotchtcg-events.ics', buildIcs(items))) {
        flash(ui.all, 'Saved');
        say('Calendar file with ' + countWord(items.length) + ' downloaded.');
      } else {
        say('This browser could not save the calendar file.');
      }
    });
    ui.grid.addEventListener('click', onGridClick);
    ui.grid.addEventListener('keydown', onGridKey);
    ui.panelBody.addEventListener('click', onPanelClick);

    state.built = true;
    go(state.y, state.m, false);
  }

  function clampIdx(idx) { return idx < minIdx ? minIdx : idx > maxIdx ? maxIdx : idx; }
  function step(delta, animate) {
    var idx = clampIdx(state.y * 12 + (state.m - 1) + delta);
    var y = Math.floor(idx / 12), m = (idx % 12) + 1;
    if (y === state.y && m === state.m) return;
    /* keep the day-of-month under the cursor where the new month has it */
    var keep = state.sel ? keyParts(state.sel).d : 1;
    go(y, m, animate);
    var dim = daysIn(y, m);
    var wantKey = y * 10000 + m * 100 + (keep > dim ? dim : keep);
    var wasFocused = doc.activeElement && ui.grid.contains(doc.activeElement);
    select(wantKey, { focus: wasFocused, announce: false });
  }

  /* draw a month; selection is re-resolved by the caller */
  function go(y, m, animate) {
    state.y = y; state.m = m;
    state.data = monthData(y, m);
    var d = state.data;
    ui.title.textContent = monthName(y, m);
    if (d.count === 0) {
      ui.count.textContent = d.filter.value
        ? 'No ' + d.filter.label + ' events in ' + monthName(y, m)
        : 'Nothing on the board in ' + monthName(y, m);
    } else {
      ui.count.textContent = countWord(d.count, d.filter.label);
    }
    var idx = y * 12 + (m - 1);
    var pIdx = idx - 1, nIdx = idx + 1;
    ui.prev.setAttribute('aria-label', 'Previous month, ' + monthName(Math.floor(pIdx / 12), (pIdx % 12) + 1));
    ui.next.setAttribute('aria-label', 'Next month, ' + monthName(Math.floor(nIdx / 12), (nIdx % 12) + 1));
    ui.prev.setAttribute('aria-disabled', idx <= minIdx ? 'true' : 'false');
    ui.next.setAttribute('aria-disabled', idx >= maxIdx ? 'true' : 'false');

    for (var i = 0; i < 42; i++) {
      var k = keyAdd(d.gridStart, i);
      var p = keyParts(k);
      var cell = cells[i];
      var list = d.days[k] || [];
      cell.textContent = '';
      cell.setAttribute('data-evcal-key', String(k));
      cell.className = 'evcal-day' +
        (p.m !== m ? ' is-out' : '') +
        (k === todayKey ? ' is-today' : '') +
        (k < todayKey ? ' is-past' : '') +
        (list.length ? ' has-ev' : '');
      var dn = evcEl('span', 'evcal-dn', String(p.d));
      cell.appendChild(dn);
      if (list.length) {
        var dots = evcEl('span', 'evcal-dots');
        dots.setAttribute('aria-hidden', 'true');
        var seen = [];
        for (var j = 0; j < list.length && seen.length < 3; j++) {
          var slug = list[j].e.slug;
          if (seen.indexOf(slug) > -1) continue;
          seen.push(slug);
          var dot = evcEl('i', 'evcal-dot');
          if (slug) dot.setAttribute('data-ev-type', slug);
          dots.appendChild(dot);
        }
        cell.appendChild(dots);
        var n = evcEl('span', 'evcal-n', String(list.length));
        n.setAttribute('aria-hidden', 'true');
        cell.appendChild(n);
      }
      cell.setAttribute('aria-label', longDay(k) + (k === todayKey ? ', today' : '') + ', ' +
        (list.length === 0 ? 'no events' : countWord(list.length)));
    }
    /* a selection outside the new grid is resolved: today, else the first
       day of the month with something on it, else the first of the month */
    if (state.sel === null || state.sel < d.gridStart || state.sel > d.gridEnd) {
      var pick = null;
      if (todayKey >= y * 10000 + m * 100 + 1 && todayKey <= y * 10000 + m * 100 + daysIn(y, m)) pick = todayKey;
      if (pick === null) {
        for (var q = 1; q <= daysIn(y, m); q++) {
          var qk = y * 10000 + m * 100 + q;
          if (d.days[qk] && d.days[qk].length) { pick = qk; break; }
        }
      }
      state.sel = pick === null ? y * 10000 + m * 100 + 1 : pick;
    }
    paintSelection();
    renderPanel(state.sel);
    if (animate) fade(ui.body);
  }

  function cellFor(k) {
    var i = k - 0;
    for (var c = 0; c < cells.length; c++) if (parseInt(cells[c].getAttribute('data-evcal-key'), 10) === i) return cells[c];
    return null;
  }
  function paintSelection() {
    for (var c = 0; c < cells.length; c++) {
      var on = parseInt(cells[c].getAttribute('data-evcal-key'), 10) === state.sel;
      cells[c].setAttribute('aria-selected', on ? 'true' : 'false');
      cells[c].setAttribute('tabindex', on ? '0' : '-1');
    }
  }
  function select(k, opts) {
    opts = opts || {};
    var p = keyParts(k);
    if (p.y !== state.y || p.m !== state.m) {
      /* a day from the neighbouring month: page there, if allowed */
      var idx = clampIdx(p.y * 12 + (p.m - 1));
      var y = Math.floor(idx / 12), m = (idx % 12) + 1;
      if (y !== state.y || m !== state.m) {
        state.sel = k;
        go(y, m, true);
      }
    }
    /* past the first or last month the grid may show: the day has no cell,
       so the selection (and the one tab stop) stays where it was */
    if (!state.data || k < state.data.gridStart || k > state.data.gridEnd) return;
    state.sel = k;
    paintSelection();
    renderPanel(k);
    var cell = cellFor(k);
    if (opts.focus && cell) cell.focus();
    if (opts.announce) {
      var list = (state.data && state.data.days[k]) || [];
      say(longDay(k) + ': ' + (list.length ? countWord(list.length) + '.' : 'nothing on the board.'));
    }
  }

  function timeLabel(o, k) {
    var s = fmtTime.format(o.start);
    if (!o.end) return s + ' ET';
    var e = fmtTime.format(o.end);
    if (o.ke === o.k) return s + ' \u2013 ' + e + ' ET';
    var sd = fmtShort.format(o.start), ed = fmtShort.format(o.end);
    if (k === o.k) return s + ' \u2013 ' + ed + ', ' + e + ' ET';
    return sd + ', ' + s + ' \u2013 ' + (k === o.ke ? e : ed + ', ' + e) + ' ET';
  }

  function renderPanel(k) {
    ui.panelH.textContent = longDay(k) + (k === todayKey ? ' (today)' : '');
    ui.panelBody.textContent = '';
    var list = (state.data && state.data.days[k]) || [];
    if (!list.length) {
      var none = evcEl('p', 'evcal-none', k < todayKey ? 'That day has passed.' : 'Nothing on the board this day.');
      ui.panelBody.appendChild(none);
      return;
    }
    var ol = evcEl('ol', 'evcal-plist');
    for (var i = 0; i < list.length; i++) {
      var e = list[i].e, o = list[i].o;
      var li = evcEl('li', 'evcal-item');
      if (e.slug) li.setAttribute('data-ev-type', e.slug);
      if (e.game) li.setAttribute('data-game', e.game);
      var top = evcEl('div', 'evcal-item-top');
      if (e.type) top.appendChild(evcEl('span', 'ev-tag ev-tag--sm', e.type));
      top.appendChild(evcEl('span', 'evcal-time', timeLabel(o, k)));
      if (list[i].cont) top.appendChild(evcEl('span', 'evcal-cont', 'Continues'));
      li.appendChild(top);
      li.appendChild(evcEl('h5', 'evcal-item-h', e.name));
      var meta = [];
      if (e.weekly) meta.push('Every week');
      if (e.entry) meta.push('Entry ' + e.entry);
      if (e.spots && !(e.spotsrc === 'stock' && e.spots === '0')) meta.push((e.spotsrc === 'stock' ? 'Seats left ' : 'Capacity ') + e.spots);
      if (meta.length) li.appendChild(evcEl('p', 'evcal-item-meta', meta.join(' \u00b7 ')));
      var acts = evcEl('div', 'evcal-item-acts');
      if (e.anchor && doc.getElementById(e.anchor)) {
        var jumpA = evcEl('a', 'evcal-act', 'On the board');
        jumpA.href = '#' + e.anchor;
        jumpA.setAttribute('data-evcal-jump', e.anchor);
        jumpA.setAttribute('aria-label', 'Details for ' + e.name + ' on the board');
        acts.appendChild(jumpA);
      }
      if (e.spots === '0') {
        acts.appendChild(evcEl('span', 'ev-full', 'Fully booked'));
      } else if (e.signup) {
        var go_ = evcEl('a', 'evcal-act evcal-act--go', 'Save your spot');
        go_.href = e.signup;
        go_.setAttribute('aria-label', 'Save your spot for ' + e.name);
        acts.appendChild(go_);
      }
      var ics = evcEl('button', 'evcal-act evcal-ics');
      ics.type = 'button';
      ics.setAttribute('data-evcal-ics', String(i));
      ics.setAttribute('aria-label', 'Add to calendar: ' + e.name + ', ' + longDay(o.k) +
        (e.weekly ? ', repeats weekly' : ''));
      /* the board's data-ics-* vocabulary, for anyone reading the DOM - except
         data-ics-start: title + start together is the events-motion module's
         wiring selector, and a button wired twice would download twice */
      ics.setAttribute('data-ics-title', e.name);
      ics.setAttribute('data-evcal-start', stampUTC(o.start));
      if (o.end) ics.setAttribute('data-ics-end', stampUTC(o.end));
      if (e.weekly) ics.setAttribute('data-ics-rrule', 'FREQ=WEEKLY');
      ics.appendChild(evcIcon('M3.5 5h17v15.5h-17zM3.5 9.5h17M8 3v4M16 3v4'));
      var icsLab = evcEl('span', '', e.weekly ? 'Add weekly series' : 'Add to calendar');
      icsLab.setAttribute('data-evcal-btn-label', '');
      ics.appendChild(icsLab);
      acts.appendChild(ics);
      li.appendChild(acts);
      ol.appendChild(li);
    }
    ui.panelBody.appendChild(ol);
  }

  function onGridClick(ev) {
    var t = ev.target;
    var cell = t && t.closest ? t.closest('.evcal-day') : null;
    if (!cell || !ui.grid.contains(cell)) return;
    var k = parseInt(cell.getAttribute('data-evcal-key'), 10);
    if (isNaN(k)) return;
    select(k, { focus: true, announce: true });
  }
  function onGridKey(ev) {
    var t = ev.target;
    var cell = t && t.closest ? t.closest('.evcal-day') : null;
    if (!cell) return;
    var k = parseInt(cell.getAttribute('data-evcal-key'), 10);
    if (isNaN(k)) return;
    var key = ev.key;
    var handled = true;
    switch (key) {
      case 'ArrowRight': select(keyAdd(k, 1), { focus: true }); break;
      case 'ArrowLeft': select(keyAdd(k, -1), { focus: true }); break;
      case 'ArrowDown': select(keyAdd(k, 7), { focus: true }); break;
      case 'ArrowUp': select(keyAdd(k, -7), { focus: true }); break;
      case 'Home': select(keyAdd(k, -weekdayOf(k)), { focus: true }); break;
      case 'End': select(keyAdd(k, 6 - weekdayOf(k)), { focus: true }); break;
      case 'PageUp': step(-1, true); break;
      case 'PageDown': step(1, true); break;
      case 'Enter': case ' ': case 'Spacebar': select(k, { focus: true, announce: true }); break;
      default: handled = false;
    }
    if (handled) ev.preventDefault();
  }
  function onPanelClick(ev) {
    var t = ev.target;
    var jumpA = t && t.closest ? t.closest('[data-evcal-jump]') : null;
    if (jumpA) {
      var anchor = jumpA.getAttribute('data-evcal-jump');
      var target = anchor ? doc.getElementById(anchor) : null;
      if (!target) return; /* a plain hash link is the honest fallback */
      ev.preventDefault();
      if (toggles.length) setView('list', false, false);
      try { target.scrollIntoView({ block: 'start', behavior: evcMotion ? 'smooth' : 'auto' }); }
      catch (e) { target.scrollIntoView(); }
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (e2) { target.focus(); }
      try { history.replaceState(null, '', '#' + anchor); } catch (e3) { /* URL is cosmetic here */ }
      return;
    }
    var icsBtn = t && t.closest ? t.closest('[data-evcal-ics]') : null;
    if (icsBtn) {
      ev.preventDefault();
      var list = (state.data && state.data.days[state.sel]) || [];
      var item = list[parseInt(icsBtn.getAttribute('data-evcal-ics'), 10)];
      if (!item) return;
      var file = (evcNorm(item.e.name) || 'event') + '.ics';
      if (download(file, buildIcs([{ e: item.e, o: item.o }]))) {
        flash(icsBtn, 'Saved');
        say('Calendar file for ' + item.e.name + ' downloaded.');
      } else {
        say('This browser could not save the calendar file.');
      }
    }
  }

  /* ---------- the board's filter chips drive the grid too ---------- */
  doc.addEventListener('click', function (ev) {
    var t = ev.target;
    var chip = t && t.closest ? t.closest('[data-ev-filter]') : null;
    if (!chip) return;
    /* the chip's own handler (registered earlier, on the element) has already
       moved aria-pressed by the time this bubbles; a tick makes that certain */
    setTimeout(function () {
      if (!state.built) return;
      if (mount.hidden) { state.dirty = true; return; }
      go(state.y, state.m, false);
    }, 0);
  });

  /* ---------- List / Calendar ---------- */
  function setView(v, remember, animate) {
    state.view = v;
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', toggles[i].getAttribute('data-evcal-view') === v ? 'true' : 'false');
    }
    if (v === 'cal') {
      if (!state.built) build();
      else if (state.dirty) { state.dirty = false; go(state.y, state.m, false); }
      if (stream) stream.hidden = true;
      mount.hidden = false;
      if (animate) { fade(mount); say('Calendar view. ' + ui.title.textContent + ', ' + ui.count.textContent.toLowerCase() + '.'); }
    } else {
      mount.hidden = true;
      if (stream) stream.hidden = false;
      if (animate) { fade(stream); say('List view.'); }
    }
    if (remember) { try { window.localStorage.setItem(EVC_STORE, v); } catch (e) { /* private mode */ } }
  }

  if (solo || !toggles.length) {
    build();
    mount.hidden = false;
    return;
  }
  for (var tI = 0; tI < toggles.length; tI++) {
    (function (btn) {
      if (btn.tagName === 'BUTTON' && !btn.getAttribute('type')) btn.setAttribute('type', 'button');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var v = btn.getAttribute('data-evcal-view') === 'cal' ? 'cal' : 'list';
        if (v === state.view) return;
        setView(v, true, true);
      });
    }(toggles[tI]));
  }
  var saved = null;
  try { saved = window.localStorage.getItem(EVC_STORE); } catch (e) { saved = null; }
  setView(saved === 'cal' ? 'cal' : 'list', false, false);
});
  })();

  /* ==== everypage:shop-pages ==== */
  (function () {
/* ==== shop-pages (2026-09-01): collection / search / collections-list ==== */
  var SHP_SORT_KEY = 'tntcg:shp-sort';
  var SHP_SORTS = ['price-ascending', 'price-descending', 'created-descending', 'title-ascending'];

  /* ---------- remembered sort: the URL is the truth ----------
     The collection section's inline fast-path already redirected a bare room
     URL to the remembered sort before first paint; this side RECORDS the choice
     from the URL on every load (toolbar, quick bar and pagination all feed one
     memory), forgets it on an explicit Featured ('manual'), and drops the
     polite "your last choice" note into the status region when the fast path
     fired. Storage failures (private mode, quota) are swallowed: no memory, no
     error, page unchanged. */
  ready(function () {
    var room = doc.querySelector('[data-shp-coll]');
    if (!room) return;
    var sort = null;
    try { sort = new URLSearchParams(location.search).get('sort_by'); } catch (err) { return; }
    try {
      if (sort === 'manual') localStorage.removeItem(SHP_SORT_KEY);
      else if (sort && SHP_SORTS.indexOf(sort) > -1) localStorage.setItem(SHP_SORT_KEY, sort);
    } catch (err) {}
    var applied = false;
    try {
      applied = sessionStorage.getItem('tntcg:shp-sort-applied') === '1';
      sessionStorage.removeItem('tntcg:shp-sort-applied');
    } catch (err) {}
    var note = doc.querySelector('[data-shp-note]');
    if (!applied || !note || !sort || sort === 'manual' || SHP_SORTS.indexOf(sort) < 0) return;
    var sel = doc.getElementById('sortsel');
    var opt = sel && sel.selectedIndex > -1 ? sel.options[sel.selectedIndex] : null;
    var label = opt ? opt.textContent : sort;
    var reset;
    try {
      reset = new URL(location.href);
      reset.searchParams.set('sort_by', 'manual');
      reset.searchParams.delete('page');
    } catch (err) { return; }
    /* textContent + createElement only - the label is the select's own option text */
    note.appendChild(doc.createTextNode(' · sorted by your last choice (' + label + ') — '));
    var a = doc.createElement('a');
    a.href = reset.pathname + reset.search;
    a.textContent = 'reset to Featured';
    note.appendChild(a);
    note.hidden = false;
  });

  /* ---------- mobile quick bar (collection pages, <=860px) ----------
     Ships hidden in the section; shown once the toolbar has scrolled under the
     sticky header, docked to the header's live bottom edge. Show/hide is a
     direct rect check on the passive scroll listener, rAF-coalesced - a 0-height
     IntersectionObserver sentinel misses fast flicks and never fires. Filters
     opens the CSS disclosure and scrolls to it; on coarse pointers the sort
     select submits its own form on change (the native picker's confirm is the
     intent) and the Go button retires; fine pointers keep Go. */
  ready(function () {
    var bar = doc.querySelector('[data-shp-bar]');
    if (!bar || wideScreen) return;
    var toolbar = doc.querySelector('.coll-toolbar');
    if (!toolbar) return;
    var header = doc.querySelector('header');
    var cb = doc.getElementById('fltoggle');
    var panel = doc.getElementById('shp-filters');
    var fbtn = bar.querySelector('[data-shp-filters]');
    var sel = bar.querySelector('[data-shp-bar-sort]');
    var go = bar.querySelector('[data-shp-bar-go]');

    if (fbtn) {
      if (!cb || !panel) {
        fbtn.hidden = true;
      } else {
        var syncExpanded = function () { fbtn.setAttribute('aria-expanded', cb.checked ? 'true' : 'false'); };
        cb.addEventListener('change', syncExpanded);
        syncExpanded();
        fbtn.addEventListener('click', function () {
          if (!cb.checked) {
            cb.checked = true;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
          /* land the panel just under header + bar, then hand focus to its first control */
          var pad = (header ? header.getBoundingClientRect().bottom : 0) + bar.offsetHeight + 12;
          var y = panel.getBoundingClientRect().top + window.scrollY - pad;
          window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' });
          var first = panel.querySelector('a, input, button, select');
          if (first) {
            try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
          }
        });
      }
    }
    if (sel && !finePointer) {
      if (go) go.hidden = true;
      sel.addEventListener('change', function () { bar.submit(); });
    }

    var on = false, queued = false, lastTop = -1;
    function measure() {
      queued = false;
      var hb = header ? header.getBoundingClientRect().bottom : 0;
      var want = toolbar.getBoundingClientRect().bottom < hb && window.scrollY > 0;
      if (want) {
        var top = Math.max(0, Math.round(hb));
        if (top !== lastTop) { lastTop = top; bar.style.setProperty('--shp-top', top + 'px'); }
      }
      if (want === on) return;
      on = want;
      if (want) { bar.hidden = false; bar.classList.add('is-on'); }
      else { bar.classList.remove('is-on'); bar.hidden = true; }
    }
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    measure();
  });

  /* ---------- search: highlight the query inside result titles ----------
     Reads the escaped terms the section stamps on [data-shp-terms], folds
     accents per character (so "pokemon" lights up "Pokemon" with an accent -
     Shopify's own matcher already ignored it), and wraps each hit in a
     <mark> with splitText + createElement. Text content is never rewritten,
     so titles read exactly as before for screen readers and copy-paste. */
  ready(function () {
    var host = doc.querySelector('[data-shp-terms]');
    if (!host) return;
    var canFold = typeof ''.normalize === 'function';
    function foldChar(ch) {
      if (!canFold) return ch.toLowerCase();
      var d = ch.normalize('NFD');
      return (d.charAt(0) || ch).toLowerCase();
    }
    function fold(str) {
      var out = '';
      for (var i = 0; i < str.length; i++) out += foldChar(str.charAt(i));
      return out; /* same length as str: one folded char per UTF-16 unit */
    }
    var raw = fold(host.getAttribute('data-shp-terms') || '');
    var seen = {}, terms = [];
    raw.split(/[\s"'*+,;:()\[\]{}]+/).forEach(function (t) {
      if (t.length >= 2 && !seen[t]) { seen[t] = 1; terms.push(t); }
    });
    if (!terms.length) return;
    terms.sort(function (a, b) { return b.length - a.length; });

    function wrapMatches(node) {
      var lower = fold(node.nodeValue);
      var best = -1, bestLen = 0;
      for (var k = 0; k < terms.length; k++) {
        var idx = lower.indexOf(terms[k]);
        if (idx > -1 && (best === -1 || idx < best)) { best = idx; bestLen = terms[k].length; }
      }
      if (best === -1) return;
      var hit = node.splitText(best);
      var rest = hit.splitText(bestLen);
      var mark = doc.createElement('mark');
      mark.className = 'shp-hl';
      hit.parentNode.insertBefore(mark, hit);
      mark.appendChild(hit);
      wrapMatches(rest);
    }
    var targets = host.querySelectorAll('.relic-title, .search-pages a');
    for (var i = 0; i < targets.length; i++) {
      var walker = doc.createTreeWalker(targets[i], NodeFilter.SHOW_TEXT, null);
      var nodes = [], n;
      while ((n = walker.nextNode())) nodes.push(n);
      for (var j = 0; j < nodes.length; j++) wrapMatches(nodes[j]);
    }
  });
  })();

  /* ==== everypage:pdp-cart ==== */
  (function () {
/* ==== everything:pdp-cart ==== */
/* PDP + cart pass. Uses the engine's doc / ready / finePointer only. Everything
   here is DOM-method construction (no innerHTML), same-origin fetch only, and
   every control it wires exists solely inside html.motion-ok chrome, so a page
   without the engine never shows a control that does nothing. */

/* ---------- recently viewed: the fleet's ONE implementation ----------
   Contract: localStorage "tntcg:recent" holds a JSON array of product handles,
   newest first, max 12, written on every PDP view. Any [data-recent-rail]
   mount (emitted empty + hidden by any pass) is filled from
   /products/<handle>.js - real title, price, image, availability, link - and
   unhidden only when at least one product came back. The current product is
   skipped; a handle that 404s is pruned; a network failure leaves storage
   alone and renders nothing. */
(function () {
  var KEY = 'tntcg:recent', CAP = 12, SHOW = 8;
  /* handles are lowercase ascii + hyphens on this store; the allowlist keeps
     tampered storage from steering the same-origin fetch or the card href */
  var HANDLE_OK = /^[a-z0-9][a-z0-9-]*$/;
  function load() {
    var arr;
    try { arr = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (err) { return []; }
    if (Object.prototype.toString.call(arr) !== '[object Array]') return [];
    var out = [];
    for (var i = 0; i < arr.length && out.length < CAP; i++) {
      if (typeof arr[i] === 'string' && HANDLE_OK.test(arr[i]) && out.indexOf(arr[i]) === -1) out.push(arr[i]);
    }
    return out;
  }
  function save(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP))); } catch (err) {}
  }
  function currentHandle() {
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m && HANDLE_OK.test(m[1]) ? m[1] : '';
  }
  /* record on every PDP view (the pathname is the source of truth; the
     collection-scoped /collections/x/products/y form matches too) */
  ready(function () {
    var h = currentHandle();
    if (!h) return;
    var list = load(), next = [h];
    for (var i = 0; i < list.length; i++) if (list[i] !== h) next.push(list[i]);
    save(next);
  });

  function money(cents) {
    var s = (cents / 100).toFixed(2);
    return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  /* same lockup shape as snippets/tntcg-fant-price.liquid: SR-only plain
     price, aria-hidden styled lockup */
  function priceNode(cents) {
    var m = money(cents), parts = m.slice(1).split('.');
    var wrap = doc.createElement('span'); wrap.className = 'pr';
    var plk = doc.createElement('span'); plk.className = 'plk';
    var sr = doc.createElement('span'); sr.className = 'visually-hidden'; sr.textContent = m;
    var vis = doc.createElement('span'); vis.setAttribute('aria-hidden', 'true');
    var cur = doc.createElement('span'); cur.className = 'cur'; cur.textContent = '$';
    vis.appendChild(cur);
    vis.appendChild(doc.createTextNode(parts[0]));
    if (parts[1] && parts[1] !== '00') {
      var sup = doc.createElement('sup'); sup.className = 'cents'; sup.textContent = parts[1];
      vis.appendChild(sup);
    }
    plk.appendChild(sr); plk.appendChild(vis); wrap.appendChild(plk);
    return wrap;
  }
  /* per-game accent scope: the fleet-wide detection recipe (card snippet) */
  function gameOf(p) {
    var probe = (String(p.title || '') + ' ' + String(p.vendor || '')).toLowerCase();
    if (probe.indexOf('kayou') > -1 || probe.indexOf('naruto') > -1) return 'kayou';
    if (probe.indexOf('lorcana') > -1) return 'lorcana';
    if (probe.indexOf('final fantasy') > -1) return 'ff';
    if (p.type === 'Card Supplies') return 'supplies';
    if (probe.indexOf('pokemon') > -1 || probe.indexOf('pokémon') > -1) return 'pokemon';
    if (p.vendor === 'TopNotchTCG' && String(p.type || '').toLowerCase().indexOf('sealed') > -1) return 'pokemon';
    return '';
  }
  /* only the store's own CDN, sized like the Liquid rendition (width=360) */
  function imgUrl(src) {
    if (typeof src !== 'string') return '';
    if (src.indexOf('//cdn.shopify.com/') !== 0 && src.indexOf('https://cdn.shopify.com/') !== 0) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=360';
  }
  function card(h, p) {
    var a = doc.createElement('a');
    var avail = !!p.available;
    /* holo-edge only when buyable - the sold-out state stays quiet */
    a.className = 'relic' + (avail ? ' holo-edge' : '');
    a.href = '/products/' + h + (avail ? '' : '#notify');
    a.setAttribute('data-handle', h);
    var g = gameOf(p);
    if (g) a.setAttribute('data-game', g);
    var src = imgUrl(p.featured_image);
    var ph = doc.createElement('div'); ph.className = 'ph' + (src ? ' lit' : '');
    if (src) {
      var img = doc.createElement('img');
      img.src = src; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
      img.width = 360; img.height = 360; /* nominal box; .ph aspect-ratio owns layout */
      ph.appendChild(img);
    }
    var full = String(p.title || h);
    var t = doc.createElement('h3'); t.className = 'relic-title'; t.title = full;
    t.textContent = full.split(' (')[0];
    var row = doc.createElement('div'); row.className = 'price-row';
    var cents = (p.price_varies && typeof p.price_min === 'number') ? p.price_min : p.price;
    if (typeof cents === 'number' && cents >= 0) row.appendChild(priceNode(cents));
    var st = doc.createElement('span'); st.className = 'stock ' + (avail ? 'in' : 'out');
    st.textContent = avail ? 'In stock' : 'Sold out';
    row.appendChild(st);
    a.appendChild(ph); a.appendChild(t); a.appendChild(row);
    return a;
  }
  function fill(mount, live, idx) {
    if (mount.firstElementChild) return; /* another pass already filled it */
    mount.classList.add('pdc-recent');
    var hid = 'pdc-recent-h' + idx;
    var sec = doc.createElement('section'); sec.setAttribute('aria-labelledby', hid);
    /* the renderer supplies the page gutter; a mount another pass placed
       inside a .wrap already has one */
    if (!mount.closest('.wrap')) sec.className = 'wrap';
    var head = doc.createElement('div'); head.className = 'sect-head';
    var hin = doc.createElement('div');
    var k = doc.createElement('span'); k.className = 'kicker'; k.textContent = 'Recently viewed';
    var h2 = doc.createElement('h2'); h2.id = hid;
    h2.appendChild(doc.createTextNode('Pick up where you '));
    var em = doc.createElement('em'); em.textContent = 'left off'; h2.appendChild(em);
    hin.appendChild(k); hin.appendChild(h2); head.appendChild(hin);
    var rail = doc.createElement('div'); rail.className = 'rail pdc-recent-rail';
    for (var i = 0; i < live.length; i++) rail.appendChild(card(live[i].h, live[i].p));
    sec.appendChild(head); sec.appendChild(rail);
    mount.appendChild(sec);
    mount.hidden = false;
  }
  ready(function () {
    var mounts = doc.querySelectorAll('[data-recent-rail]');
    if (!mounts.length || !window.fetch || !window.Promise) return;
    var cur = currentHandle();
    var list0 = load().filter(function (h) { return h !== cur; });
    if (!list0.length) return;
    /* on the cart page a product already in the cart is not something to
       "pick up" - drop those handles before the rail is built */
    var onCart = /^\/cart(\/|$)/.test(window.location.pathname || '');
    var pre = onCart
      ? fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (c) {
            var inCart = {};
            if (c && c.items) for (var i = 0; i < c.items.length; i++) inCart[c.items[i].handle] = true;
            return inCart;
          })
          .catch(function () { return {}; })
      : Promise.resolve({});
    pre.then(function (inCart) {
    var list = list0.filter(function (h) { return !inCart[h]; }).slice(0, SHOW);
    if (!list.length) return;
    var gone = [];
    Promise.all(list.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (r.status === 404) { gone.push(h); return null; }
          return r.ok ? r.json() : null;
        })
        .then(function (p) { return (p && typeof p === 'object' && p.title) ? { h: h, p: p } : null; })
        .catch(function () { return null; });
    })).then(function (rows) {
      if (gone.length) save(load().filter(function (h) { return gone.indexOf(h) === -1; }));
      var live = rows.filter(Boolean);
      if (!live.length) return; /* nothing real to show = mounts stay hidden */
      for (var i = 0; i < mounts.length; i++) fill(mounts[i], live, i);
    });
    });
  });
})();

/* ---------- PDP share: the OS share sheet on touch-primary devices (phones,
   tablets - where it is the native idiom), clipboard copy on fine-pointer
   desktops (Windows Chrome also exposes navigator.share, but a system dialog
   is not what a desktop shopper expects from "Share"). Result announced in
   the role=status span beside the button. ---------- */
(function () {
  ready(function () {
    var btn = doc.querySelector('[data-pdc-share]');
    if (!btn) return;
    var status = doc.querySelector('[data-pdc-share-status]');
    var url = btn.getAttribute('data-pdc-url') || location.href;
    var title = btn.getAttribute('data-pdc-title') || doc.title;
    var useSheet = !!navigator.share && !finePointer;
    var clearT = null;
    function say(msg) {
      if (!status) return;
      /* clear-then-set so a repeated identical message is re-announced */
      status.textContent = '';
      setTimeout(function () { status.textContent = msg; }, 30);
      clearTimeout(clearT);
      clearT = setTimeout(function () { status.textContent = ''; }, 6000);
    }
    function legacyCopy() {
      var ta = doc.createElement('textarea');
      ta.value = url; ta.setAttribute('readonly', ''); ta.setAttribute('aria-hidden', 'true');
      ta.style.position = 'fixed'; ta.style.opacity = '0'; ta.style.pointerEvents = 'none';
      doc.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = doc.execCommand('copy'); } catch (err) { ok = false; }
      ta.remove();
      say(ok ? 'Link copied to your clipboard.' : 'Copy this link: ' + url);
    }
    function copy() {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { say('Link copied to your clipboard.'); }, legacyCopy);
      } else { legacyCopy(); }
    }
    btn.addEventListener('click', function () {
      if (useSheet) {
        navigator.share({ title: title, text: title, url: url })
          .then(function () { say('Shared.'); })
          .catch(function (err) {
            /* the visitor closed the sheet: say nothing. Any other failure
               (no share target, unsupported data) falls back to copying. */
            if (err && err.name === 'AbortError') return;
            copy();
          });
        return;
      }
      copy();
    });
  });
})();

/* ---------- cart: order note saves as you type (the textarea's name="note"
   also rides the native cart POST on Checkout, JS or not) ---------- */
(function () {
  ready(function () {
    var field = doc.querySelector('[data-pdc-note-field]');
    if (!field || !window.fetch) return;
    var status = doc.querySelector('[data-pdc-note-status]');
    var t = null, last = field.value, inflight = false, dirty = false;
    function tell(msg) { if (status) status.textContent = msg; }
    function save() {
      var val = field.value;
      if (val === last) return;
      if (inflight) { dirty = true; return; }
      inflight = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: val })
      }).then(function (r) {
        if (!r.ok) throw new Error('note not saved');
        return r.json();
      }).then(function () {
        last = val;
        tell(val ? 'Note saved.' : 'Note cleared.');
      }).catch(function () {
        tell('Could not save yet - your note still goes through with checkout.');
      }).then(function () {
        inflight = false;
        if (dirty) { dirty = false; save(); }
      });
    }
    field.addEventListener('input', function () {
      clearTimeout(t);
      tell('');
      t = setTimeout(save, 700);
    });
    field.addEventListener('blur', function () { clearTimeout(t); save(); });
  });
})();

/* ---------- cart: sticky mobile total + Checkout. Shows only while the real
   manifest Checkout button is off-screen (<861px); the button is a click
   proxy onto that real submit so the shipped pending-update guard and the
   native name=checkout POST both still happen. Visual + a11y state are applied
   together, so no code path can show a bar that is still inert. ---------- */
(function () {
  ready(function () {
    var bar = doc.querySelector('[data-pdc-cartbar]');
    if (!bar) return;
    var real = doc.querySelector('form[action="/cart"] button[name="checkout"]');
    if (!real) { bar.remove(); return; }
    var total = doc.querySelector('[data-cart-total]');
    var mirror = bar.querySelector('[data-pdc-bar-total]');
    function sync() {
      if (total && mirror && mirror.textContent !== total.textContent) mirror.textContent = total.textContent;
    }
    sync();
    if (total && window.MutationObserver) {
      new MutationObserver(sync).observe(total, { childList: true, characterData: true, subtree: true });
    }
    var shown = null; /* null forces the first apply() to stamp a11y state */
    function apply(want) {
      if (want === shown) return;
      shown = want;
      bar.classList.toggle('show', want);
      bar.toggleAttribute('inert', !want);
      bar.setAttribute('aria-hidden', want ? 'false' : 'true');
    }
    var narrow = window.matchMedia('(max-width: 860px)');
    function check() {
      if (!narrow.matches || !doc.querySelector('.cart-row')) { apply(false); return; }
      var r = real.getBoundingClientRect();
      apply(r.bottom < 0 || r.top > window.innerHeight);
    }
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
    var go = bar.querySelector('[data-pdc-bar-checkout]');
    if (go) go.addEventListener('click', function () { real.click(); });
  });
})();
  })();

  /* ==== everypage:editorial ==== */
  (function () {
/* ==== editorial:edt ====
   Article share button + generic-page table of contents (2026-09-01).
   DOM methods only; both controls ship hidden/empty and only appear once this
   code has made them work. */

/* ---- share this post: [data-edt-share] ----
   Native share sheet where the browser has one, clipboard otherwise, and a plain
   report either way into the sibling [data-edt-share-say] live region. */
ready(function () {
  var btns = Array.prototype.slice.call(doc.querySelectorAll('[data-edt-share]'));
  if (!btns.length) return;

  function say(btn, msg) {
    var row = btn.parentElement;
    var out = row && row.querySelector('[data-edt-share-say]');
    if (!out) return;
    /* clear first so an identical repeat message is announced again */
    out.textContent = '';
    out.textContent = msg;
  }

  function legacyCopy(text) {
    try {
      var ta = doc.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      doc.body.appendChild(ta);
      ta.select();
      if (ta.setSelectionRange) ta.setSelectionRange(0, ta.value.length);
      var done = doc.execCommand ? doc.execCommand('copy') : false;
      doc.body.removeChild(ta);
      return !!done;
    } catch (e) { return false; }
  }

  btns.forEach(function (btn) {
    var label = btn.querySelector('.edt-share-l');
    var orig = label ? label.textContent : '';
    var timer = null;

    function flash(txt) {
      if (!label) return;
      label.textContent = txt;
      btn.classList.add('is-done');
      clearTimeout(timer);
      timer = setTimeout(function () {
        label.textContent = orig;
        btn.classList.remove('is-done');
      }, 2600);
    }

    function copy(url, title) {
      function ok() {
        flash('Link copied');
        say(btn, 'Link to ' + title + ' copied to your clipboard.');
      }
      function fail() {
        say(btn, 'This browser would not copy the link. The address is ' + url);
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(ok, function () {
          if (legacyCopy(url)) ok(); else fail();
        });
        return;
      }
      if (legacyCopy(url)) ok(); else fail();
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var title = (btn.getAttribute('data-share-title') || doc.title || '').trim();
      var url = (btn.getAttribute('data-share-url') || window.location.href).trim();
      var payload = { title: title, url: url };
      var canNative = !!navigator.share &&
        (typeof navigator.canShare !== 'function' || navigator.canShare(payload));
      if (canNative) {
        try {
          navigator.share(payload).then(function () {
            say(btn, 'Shared ' + title + '.');
          }, function (err) {
            /* closing the share sheet is not a failure */
            if (err && err.name === 'AbortError') return;
            copy(url, title);
          });
          return;
        } catch (shareErr) { /* fall through to the clipboard */ }
      }
      copy(url, title);
    });
  });
});

/* ---- table of contents: [data-edt-page] > [data-edt-toc] + [data-edt-prose] ----
   Built from the prose's real h2 headings; shows only with three or more. Headings
   without an id get a unique slug id. The current section is tracked with one
   IntersectionObserver on the headings plus scrollend where supported
   (aria-current="location"); no per-frame scroll handler. */
ready(function () {
  var wrap = doc.querySelector('[data-edt-page]');
  if (!wrap) return;
  var toc = wrap.querySelector('[data-edt-toc]');
  var list = wrap.querySelector('[data-edt-toc-list]');
  var prose = wrap.querySelector('[data-edt-prose]');
  if (!toc || !list || !prose) return;

  var heads = Array.prototype.slice.call(prose.querySelectorAll('h2')).filter(function (h) {
    return (h.textContent || '').trim().length > 0;
  });
  if (heads.length < 3) return;

  function slug(text) {
    var s = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    return s || 'section';
  }

  var links = [];
  heads.forEach(function (h) {
    var text = h.textContent.trim();
    if (!h.id) {
      var base = slug(text), id = base, n = 2;
      while (doc.getElementById(id)) { id = base + '-' + n; n++; }
      h.id = id;
    }
    var li = doc.createElement('li');
    var a = doc.createElement('a');
    a.href = '#' + h.id;
    a.textContent = text;
    li.appendChild(a);
    list.appendChild(li);
    links.push(a);
  });

  toc.hidden = false;
  wrap.classList.add('edt-has-toc');

  var current = -1;
  function setCurrent(i) {
    if (i === current) return;
    if (current > -1) links[current].removeAttribute('aria-current');
    links[i].setAttribute('aria-current', 'location');
    current = i;
  }
  /* the section whose heading is the last one above the reading line */
  function pick() {
    var line = window.innerHeight * 0.3;
    var idx = 0;
    for (var i = 0; i < heads.length; i++) {
      if (heads[i].getBoundingClientRect().top <= line) idx = i;
    }
    setCurrent(idx);
  }
  pick();
  links.forEach(function (a, i) {
    a.addEventListener('click', function () { setCurrent(i); });
  });
  /* observe the top 40% of the viewport: any heading entering or leaving that
     band re-picks, and scrollend (where supported) settles fast jumps */
  window.addEventListener('scrollend', pick, { passive: true });
  if (!('IntersectionObserver' in window)) return;
  var io = new IntersectionObserver(function () { pick(); }, {
    rootMargin: '0px 0px -60% 0px', threshold: [0, 1]
  });
  heads.forEach(function (h) { io.observe(h); });
});
  })();

  /* ==== everypage:global-ux ==== */
  (function () {
/* ==== global-ux:gux (2026-09-01) ====
   Skip-link landing, back-to-top, keyboard-shortcuts dialog. Markup for the
   last two ships in snippets/tntcg-fant-chrome-bottom.liquid; this layer only
   toggles state on it. DOM methods only, no fetch, compositor-only motion. */
ready(function () {
  function isTyping(t) {
    return !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''));
  }
  function focusQuiet(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }

  /* ---------- skip link lands ON main ----------
     The native jump sets the sequential-focus start point but leaves focus on
     body, so the next Tab could still land in the header. Focus main itself
     (tabindex=-1, ring suppressed in CSS) once the hash jump has happened;
     preventScroll keeps the browser's own scroll-margin-aware jump. */
  var skip = doc.querySelector('.skip-to-content-link');
  var main = doc.getElementById('MainContent');
  if (skip && main) {
    skip.addEventListener('click', function () {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      setTimeout(function () { focusQuiet(main); }, 0);
    });
  }

  /* ---------- back to top ----------
     Shows after ~2 viewports of scroll, hides again near the top. [hidden] is
     the tab-order gate: removed before the reveal class lands, restored after
     the exit transition, so an invisible button is never focusable. */
  var topBtn = doc.querySelector('.gux-top');
  if (topBtn) {
    var shown = false, hideTimer = null;
    function paintTop() {
      var want = window.scrollY > window.innerHeight * 2;
      if (want === shown) return;
      shown = want;
      if (want) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        topBtn.hidden = false;
        void topBtn.offsetWidth; /* commit display before the class flips so the transition runs */
        topBtn.classList.add('on');
      } else {
        topBtn.classList.remove('on');
        hideTimer = setTimeout(function () {
          hideTimer = null;
          if (!shown) topBtn.hidden = true;
        }, reduceMotion ? 0 : 320);
      }
    }
    window.addEventListener('scroll', paintTop, { passive: true });
    window.addEventListener('resize', paintTop, { passive: true });
    paintTop();
    topBtn.addEventListener('click', function () {
      window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      /* the header is sticky, so the logo is on screen at any scroll position:
         focus it without a competing scroll so the glide home is not cut short */
      focusQuiet(doc.querySelector('header .logo'));
    });
  }

  /* ---------- keyboard shortcuts dialog ----------
     "?" toggles it (never while typing, never over the search overlay, the haul
     drawer or another <dialog>); the footer button opens it; showModal()
     supplies the focus trap and Escape; backdrop click closes. Focus returns to
     whatever opened it. */
  var dlg = doc.querySelector('.gux-help');
  if (dlg && typeof dlg.showModal === 'function') {
    var lastFocus = null;
    function otherSurfaceOpen() {
      var haul = doc.getElementById('tntcg-haul');
      var seek = doc.querySelector('.seek-overlay');
      return (haul && haul.classList.contains('open')) ||
        (seek && seek.classList.contains('open')) ||
        !!doc.querySelector('dialog[open]');
    }
    function openHelp(from) {
      if (dlg.open || otherSurfaceOpen()) return;
      lastFocus = from && from !== doc.body ? from : null;
      try { dlg.showModal(); } catch (err) { lastFocus = null; }
    }
    dlg.addEventListener('close', function () {
      var back = lastFocus;
      lastFocus = null;
      if (back && doc.contains(back)) focusQuiet(back);
    });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); }); /* backdrop */
    var closers = dlg.querySelectorAll('[data-gux-help-close]');
    for (var c = 0; c < closers.length; c++) closers[c].addEventListener('click', function () { dlg.close(); });
    var openers = doc.querySelectorAll('[data-gux-help-open]');
    for (var o = 0; o < openers.length; o++) {
      openers[o].addEventListener('click', function (e) { openHelp(e.currentTarget); });
    }
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (dlg.open) { e.preventDefault(); dlg.close(); return; }
      if (isTyping(e.target) || otherSurfaceOpen()) return;
      e.preventDefault();
      openHelp(doc.activeElement);
    });
  }
});
  })();

  /* ==== events:game-worlds motion ====
     Living hero: pointer parallax on the art (compositor-only translate), a
     magnetic sign-up button, and a handful of CSS-driven sparks. Everything is
     gated on a fine pointer + motion-ok + no reduced-motion preference; the
     page is complete without any of it. Rows energise on hover via CSS alone. */
  (function () {
    ready(function () {
      var feat = doc.querySelector('.ev-feat');
      if (!feat) return;
      var art = feat.querySelector('.ev-feat-art');
      var frame = feat.querySelector('.ev-feat-frame');
      var glow = feat.querySelector('.art-glow');
      var motif = feat.querySelector('.ev-motif');
      var cta = feat.querySelector('.ev-feat-acts .btn-quest');
      if (!art || !frame) return;
      var live = !reduceMotion && doc.documentElement.classList.contains('motion-ok');

      /* sparks: seven points of light that drift and fade on their own
         CSS keyframes - no per-frame JS, no layout, nothing when motion is off */
      if (live) {
        var host = doc.createElement('span');
        host.className = 'ev-sparks';
        host.setAttribute('aria-hidden', 'true');
        for (var s = 0; s < 7; s++) {
          var sp = doc.createElement('i');
          sp.className = 'ev-spark';
          sp.style.setProperty('--sx', (8 + Math.random() * 84).toFixed(1) + '%');
          sp.style.setProperty('--sy', (8 + Math.random() * 84).toFixed(1) + '%');
          sp.style.setProperty('--sd', (Math.random() * 6).toFixed(2) + 's');
          sp.style.setProperty('--st', (5 + Math.random() * 5).toFixed(2) + 's');
          sp.style.setProperty('--ss', (0.6 + Math.random() * 0.9).toFixed(2));
          host.appendChild(sp);
        }
        art.appendChild(host);
      }

      if (!live || !finePointer) return;

      /* parallax: the frame leans toward the pointer, the glow drifts the
         other way, the motif drifts furthest - three planes of depth. */
      var rect = null, raf = 0, tx = 0, ty = 0, cx = 0, cy = 0, inside = false;
      function measure() { rect = art.getBoundingClientRect(); }
      function paint() {
        raf = 0;
        cx += (tx - cx) * 0.14; cy += (ty - cy) * 0.14;
        frame.style.transform = 'translate3d(' + (cx * 9).toFixed(2) + 'px,' + (cy * 9).toFixed(2) + 'px,0) rotateX(' + (-cy * 3).toFixed(2) + 'deg) rotateY(' + (cx * 4).toFixed(2) + 'deg)';
        if (glow) glow.style.transform = 'translate3d(' + (-cx * 14).toFixed(2) + 'px,' + (-cy * 14).toFixed(2) + 'px,0)';
        if (motif) motif.style.transform = 'translate3d(' + (cx * 18).toFixed(2) + 'px,' + (cy * 18).toFixed(2) + 'px,0)';
        if (inside || Math.abs(cx) > 0.002 || Math.abs(cy) > 0.002) raf = requestAnimationFrame(paint);
      }
      function onMove(e) {
        if (!rect) measure();
        tx = ((e.clientX - rect.left) / rect.width - 0.5);
        ty = ((e.clientY - rect.top) / rect.height - 0.5);
        if (!raf) raf = requestAnimationFrame(paint);
      }
      art.addEventListener('pointerenter', function () { inside = true; measure(); art.classList.add('is-live'); });
      art.addEventListener('pointermove', onMove);
      art.addEventListener('pointerleave', function () { inside = false; tx = 0; ty = 0; art.classList.remove('is-live'); if (!raf) raf = requestAnimationFrame(paint); });
      window.addEventListener('resize', function () { rect = null; }, { passive: true });

      /* magnetic CTA: leans up to 6px toward the pointer while hovered */
      if (cta) {
        var crect = null;
        cta.addEventListener('pointerenter', function () { crect = cta.getBoundingClientRect(); });
        cta.addEventListener('pointermove', function (e) {
          if (!crect) crect = cta.getBoundingClientRect();
          var mx = ((e.clientX - crect.left) / crect.width - 0.5) * 12;
          var my = ((e.clientY - crect.top) / crect.height - 0.5) * 8;
          cta.style.transform = 'translate3d(' + mx.toFixed(1) + 'px,' + my.toFixed(1) + 'px,0)';
        });
        cta.addEventListener('pointerleave', function () { cta.style.transform = ''; crect = null; });
      }
    });
  })();

})();
